import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import SendPointer from '../../components/SendPointer';
import AttachButton, { AttachThumb, isPdf } from '../../components/AttachButton';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import AdminLayout from './AdminLayout';
import { USA_STATUS } from './ProjectsTable';
import { useAuth } from '../../context/AuthContext';
import Reactions from '../../components/Reactions';
import { TM_SOURCE_LABEL,  TmProjectTracker, AddCommonItem } from './ExtraWorkCard';
import { JobPhotosSection, useJobPhotos } from './JobPhotos';
import PlansFromDocuments from './PlansFromDocuments';
import ActivityPills from '../../components/ActivityPills';
import { useCategories } from '../../hooks/useCategories';
import { jobNumber, jobName, asLocalDate, fmtDate as fmtDateLocal } from '../../utils/ui';
import { NVETab, LoopsTab, UsaDigsTab } from './AdminBidDetail';
import WorkOrdersBoard from './WorkOrdersBoard';
import NveUpcomingPill from './NveUpcoming';
import AddToSchedule from './AddToSchedule';
import { spanLabel } from '../../utils/workdays';
import { FieldwireTasks, NveFieldwireSheets, NveWindow, NveSheetPager, FwTaskChooser, FieldwireTaskDrawer } from './FieldwirePanel';
import { partLabel } from '../../utils/billParts';
import PermitsBoard, { JobPermitsPillow } from './PermitsBoard';
import JobNumberLink from '../../components/JobNumberLink';
import { InvoiceModal } from './WoBillingPillow';
import NveInspectionRequest from './NveInspectionRequest';
import PdfWindow from '../../components/PdfWindow';

// Fix Leaflet's default marker icon (broken by webpack asset handling).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// The drills come from Settings → Categories (Pat, Sep 25 2026): Vertical Drilling has
// its own list (Harold, Ella, Volvo) and Directional Drilling has the Ditch Witch, the
// Vermeers and the Barbcos. Lanes on the board run in the Settings order.

// Workflow rows carry project_number + project_name; adapt to the shared helpers.
const wfJob = w => ({ project_number: w.project_number, name: w.project_name });

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }

// Finished drilling / loops cards: 🧾 Send to billing or 📁 Close to the job (Ryan, Oct 28 2026).
// PMs, the office and Executives choose; field supervisors only mark Complete. A stand-alone work
// order is its own job and still goes to billing by itself, so it keeps the one Close button.
const BILL_CHOOSER_ROLES = ['Admin', 'ProjectManager', 'OfficeStaff', 'Accounting', 'Executive'];
function canChooseBilling() {
  try { const s = JSON.parse(localStorage.getItem('titan_session') || 'null'); return !!s && (s.user?.id === 'admin' || BILL_CHOOSER_ROLES.includes(s.role)); } catch { return false; }
}
const isStandaloneWo = card => card && card.division === 'small' && !card.parent_project_id;
const CLOSE_CHOICE_TYPES = ['vertical_drilling', 'horizontal_drilling', 'loops'];
const jobWord = card => card && card.parent_number ? `job ${card.parent_number}` : 'the job';
// The tag a closed card wears: which way it went.
const closeTag = card => !card || !card.close_kind ? null
  : card.close_kind === 'billing' ? { text: '🧾 Sent to billing', color: '#15803d', bg: '#f0fdf4' }
  : { text: `📁 Billed with ${jobWord(card)}`, color: '#1d4ed8', bg: '#eff6ff' };
async function closeWorkCard(card, sjId, kind) {
  const msg = kind === 'billing'
    ? `Send ${card.name || 'this card'} to billing?\n\nIt comes off the board and stays on the job. ${card.division === 'small' ? 'The work order goes to 100% Complete · To Bill once its other cards are finished.' : 'The office gets a bell that it is ready to bill.'}`
    : `Close ${card.name || 'this card'} to ${jobWord(card)}?\n\nIt comes off the board and stays on the job. Nothing goes to billing — it is billed with ${jobWord(card)}${card.parent_number ? ' (it shows up on its next pay application)' : ''}.`;
  if (!window.confirm(msg)) return null;
  return apiFetch(`/projects/${card.project_id}/sub-jobs/${sjId}/close`, { method: 'POST', body: JSON.stringify({ kind }) });
}
function apiFetch(path, opts = {}) {
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken(), ...opts.headers },
  }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
}
const photoUrl = fn => `${API_BASE.replace('/api', '')}/uploads/${fn}`;

const WORKFLOW_TYPES = {
  vertical_drilling:   { label: 'Vertical Drilling',   icon: '🕳️', color: '#1e3a5f' },
  horizontal_drilling: { label: 'Directional Drilling', icon: '↔️', color: '#4a1d50' },
  loops:               { label: 'Loops',               icon: '🔁', color: '#059669' },
  small_jobs:          { label: 'Small Jobs',          icon: '🗂️', color: '#7c3aed' },
  nve:                 { label: 'NVE',                  icon: '⚡', color: '#d97706' },
  usa_digs:            { label: 'USA Digs',             icon: '🚧', color: '#7c2d12' },
  shop:                { label: 'Shop',                 icon: '🔧', color: '#475569' },
  fiber:               { label: 'Fiber',                icon: '🔌', color: '#0e7490' },
  permits:             { label: 'Permits',              icon: '📜', color: '#0e7490' },
  submittals:          { label: 'Submittals',          icon: '📋', color: '#1e3a5f' },
  subcontracts:        { label: 'Subcontracts',        icon: '🤝', color: '#4c1d95' },
};
function typeMeta(t) { return WORKFLOW_TYPES[t] || { label: t ? t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Other', icon: '📁', color: '#374151' }; }

// USA (811) dig-ticket badges on drilling cards — green = valid/active, yellow =
// called-in or expiring soon, red = expired. Shows the number and expiry date.
const USA_BADGE = {
  not_called_in: { bg: '#f3f4f6', bd: '#d1d5db', tx: '#6b7280', word: 'not called in' },
  active:        { bg: '#f0fdf4', bd: '#bbf7d0', tx: '#15803d', word: 'valid' },
  called_in:     { bg: '#fffbeb', bd: '#fde68a', tx: '#b45309', word: 'called in' },
  expiring_soon: { bg: '#fffbeb', bd: '#fde68a', tx: '#b45309', word: 'expiring soon' },
  dug:           { bg: '#f5f5f4', bd: '#e7e5e4', tx: '#57534e', word: 'dug' },
  expired:       { bg: '#fef2f2', bd: '#fecaca', tx: '#b91c1c', word: 'EXPIRED' },
};
const usaExp = iso => { if (!iso) return ''; const d = new Date(iso); return isNaN(d) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
function usaBadges(tickets, size) {
  if (!Array.isArray(tickets) || tickets.length === 0) return null;
  const fs = size === 'sm' ? 10 : 11;
  return tickets.map((t, i) => {
    const c = USA_BADGE[t.status] || USA_BADGE.not_called_in;
    const mark = t.status === 'expired' ? ' ⛔' : t.status === 'expiring_soon' ? ' ⚠️' : '';
    return (
      <span key={i} title={`USA ${t.usa_number || '(no number)'} — ${c.word}${t.expires_at ? ', expires ' + usaExp(t.expires_at) : ''}`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: fs, fontWeight: 700, background: c.bg, color: c.tx, border: `1px solid ${c.bd}`, borderRadius: 20, padding: '2px 8px', whiteSpace: 'nowrap' }}>
        🎫 {t.usa_number || 'USA'}{t.expires_at ? ` · ${usaExp(t.expires_at)}` : ''}{mark}
      </span>
    );
  });
}

// ── Drilling card pillows ─────────────────────────────────────────────────────
// The four things you need while scheduling a rig, in one 2×2 block: which drill,
// log a day's work, when it is booked, and whether the locate is good. Shared by the
// board tile and the card that opens, so a card reads the same wherever you meet it.
//
// The date pillow's colour is the point of the whole block:
//   green  — the day has come and work is being logged, OR the rig is standing on it
//   yellow — booked within the next week
//   grey   — booked further out, or the day passed with nothing to show
//   blank  — no date yet; click to set one
const DRILL_PILL_H = 40;
function drillDateState(w, date) {
  if (!date) return 'none';
  const d = new Date(`${date}T12:00:00`).getTime();
  if (isNaN(d)) return 'none';
  const now = Date.now();
  const passed = d <= now;
  const activity = w.last_update_at && new Date(w.last_update_at).getTime() >= d;
  if ((passed && activity) || w.rig_on_site) return 'green';
  if (!passed && (d - now) <= 7 * 86400000) return 'yellow';
  return 'grey';
}
const DRILL_DATE_STYLE = {
  green:  { bg: '#f0fdf4', bd: '#bbf7d0', tx: '#15803d' },
  yellow: { bg: '#fffbeb', bd: '#fde68a', tx: '#b45309' },
  grey:   { bg: '#f9fafb', bd: '#e5e7eb', tx: '#6b7280' },
  none:   { bg: '#fff',    bd: '#e5e7eb', tx: '#9ca3af' },
};
// Worst ticket wins: one expired locate matters more than three good ones.
const USA_RANK = { expired: 4, expiring_soon: 3, not_called_in: 2, called_in: 1, active: 0, dug: -1 };
function usaWorst(tickets) {
  if (!Array.isArray(tickets) || !tickets.length) return null;
  return tickets.reduce((a, b) => (USA_RANK[b.status] ?? 0) > (USA_RANK[a.status] ?? 0) ? b : a);
}

// "3/8 · 240 ft" — how many holes, how many done, and the depth they add up to.
// Built in one place so the tile, the phone list and the open card cannot describe
// the same job three different ways.
function holesPillowLabel(count, done, depthTotal) {
  if (!count) return '🕳️ Holes';
  const ft = depthTotal ? ` · ${Math.round(depthTotal).toLocaleString()} ft` : '';
  return `🕳️ Holes ${done}/${count}${ft}`;
}

// The USA locate pillow. Shared, because a locate is the same fact on a drilling
// card and on a work order, and two copies drift.
//
// An empty pillow used to read "No USA", which says nothing about whether one is
// coming or was never needed. Now the job can say so, and a job that has said so
// stops looking like a gap.
function UsaPillow({ w, pill, compact, onChanged, showToast }) {
  const [notNeeded, setNotNeeded] = React.useState(!!w.usa_not_needed);
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { setNotNeeded(!!w.usa_not_needed); }, [w.usa_not_needed]);
  const tickets = Array.isArray(w.usa_tickets) ? w.usa_tickets : [];
  const worst = usaWorst(tickets);
  const us = worst ? (USA_STATUS[worst.status] || USA_STATUS.not_called_in) : null;

  async function setFlag(next) {
    if (busy) return;
    setBusy(true); setNotNeeded(next);
    try {
      await apiFetch(`/projects/${w.project_id}/usa-not-needed`, { method: 'PATCH', body: JSON.stringify({ usa_not_needed: next ? 1 : 0 }) });
      onChanged && onChanged();
    } catch (e) { setNotNeeded(!next); showToast && showToast(e.message, 'error'); }
    setBusy(false);
  }

  // A real ticket always wins the pillow: whatever anyone once ticked, a locate that
  // exists is the thing the crew needs to see.
  if (worst) {
    return (
      <>
        <button onClick={() => tickets.length === 1 && worst.pdf ? window.open(photoUrl(worst.pdf), '_blank', 'noopener') : setOpen(true)}
          title={`${tickets.length} USA ticket${tickets.length !== 1 ? 's' : ''} — ${us.label}`}
          style={pill({ background: us.bg, borderColor: us.bg, color: us.color, flexDirection: 'column', gap: 1, lineHeight: 1.15, padding: '6px 8px' })}>
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.5, opacity: 0.75 }}>🎫 USA DIG</span>
          <span style={{ fontSize: compact ? 11.5 : 12.5, fontWeight: 800 }}>{us.label}{tickets.length > 1 ? ` · ${tickets.length}` : ''}</span>
        </button>
        {open && <UsaTicketList tickets={tickets} onClose={() => setOpen(false)} />}
      </>
    );
  }
  if (notNeeded) {
    return (
      <button onClick={() => setFlag(false)} disabled={busy} title="Marked as not needed — tap to undo"
        style={pill({ background: '#f8fafc', borderColor: '#e2e8f0', color: '#64748b', flexDirection: 'column', gap: 1, lineHeight: 1.15, padding: '6px 8px' })}>
        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.5, opacity: 0.75 }}>🎫 USA DIG</span>
        <span style={{ fontSize: compact ? 11.5 : 12.5, fontWeight: 800 }}>Not Needed</span>
      </button>
    );
  }
  return (
    <button onClick={() => setFlag(true)} disabled={busy} title="No locate on this job yet — tap to mark it not needed"
      style={pill({ color: '#9ca3af', borderStyle: 'dashed' })}>🎫 No USA</button>
  );
}

// The ticket list behind the pillow, once a job has more than one.
function UsaTicketList({ tickets, onClose }) {
  return (
    <div onClick={e => e.stopPropagation()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: 460, maxWidth: '94vw', padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>USA dig tickets</div>
          <button onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', fontSize: 24, lineHeight: 1, color: '#9ca3af', cursor: 'pointer', padding: '0 4px' }}>×</button>
        </div>
        {tickets.map((t, i) => {
          const c = USA_STATUS[t.status] || USA_STATUS.not_called_in;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: i ? '1px solid #f3f4f6' : 'none' }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 4, background: c.bg, color: c.color, whiteSpace: 'nowrap' }}>{c.label}</span>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>{t.usa_number || '(no number)'}</span>
              {t.expires_at && <span style={{ fontSize: 11.5, color: '#6b7280' }}>expires {usaExp(t.expires_at)}</span>}
              {t.pdf
                ? <a href={photoUrl(t.pdf)} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, fontWeight: 700, color: '#1d4ed8' }}>📄 PDF</a>
                : <span style={{ fontSize: 11.5, color: '#c4c4c4' }}>no PDF</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// The Work Orders board columns, shared with the card that now also sets them.
// Short names for the pillow, which has half a phone to say it in.
const WO_COLUMN_META = {
  week_electrical:   { label: 'This Week · Electrical',  short: 'Wk · Electrical', color: '#1d4ed8', bg: '#eff6ff', edge: '#bfdbfe' },
  week_dirt:         { label: 'This Week · Dirt',        short: 'Wk · Dirt',       color: '#b45309', bg: '#fffbeb', edge: '#fde68a' },
  inside_electrical: { label: 'Inside Electrical',       short: 'Inside Elec',     color: '#0e7490', bg: '#ecfeff', edge: '#a5f3fc' },
  waiting_material:  { label: 'Waiting On Material',     short: 'Waiting Mat’l',   color: '#9333ea', bg: '#faf5ff', edge: '#e9d5ff' },
  next_week:         { label: 'Next Week Or Longer',     short: 'Next Wk+',        color: '#475569', bg: '#f8fafc', edge: '#e2e8f0' },
  pole_hold:         { label: 'Pole Hold',               short: 'Pole Hold',       color: '#dc2626', bg: '#fef2f2', edge: '#fecaca' },
  complete_to_bill:  { label: '100% Complete · To Bill', short: 'To Bill',         color: '#15803d', bg: '#f0fdf4', edge: '#bbf7d0' },
  // Pending came off the board (Pat, Sep 2026): nothing ever used it, the billing half
  // lives on Work Orders To Be Billed. Needs Checking is set by preparing a bill there,
  // never picked here — it's shown when it's the case, not offered.
  ready_to_send:     { label: 'Needs Checking',          short: 'Checking',        color: '#c2410c', bg: '#fff7ed', edge: '#fed7aa' },
  billing_not_paid:  { label: 'Billing / Not Paid',      short: 'Billing',         color: '#c2410c', bg: '#fff7ed', edge: '#fed7aa' },
  paid:              { label: 'Paid',                    short: 'Paid',            color: '#166534', bg: '#ecfdf5', edge: '#a7f3d0' },
};
const WO_COLUMNS_FIELD = ['week_electrical', 'week_dirt', 'inside_electrical', 'waiting_material', 'next_week', 'pole_hold'];
const WO_COLUMNS_BILLING = ['complete_to_bill', 'billing_not_paid', 'paid'];

// Small jobs. The drill becomes the category, which is the board column itself —
// tap the pillow or drag the card and they cannot disagree, because they are one
// field. The date is the day the work happens, kept apart from a due date.
export function WorkOrderPillows({ w, onChanged, showToast, compact, onDailyUpdate, extras = [] }) {
  const [col, setCol] = React.useState(w.wo_status || 'next_week');
  const [date, setDate] = React.useState(w.wo_date || '');
  const [busy, setBusy] = React.useState('');
  const [updateOpen, setUpdateOpen] = React.useState(false);
  React.useEffect(() => { setCol(w.wo_status || 'next_week'); setDate(w.wo_date || ''); }, [w.wo_status, w.wo_date]);

  const stop = e => { e.stopPropagation(); };
  const pill = (extra = {}) => ({
    minHeight: DRILL_PILL_H, boxSizing: 'border-box', width: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    borderRadius: 10, border: '1.5px solid #e5e7eb', background: '#fff',
    fontSize: compact ? 12 : 13, fontWeight: 700, color: '#374151',
    cursor: 'pointer', padding: '0 10px', textAlign: 'center', ...extra,
  });

  async function save(body, revert) {
    try {
      await apiFetch(`/work-orders/${w.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      onChanged && onChanged();
    } catch (e) { revert(); showToast && showToast(e.message, 'error'); }
    setBusy('');
  }
  function saveCol(next) { setBusy('col'); setCol(next); save({ wo_status: next }, () => setCol(w.wo_status || 'next_week')); }
  function saveDate(next) { setBusy('date'); setDate(next); save({ wo_date: next || null }, () => setDate(w.wo_date || '')); }

  const meta = WO_COLUMN_META[col] || WO_COLUMN_META.next_week;
  const ds = DRILL_DATE_STYLE[drillDateState(w, date)];
  const dateLabel = date
    ? spanLabel(date, w.wo_days)
    : 'Set date';

  return (
    // draggable={false} is the point of this line. On the desktop board the whole card
    // is draggable, and a draggable ancestor makes the browser start a drag on mousedown
    // instead of handing the click to the control underneath — so the date picker and the
    // drill dropdown never opened. The phone has no draggable card, which is exactly why
    // the same pillows worked there and not here.
    <div onClick={stop} draggable={false} onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}
      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
      <button onClick={() => onDailyUpdate ? onDailyUpdate() : setUpdateOpen(true)} style={pill({ background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' })}>
        ➕ Daily Update
      </button>

      {/* Category — the board column, in the card. Field work and billing are one
          list here because they are one field; the menu keeps them apart. */}
      <label style={pill({ position: 'relative', background: meta.bg, borderColor: meta.edge, color: meta.color, flexDirection: 'column', gap: 1, lineHeight: 1.15, padding: '6px 8px' })}
        title="Which column this work order sits in">
        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.5, opacity: 0.75, pointerEvents: 'none' }}>📋 CATEGORY</span>
        <span style={{ fontSize: compact ? 11 : 12, fontWeight: 800, pointerEvents: 'none' }}>{meta.short}</span>
        <select value={col} disabled={busy === 'col'} onChange={e => saveCol(e.target.value)}
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}>
          <optgroup label="Field">
            {WO_COLUMNS_FIELD.map(c => <option key={c} value={c}>{WO_COLUMN_META[c].label}</option>)}
          </optgroup>
          <optgroup label="Billing">
            {WO_COLUMNS_BILLING.map(c => <option key={c} value={c}>{WO_COLUMN_META[c].label}</option>)}
            {col === 'ready_to_send' && <option value="ready_to_send" disabled>{WO_COLUMN_META.ready_to_send.label}</option>}
          </optgroup>
        </select>
      </label>

      {/* The day the work happens — the same pillow, the same colours, as drilling. */}
      <label style={pill({ position: 'relative', background: ds.bg, borderColor: ds.bd, color: ds.tx })}
        title={date ? 'The day this work is scheduled' : 'No work date set'}>
        <span style={{ pointerEvents: 'none' }}>📅 {dateLabel}</span>
        <input type="date" value={date} disabled={busy === 'date'} onChange={e => saveDate(e.target.value)}
        // A date input only opens its picker from the small calendar icon at its right
        // edge, and ours is invisible under the pill — so clicking it did nothing and
        // the date could not be changed from the board at all. A phone opens the picker
        // on any tap, which is exactly why this failed on the desktop and nowhere else.
        onClick={e => { const i = e.currentTarget; if (typeof i.showPicker === 'function') { try { i.showPicker(); } catch { /* unsupported here */ } } }}
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
      </label>

      <UsaPillow w={{ ...w, project_id: w.id }} pill={pill} compact={compact} onChanged={onChanged} showToast={showToast} />

      {extras.filter(Boolean).map(x => (
        <React.Fragment key={x.key || x.label}>
        <button onClick={x.onClick}
          style={pill({ gridColumn: '1 / -1', background: x.open ? (x.tone || '#7c3aed') : (x.toneSoft || '#faf5ff'), borderColor: x.toneEdge || '#ddd6fe', color: x.open ? '#fff' : (x.tone || '#7c3aed') })}>
          {x.label} <span style={{ fontSize: 11, opacity: 0.8 }}>{x.open ? '▲' : '▼'}</span>
        </button>
        {/* What the pillow opens sits right under it — not under the whole stack (Ryan, Oct 1 2026). */}
        {x.open && x.panel ? <div style={{ gridColumn: '1 / -1' }}>{x.panel}</div> : null}
        </React.Fragment>
      ))}

      {updateOpen && (
        <div onClick={stop} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 460, maxWidth: '94vw', padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 800 }}>Daily update</div>
              <button onClick={() => setUpdateOpen(false)} aria-label="Close"
                style={{ background: 'none', border: 'none', fontSize: 24, lineHeight: 1, color: '#9ca3af', cursor: 'pointer', padding: '0 4px' }}>×</button>
            </div>
            <DailyUpdateComposer projectId={w.id} autoOpen hideLabel workflowLabel="this work order" />
          </div>
        </div>
      )}
    </div>
  );
}

// ── NVE ─────────────────────────────────────────────────────────────────────
// A permit reads in three states, and the colour is the whole point: nothing yet,
// pulled, and signed off by the city. The fields these come from already existed —
// permit_number and city_ok — they were just never shown anywhere you'd look.
function permitState(m) {
  if (!m) return 'none';
  if (m.city_ok) return 'ok';
  return m.permit_number ? 'have' : 'none';
}
const PERMIT_STYLE = {
  none: { bg: '#fff',    edge: '#e5e7eb', tx: '#9ca3af', word: 'No permit' },
  have: { bg: '#fffbeb', edge: '#fde68a', tx: '#b45309', word: 'Permit' },
  ok:   { bg: '#f0fdf4', edge: '#bbf7d0', tx: '#15803d', word: "City OK'd" },
};

// The drilling card's pillow row for an NVE meter. The drill becomes the meter's
// status, the date becomes the job's drawings, and the locate becomes the permit.
export function NvePillows({ w, onChanged, showToast, compact, onDailyUpdate, onOpenDrawings, onOpenPermit, drawingsPanel = null, permitPanel = null, extras = [], meters = [], underground = false, poleHold = !!Number(w && w.nve_pole_hold) }) {
  const [status, setStatus] = React.useState(w.nve_status || 'application_in');
  const [busy, setBusy] = React.useState('');
  const [updateOpen, setUpdateOpen] = React.useState(false);
  React.useEffect(() => { setStatus(w.nve_status || 'application_in'); }, [w.nve_status]);

  const stop = e => { e.stopPropagation(); };
  const pill = (extra = {}) => ({
    minHeight: DRILL_PILL_H, boxSizing: 'border-box', width: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    borderRadius: 10, border: '1.5px solid #e5e7eb', background: '#fff',
    fontSize: compact ? 12 : 13, fontWeight: 700, color: '#374151',
    cursor: 'pointer', padding: '0 10px', textAlign: 'center', ...extra,
  });

  async function saveStatus(next) {
    setBusy('status'); setStatus(next);
    try {
      // With a meter the status is the meter's; underground only (no meter) it is the card's own.
      if (w.meter_id) await apiFetch('/sub-jobs/' + (w.sub_job_id || w.id) + '/nve-meters/' + w.meter_id, { method: 'PATCH', body: JSON.stringify({ status: next }) });
      else await apiFetch('/sub-jobs/' + (w.sub_job_id || w.id) + '/card', { method: 'PATCH', body: JSON.stringify({ nve_status: next }) });
      onChanged && onChanged();
    } catch (e) { setStatus(w.nve_status || 'application_in'); showToast && showToast(e.message, 'error'); }
    setBusy('');
  }

  const sm = NVE_META[status] || NVE_META.application_in;
  const meter = w.meter || null;
  const ps = PERMIT_STYLE[permitState(meter)];
  const pid = w.nve_pid;
  const drawings = w.nve_file_count || 0;

  return (
    // draggable={false} is the point of this line. On the desktop board the whole card
    // is draggable, and a draggable ancestor makes the browser start a drag on mousedown
    // instead of handing the click to the control underneath — so the date picker and the
    // drill dropdown never opened. The phone has no draggable card, which is exactly why
    // the same pillows worked there and not here.
    <div onClick={stop} draggable={false} onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}
      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
      <button onClick={() => onDailyUpdate ? onDailyUpdate() : setUpdateOpen(true)} style={pill({ background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' })}>
        ➕ Daily Update
      </button>

      {/* Where this meter stands with NV Energy. Six states is too many to cycle
          through without overshooting, so it is a menu. */}
      <label style={pill({ position: 'relative', background: sm.bg, borderColor: sm.bg, color: sm.color, flexDirection: 'column', gap: 1, lineHeight: 1.15, padding: '6px 8px' })}
        title="Where this meter stands with NV Energy">
        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.5, opacity: 0.75, pointerEvents: 'none' }}>⚡ NVE STATUS</span>
        <span style={{ fontSize: compact ? 11 : 12, fontWeight: 800, pointerEvents: 'none' }}>{sm.label}</span>
        <select value={status} disabled={busy === 'status'} onChange={e => saveStatus(e.target.value)}
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}>
          {NVE_STATUS_COLUMNS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </label>

      {/* The job's NVE drawings, filed under the PID they were issued against. */}
      <button onClick={() => onOpenDrawings && onOpenDrawings()}
        title={pid ? 'Drawings for PID ' + pid : 'NVE drawings — no PID on this job yet'}
        style={pill({ background: drawings ? '#eef2ff' : '#fff', borderColor: drawings ? '#c7d2fe' : '#e5e7eb', color: drawings ? '#4338ca' : '#374151', flexDirection: 'column', gap: 1, lineHeight: 1.15, padding: '6px 8px' })}>
        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.5, opacity: 0.75 }}>📐 DRAWINGS</span>
        <span style={{ fontSize: compact ? 11 : 12, fontWeight: 800 }}>
          {pid ? 'PID ' + pid : 'No PID'}{drawings ? ' · ' + drawings : ''}
        </span>
      </button>

      {/* Request Inspection — one pillow per meter when the job has several (Ryan, Oct 2
          2026); on an underground-only card there are no meters, so it goes by the PID. */}
      {(underground || meters.length < 2 ? [meter || null] : meters).map((m, i) => {
        const line = underground ? (pid ? 'PID ' + pid : 'No PID yet')
          : (m && m.address) || (meters.length < 2 ? w.meter_address : null) || (pid ? 'PID ' + pid : (m && m.permit_number ? m.permit_number : ps.word));
        const st = PERMIT_STYLE[permitState(m)];
        return (
          <button key={(m && m.id) || i} onClick={() => onOpenPermit && onOpenPermit(m ? m.id : null)}
            title={'Email NV Energy an inspection request — Field Meet, Trench / Sand / Tape, Mandrel or Final. The permit details are under it.' + (m && m.permit_number ? ' Permit ' + m.permit_number + '.' : '')}
            style={pill({ background: st.bg, borderColor: st.edge, color: st.tx, flexDirection: 'column', gap: 1, lineHeight: 1.15, padding: '6px 8px', borderStyle: st.word === 'No permit' ? 'dashed' : 'solid' })}>
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.5, opacity: 0.75 }}>
              {poleHold ? '🪝 REQUEST POLE HOLD' : '📧 REQUEST INSPECTION'}{meters.length > 1 && !underground ? ` · METER ${i + 1}` : ''}
            </span>
            <span style={{ fontSize: compact ? 11 : 12, fontWeight: 800 }}>{line}</span>
          </button>
        );
      })}
      {/* What Drawings / Permit open sits right under that row (Ryan, Oct 1 2026). */}
      {(drawingsPanel || permitPanel) ? <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 10 }}>{drawingsPanel}{permitPanel}</div> : null}

      {extras.filter(Boolean).map(x => (
        <React.Fragment key={x.key || x.label}>
        <button onClick={x.onClick}
          style={pill({ gridColumn: '1 / -1', background: x.open ? (x.tone || '#7c3aed') : (x.toneSoft || '#faf5ff'), borderColor: x.toneEdge || '#ddd6fe', color: x.open ? '#fff' : (x.tone || '#7c3aed') })}>
          {x.label} <span style={{ fontSize: 11, opacity: 0.8 }}>{x.open ? '▲' : '▼'}</span>
        </button>
        {/* What the pillow opens sits right under it — not under the whole stack (Ryan, Oct 1 2026). */}
        {x.open && x.panel ? <div style={{ gridColumn: '1 / -1' }}>{x.panel}</div> : null}
        </React.Fragment>
      ))}

      {updateOpen && (
        <div onClick={stop} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 460, maxWidth: '94vw', padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 800 }}>Daily update</div>
              <button onClick={() => setUpdateOpen(false)} aria-label="Close"
                style={{ background: 'none', border: 'none', fontSize: 24, lineHeight: 1, color: '#9ca3af', cursor: 'pointer', padding: '0 4px' }}>×</button>
            </div>
            <DailyUpdateComposer projectId={w.project_id} subJobId={w.sub_job_id || w.id} autoOpen hideLabel workflowLabel="this NVE board"
              meterId={w.meter_id || (w.meter && w.meter.id) || null} meters={meters.length > 1 ? meters : []} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Loops ───────────────────────────────────────────────────────────────────
// The three kinds a loop location can be. The stored values are untouched: what the
// field calls a counter has always been saved as 'speed', and only the word differs.
const LOOP_KIND_META = {
  traffic: { label: 'Traffic', color: '#059669', bg: '#ecfdf5', edge: '#a7f3d0' },
  speed:   { label: 'Counter', color: '#d97706', bg: '#fffbeb', edge: '#fde68a' },
  gate:    { label: 'Gate',    color: '#1d4ed8', bg: '#eff6ff', edge: '#bfdbfe' },
};
// Traffic, counter, gate — the order the work gets talked about.
const LOOP_KIND_ORDER = ['traffic', 'speed', 'gate'];
// Day or night, said in colour. Two fixed states rather than a free palette, so a
// dark card means the same thing on every board and nights read at a glance.
const LOOP_SHIFT_META = {
  day:   { label: 'Day',   icon: '☀️', bg: '#fffbeb', edge: '#fde68a', tx: '#b45309' },
  night: { label: 'Night', icon: '🌙', bg: '#1e3a5f', edge: '#1e3a5f', tx: '#fff' },
};
// "12 loops" — counted on the server exactly the way the Loops tab counts them, so
// the pillow and the detail behind it can never tell two different stories.
function loopQtyLabel(qty) {
  if (!qty) return 'None yet';
  return qty.toLocaleString() + ' loop' + (qty === 1 ? '' : 's');
}

// The drilling card's pillow row in the loops dialect: the drill becomes the
// quantity (carrying day/night in its colour), and the USA locate becomes the kinds
// of loop actually on the card. Daily Update and the date are the same fact on both
// boards, so they are the same pillow.
export function LoopPillows({ w, onChanged, showToast, compact, onDailyUpdate, onOpenLoops, extras = [] }) {
  const [shift, setShift] = React.useState(w.loop_shift || '');
  const [date, setDate] = React.useState(w.drill_date || '');
  const [busy, setBusy] = React.useState('');
  const [updateOpen, setUpdateOpen] = React.useState(false);
  React.useEffect(() => { setShift(w.loop_shift || ''); setDate(w.drill_date || ''); }, [w.loop_shift, w.drill_date]);

  const stop = e => { e.stopPropagation(); };
  const pill = (extra = {}) => ({
    minHeight: DRILL_PILL_H, boxSizing: 'border-box', width: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    borderRadius: 10, border: '1.5px solid #e5e7eb', background: '#fff',
    fontSize: compact ? 12 : 13, fontWeight: 700, color: '#374151',
    cursor: 'pointer', padding: '0 10px', textAlign: 'center', ...extra,
  });

  async function saveShift(next) {
    setBusy('shift'); setShift(next);
    try {
      await apiFetch('/sub-jobs/' + w.id + '/card', { method: 'PATCH', body: JSON.stringify({ loop_shift: next || null }) });
      onChanged && onChanged();
    } catch (e) { setShift(w.loop_shift || ''); showToast && showToast(e.message, 'error'); }
    setBusy('');
  }
  async function saveDate(next) {
    setBusy('date'); setDate(next);
    try {
      await apiFetch('/sub-jobs/' + w.id + '/card', { method: 'PATCH', body: JSON.stringify({ drill_date: next || null }) });
      onChanged && onChanged();
    } catch (e) { setDate(w.drill_date || ''); showToast && showToast(e.message, 'error'); }
    setBusy('');
  }

  const ds = DRILL_DATE_STYLE[drillDateState(w, date)];
  const dateLabel = date
    ? spanLabel(date, w.sched_days)
    : 'Set date';
  const sh = LOOP_SHIFT_META[shift] || null;
  // Day → Night → not said. Jobs booked before any of this existed carry no shift,
  // so the cycle has to be able to get back to saying nothing.
  const nextShift = shift === 'day' ? 'night' : shift === 'night' ? '' : 'day';
  const kinds = w.loop_kinds || {};
  const present = LOOP_KIND_ORDER.filter(k => kinds[k]);
  const onlyKind = present.length === 1 ? LOOP_KIND_META[present[0]] : null;

  return (
    // draggable={false} is the point of this line. On the desktop board the whole card
    // is draggable, and a draggable ancestor makes the browser start a drag on mousedown
    // instead of handing the click to the control underneath — so the date picker and the
    // drill dropdown never opened. The phone has no draggable card, which is exactly why
    // the same pillows worked there and not here.
    <div onClick={stop} draggable={false} onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}
      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
      <button onClick={() => onDailyUpdate ? onDailyUpdate() : setUpdateOpen(true)} style={pill({ background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' })}>
        ➕ Daily Update
      </button>

      {/* Quantity — and, in its colour, whether the crew is on days or nights. The
          number is added up from the loop locations, never typed here. */}
      <button onClick={() => saveShift(nextShift)} disabled={busy === 'shift'}
        title={sh ? sh.label + ' work — tap to change' : 'Tap to say day or night work'}
        style={pill({ background: sh ? sh.bg : '#fff', borderColor: sh ? sh.edge : '#e5e7eb', color: sh ? sh.tx : '#374151', flexDirection: 'column', gap: 1, lineHeight: 1.15, padding: '6px 8px' })}>
        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.5, opacity: sh ? 0.85 : 0.6 }}>
          {sh ? sh.icon + ' ' + sh.label.toUpperCase() : '🔁 QUANTITY'}
        </span>
        {/* "42 of 235 done" once the job's loops are tied to Fieldwire (Pat, Oct 9 2026 — Sparks Blvd). */}
        <span style={{ fontSize: compact ? 11.5 : 12.5, fontWeight: 800 }}>{w.loop_fw_linked > 0 && w.loop_qty ? `${(w.loop_fw_done || 0).toLocaleString()} of ${w.loop_qty.toLocaleString()} done` : loopQtyLabel(w.loop_qty)}</span>
      </button>

      {/* Same date, same colours, same rule as the drilling card. */}
      <label style={pill({ position: 'relative', background: ds.bg, borderColor: ds.bd, color: ds.tx })}
        title={date ? 'Scheduled date' : 'No date set'}>
        <span style={{ pointerEvents: 'none' }}>📅 {dateLabel}</span>
        <input type="date" value={date} disabled={busy === 'date'} onChange={e => saveDate(e.target.value)}
        // A date input only opens its picker from the small calendar icon at its right
        // edge, and ours is invisible under the pill — so clicking it did nothing and
        // the date could not be changed from the board at all. A phone opens the picker
        // on any tap, which is exactly why this failed on the desktop and nowhere else.
        onClick={e => { const i = e.currentTarget; if (typeof i.showPicker === 'function') { try { i.showPicker(); } catch { /* unsupported here */ } } }}
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
      </label>

      {/* What kind of loops these are. A job can hold more than one kind, so the
          pillow tallies what is actually on it rather than claiming a single word;
          tapping opens the list, where each location's kind is set. */}
      <button onClick={() => onOpenLoops && onOpenLoops()}
        title={present.length ? 'Loop types on this job — tap to open the list' : 'No loop locations yet — tap to add one'}
        style={pill(present.length
          ? { background: onlyKind ? onlyKind.bg : '#f8fafc', borderColor: onlyKind ? onlyKind.edge : '#e5e7eb', color: onlyKind ? onlyKind.color : '#374151', flexDirection: 'column', gap: 1, lineHeight: 1.15, padding: '6px 8px' }
          : { color: '#9ca3af', borderStyle: 'dashed' })}>
        {present.length ? (<>
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.5, opacity: 0.75 }}>🚦 LOOP TYPE</span>
          <span style={{ fontSize: compact ? 11 : 12, fontWeight: 800 }}>
            {present.map(k => LOOP_KIND_META[k].label + (present.length > 1 || kinds[k] > 1 ? ' ' + kinds[k] : '')).join(' · ')}
          </span>
        </>) : '🚦 No loops yet'}
      </button>

      {extras.filter(Boolean).map(x => (
        <React.Fragment key={x.key || x.label}>
        <button onClick={x.onClick}
          style={pill({ gridColumn: '1 / -1', background: x.open ? (x.tone || '#7c3aed') : (x.toneSoft || '#faf5ff'), borderColor: x.toneEdge || '#ddd6fe', color: x.open ? '#fff' : (x.tone || '#7c3aed') })}>
          {x.label} <span style={{ fontSize: 11, opacity: 0.8 }}>{x.open ? '▲' : '▼'}</span>
        </button>
        {/* What the pillow opens sits right under it — not under the whole stack (Ryan, Oct 1 2026). */}
        {x.open && x.panel ? <div style={{ gridColumn: '1 / -1' }}>{x.panel}</div> : null}
        </React.Fragment>
      ))}

      {updateOpen && (
        <div onClick={stop} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 460, maxWidth: '94vw', padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 800 }}>Daily update</div>
              <button onClick={() => setUpdateOpen(false)} aria-label="Close"
                style={{ background: 'none', border: 'none', fontSize: 24, lineHeight: 1, color: '#9ca3af', cursor: 'pointer', padding: '0 4px' }}>×</button>
            </div>
            <DailyUpdateComposer projectId={w.project_id} subJobId={w.id} autoOpen hideLabel workflowLabel="this Loops board" />
          </div>
        </div>
      )}
    </div>
  );
}

export function DrillPillows({ w, onChanged, showToast, compact, onDailyUpdate, extras = [] }) {
  const { drillsFor } = useCategories();
  // A drill that has since been retired from the list still shows on the card it is on.
  const rigChoices = [...drillsFor(w.type), ...(w.drill_rig && !drillsFor(w.type).includes(w.drill_rig) ? [w.drill_rig] : [])];
  const [rig, setRig] = React.useState(w.drill_rig || '');
  const [date, setDate] = React.useState(w.drill_date || '');
  const [busy, setBusy] = React.useState('');
  const [updateOpen, setUpdateOpen] = React.useState(false);
  const [updateText, setUpdateText] = React.useState('');
  React.useEffect(() => { setRig(w.drill_rig || ''); setDate(w.drill_date || ''); }, [w.drill_rig, w.drill_date]);

  const stop = e => { e.stopPropagation(); };
  const pill = (extra = {}) => ({
    minHeight: DRILL_PILL_H, boxSizing: 'border-box', width: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    borderRadius: 10, border: '1.5px solid #e5e7eb', background: '#fff',
    fontSize: compact ? 12 : 13, fontWeight: 700, color: '#374151',
    cursor: 'pointer', padding: '0 10px', textAlign: 'center', ...extra,
  });

  async function saveRig(next) {
    setBusy('rig'); setRig(next);
    try {
      await apiFetch(`/sub-jobs/${w.id}/drill`, { method: 'POST', body: JSON.stringify({ drill_rig: next || null }) });
      onChanged && onChanged();
    } catch (e) { setRig(w.drill_rig || ''); showToast && showToast(e.message, 'error'); }
    setBusy('');
  }
  async function saveDate(next) {
    setBusy('date'); setDate(next);
    try {
      await apiFetch(`/sub-jobs/${w.id}/card`, { method: 'PATCH', body: JSON.stringify({ drill_date: next || null }) });
      onChanged && onChanged();
    } catch (e) { setDate(w.drill_date || ''); showToast && showToast(e.message, 'error'); }
    setBusy('');
  }
  async function postUpdate() {
    const notes = updateText.trim();
    if (!notes) return;
    setBusy('update');
    try {
      await apiFetch(`/projects/${w.project_id}/daily-updates`, { method: 'POST', body: JSON.stringify({ notes, sub_job_id: w.id }) });
      setUpdateText(''); setUpdateOpen(false);
      showToast && showToast('Update posted');
      onChanged && onChanged();
    } catch (e) { showToast && showToast(e.message, 'error'); }
    setBusy('');
  }

  const ds = DRILL_DATE_STYLE[drillDateState(w, date)];
  const dateLabel = date
    ? spanLabel(date, w.sched_days)
    : 'Set date';

  return (
    // draggable={false} is the point of this line. On the desktop board the whole card
    // is draggable, and a draggable ancestor makes the browser start a drag on mousedown
    // instead of handing the click to the control underneath — so the date picker and the
    // drill dropdown never opened. The phone has no draggable card, which is exactly why
    // the same pillows worked there and not here.
    <div onClick={stop} draggable={false} onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}
      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
      {/* Daily Update leads: it's the thing done every day, and the drill is set once.
          On the open card there's already a fuller composer below, so point at it
          rather than posting the same thing two different ways. */}
      <button onClick={() => onDailyUpdate ? onDailyUpdate() : setUpdateOpen(true)} style={pill({ background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' })}>
        ➕ Daily Update
      </button>

      {/* Drill — a real select, so it behaves the same under a thumb as under a mouse */}
      <label style={pill({ position: 'relative', background: rig ? '#fffbeb' : '#fff', borderColor: rig ? '#fde68a' : '#e5e7eb', color: rig ? '#92400e' : '#9ca3af' })}>
        <span style={{ pointerEvents: 'none' }}>🛠️ {rig || 'No drill'}</span>
        <select value={rig} disabled={busy === 'rig'} onChange={e => saveRig(e.target.value)}
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}>
          <option value="">No drill</option>
          {rigChoices.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </label>

      {/* Scheduled date — the colour is the whole point; see drillDateState */}
      <label style={pill({ position: 'relative', background: ds.bg, borderColor: ds.bd, color: ds.tx })}
        title={date ? 'Scheduled drill date' : 'No drill date set'}>
        <span style={{ pointerEvents: 'none' }}>📅 {dateLabel}</span>
        <input type="date" value={date} disabled={busy === 'date'} onChange={e => saveDate(e.target.value)}
        // A date input only opens its picker from the small calendar icon at its right
        // edge, and ours is invisible under the pill — so clicking it did nothing and
        // the date could not be changed from the board at all. A phone opens the picker
        // on any tap, which is exactly why this failed on the desktop and nowhere else.
        onClick={e => { const i = e.currentTarget; if (typeof i.showPicker === 'function') { try { i.showPicker(); } catch { /* unsupported here */ } } }}
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
      </label>

      {/* USA locate — the same fact on every board, so the same pillow. */}
      <UsaPillow w={w} pill={pill} compact={compact} onChanged={onChanged} showToast={showToast} />

      {/* z-index sits above the drilling card modal (5000), not below it — these
          pillows live inside that card as well as on the board. */}
      {/* Full-width pillows under the four — the long sections of the card, folded
          away behind a tap. Billing, holes: things you open a card for sometimes, not
          things you want in the way every time. */}
      {extras.filter(Boolean).map(x => (
        <React.Fragment key={x.key || x.label}>
        <button onClick={x.onClick}
          style={pill({ gridColumn: '1 / -1', background: x.open ? (x.tone || '#7c3aed') : (x.toneSoft || '#faf5ff'), borderColor: x.toneEdge || '#ddd6fe', color: x.open ? '#fff' : (x.tone || '#7c3aed') })}>
          {x.label} <span style={{ fontSize: 11, opacity: 0.8 }}>{x.open ? '▲' : '▼'}</span>
        </button>
        {/* What the pillow opens sits right under it — not under the whole stack (Ryan, Oct 1 2026). */}
        {x.open && x.panel ? <div style={{ gridColumn: '1 / -1' }}>{x.panel}</div> : null}
        </React.Fragment>
      ))}

      {updateOpen && (
        <div onClick={stop} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 460, maxWidth: '94vw', padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 800 }}>Daily update</div>
              <button onClick={() => setUpdateOpen(false)} aria-label="Close"
                style={{ background: 'none', border: 'none', fontSize: 24, lineHeight: 1, color: '#9ca3af', cursor: 'pointer', padding: '0 4px' }}>×</button>
            </div>
            {/* The real composer, not a copy of it — it carries the mic and files the
                update against this sub-job, both of which a second textarea would lose. */}
            <DailyUpdateComposer projectId={w.project_id} subJobId={w.id} autoOpen hideLabel
              workflowLabel="this drilling board" />
          </div>
        </div>
      )}
    </div>
  );
}

// Canonical status columns (data uses pending / active / complete; on_hold supported).
const STATUS_COLUMNS = [
  { key: 'pending',  label: 'Not Started', color: '#6b7280', bg: '#f9fafb' },
  { key: 'active',   label: 'In Progress', color: '#1d4ed8', bg: '#eff6ff' },
  { key: 'on_hold',  label: 'On Hold',     color: '#b45309', bg: '#fffbeb' },
  { key: 'complete', label: 'Complete',    color: '#15803d', bg: '#f0fdf4' },
];
const STATUS_LABEL = Object.fromEntries(STATUS_COLUMNS.map(s => [s.key, s.label]));
const STATUS_META  = Object.fromEntries(STATUS_COLUMNS.map(s => [s.key, s]));
// On these boards "active" means the work is on the schedule, not that somebody is
// standing over it — so they say Scheduled instead of In Progress. Only the word
// changes; the stored value is still 'active'.
//
// Deliberately its own list rather than SCHEDULED_TYPES, which it nearly matches:
// small jobs is scheduled work, but its board is the work-order kanban with no status
// columns, and Ryan wants its rows left reading the ordinary way.
const SAYS_SCHEDULED = ['vertical_drilling', 'horizontal_drilling', 'loops'];
const SCHEDULED_STATUS_COLUMNS = STATUS_COLUMNS.map(s => s.key === 'active' ? { ...s, label: 'Scheduled' } : s);
const SCHEDULED_STATUS_META    = Object.fromEntries(SCHEDULED_STATUS_COLUMNS.map(s => [s.key, s]));

// Field (Zone 2) workflows show on this Workflows dashboard. Office (Zone 1)
// workflows live under the Administration tab instead. Fiber/custom stay hidden.
// The Work Flow tab groups field workflows into SCHEDULED (the field crews schedule
// these) and NON-SCHEDULED. Small Jobs is a scheduled board (the Work Orders Kanban).
const SCHEDULED_TYPES = ['vertical_drilling', 'horizontal_drilling', 'loops', 'small_jobs'];
const UNSCHEDULED_TYPES = ['nve', 'usa_digs', 'shop', 'permits'];   // Fiber became Permits (Pat, Oct 9 2026)
const CORE_TYPES = [...SCHEDULED_TYPES, ...UNSCHEDULED_TYPES];
const OFFICE_TYPES = ['contract', 'subcontracts', 'payroll', 'submittals'];

// Submittals carry their own status set, ordered least→most advanced.
const SUBMITTAL_STATUS_COLUMNS = [
  { key: 'not_submitted',            label: 'Not Submitted',          color: '#6b7280', bg: '#f9fafb' },
  { key: 'requested',                label: 'Requested',              color: '#b45309', bg: '#fffbeb' },   // asked the vendor for the documents (Oct 2026)
  { key: 'submitted',                label: 'Submitted',              color: '#1d4ed8', bg: '#eff6ff' },
  { key: 'revise_resubmit',          label: 'Revise & Resubmit',      color: '#b91c1c', bg: '#fef2f2' },
  { key: 'approved_with_conditions', label: 'Approved w/ Conditions', color: '#b45309', bg: '#fffbeb' },
  { key: 'approved',                 label: 'Approved · Holding for Release', color: '#15803d', bg: '#f0fdf4' },
  { key: 'approved_no_action',       label: 'Approved · No Further Action', color: '#4d7c0f', bg: '#f7fee7' },   // bought as needed (Oct 7 2026)
];
const SUBMITTAL_META = Object.fromEntries(SUBMITTAL_STATUS_COLUMNS.map(s => [s.key, s]));

// USA (811) dig ticket statuses.
const USA_STATUS_COLUMNS = [
  { key: 'not_called_in', label: 'Not Called In', color: '#6b7280', bg: '#f3f4f6' },
  { key: 'called_in',     label: 'Called In',     color: '#b45309', bg: '#fffbeb' },
  { key: 'active',        label: 'Active',        color: '#15803d', bg: '#f0fdf4' },
  { key: 'expiring_soon', label: 'Expiring Soon', color: '#b45309', bg: '#fffbeb' },
  { key: 'dug',           label: 'Dug',           color: '#57534e', bg: '#f5f5f4' },
  { key: 'expired',       label: 'Expired',       color: '#b91c1c', bg: '#fef2f2' },
];
const USA_META = Object.fromEntries(USA_STATUS_COLUMNS.map(s => [s.key, s]));

// NVE meter statuses (each meter is its own card on the NVE board).
const NVE_STATUS_COLUMNS = [
  { key: 'application_in',     label: 'Application In',       color: '#6b7280', bg: '#f3f4f6' },
  { key: 'construction_ready', label: 'Construction Ready',   color: '#1d4ed8', bg: '#eff6ff' },
  { key: 'trench_sand_tape',   label: 'Trench/Sand/Tape',     color: '#7c3aed', bg: '#f5f3ff' },
  { key: 'called_in_final',    label: 'Called In for Final',  color: '#d97706', bg: '#fffbeb' },
  { key: 'finaled',            label: 'Finaled',              color: '#059669', bg: '#ecfdf5' },
  { key: 'meter_set',          label: 'Meter Set / Complete', color: '#15803d', bg: '#dcfce7' },
];
const NVE_META = Object.fromEntries(NVE_STATUS_COLUMNS.map(s => [s.key, s]));

// Payroll (certified payroll) statuses.
const PAY_STATUS_COLUMNS = [
  { key: 'due',       label: 'Due',       color: '#b45309', bg: '#fffbeb' },
  { key: 'submitted', label: 'Submitted', color: '#1d4ed8', bg: '#eff6ff' },
  { key: 'accepted',  label: 'Accepted',  color: '#15803d', bg: '#f0fdf4' },
];
const PAY_META = Object.fromEntries(PAY_STATUS_COLUMNS.map(s => [s.key, s]));

const DUE_BUCKETS = [
  { key: 'overdue', label: 'Overdue',    color: '#b91c1c', bg: '#fef2f2' },
  { key: 'week',    label: 'This Week',  color: '#b45309', bg: '#fffbeb' },
  { key: 'month',   label: 'This Month', color: '#1d4ed8', bg: '#eff6ff' },
  { key: 'later',   label: 'Later',      color: '#6b7280', bg: '#f9fafb' },
  { key: 'none',    label: 'No Date',    color: '#9ca3af', bg: '#f9fafb' },
];
function dueBucket(dateStr) {
  if (!dateStr) return 'none';
  // A bare YYYY-MM-DD parses as UTC midnight, which is the previous evening in Reno —
  // so a job due today bucketed as overdue. asLocalDate pins it to local midnight.
  const d = asLocalDate(dateStr); if (!d) return 'none';
  const now = new Date(); now.setHours(0, 0, 0, 0); d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - now) / 86400000);
  if (diff < 0) return 'overdue';
  if (diff <= 7) return 'week';
  if (diff <= 31) return 'month';
  return 'later';
}
function fmtDate(d) {
  if (!d) return null;
  return d ? fmtDateLocal(d) : null;   // date-only safe — see utils/ui
}
const qtyNiceWf = n => { const v = Number(n || 0); return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100); };
const fmtMoneyWf = n => (n == null ? '$0.00' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// Closed cards. Not a board — there are no statuses left to drag between — so it
// reads as a list, filed by the year it was closed in, the way finished work is filed
// everywhere else here. Every row still opens the whole card.
function ClosedArchive({ rows, total, loading, pmName, onOpen, compact }) {
  const note = { color: '#9ca3af', textAlign: 'center', padding: compact ? '30px 0' : 40 };
  if (loading) return <p style={note}>Loading…</p>;
  if (!rows.length) return <p style={note}>{total
    ? `Nothing closed matches that — ${total} closed card${total === 1 ? '' : 's'} on this board.`
    : 'Nothing closed yet — closed cards land here and stay searchable.'}</p>;

  const years = [];
  for (const r of rows) {
    const y = (r.closed_at || '').slice(0, 4) || 'Undated';
    let g = years.find(x => x.year === y);
    if (!g) years.push(g = { year: y, rows: [] });
    g.rows.push(r);
  }
  const badge = { fontSize: 11, color: '#6b7280', background: '#f3f4f6', padding: '3px 8px', borderRadius: 10, fontWeight: 600 };

  return (
    <div style={{ maxWidth: compact ? 540 : 900 }}>
      {years.map(g => (
        <div key={g.year}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '14px 2px 8px' }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', letterSpacing: 0.6 }}>{g.year}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', background: '#f3f4f6', borderRadius: 999, padding: '2px 8px' }}>{g.rows.length}</span>
            <span style={{ flex: 1, height: 1, background: '#ececec' }} />
          </div>
          {g.rows.map(w => (
            <div key={w.id} role="button" tabIndex={0} onClick={() => onOpen(w)}
              style={{ background: '#fff', border: '1.5px solid #ececec', borderLeft: '5px solid #9ca3af', borderRadius: 12,
                padding: '12px 16px', marginBottom: 8, cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>
                  {jobNumber(wfJob(w)) || '—'}
                  <span style={{ fontWeight: 600, color: '#374151', marginLeft: 8 }}>{jobName(wfJob(w))}</span>
                </div>
                <span style={{ fontSize: 11.5, color: '#6b7280' }}>
                  Closed {fmtDate(w.closed_at) || '—'}{w.closed_by_name ? ' · ' + w.closed_by_name : ''}
                  {closeTag(w) && <span style={{ marginLeft: 8, fontWeight: 800, color: closeTag(w).color, background: closeTag(w).bg, borderRadius: 999, padding: '2px 8px' }}>{closeTag(w).text}</span>}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                {w.client_name && <span style={badge}>🏢 {w.client_name}</span>}
                <span style={badge}>👤 {pmName(w.pm_id)}</span>
                {w.drill_rig && <span style={badge}>🛠️ {w.drill_rig}</span>}
                {w.hole_count > 0 && <span style={badge}>{holesPillowLabel(w.hole_count, w.holes_done, w.hole_depth_total)}</span>}
                {w.drill_hours_total > 0 && <span style={badge}>⏱️ {w.drill_hours_total} hrs</span>}
                {w.drill_date && <span style={badge}>📅 {w.sched_days > 1 ? spanLabel(w.drill_date, w.sched_days) : fmtDate(w.drill_date)}</span>}
                {w.photo_count > 0 && <span style={badge}>📷 {w.photo_count}</span>}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

const GROUP_OPTIONS = [
  { key: 'status', label: 'Status' },
  { key: 'pm',     label: 'PM' },
  { key: 'client', label: 'Owner' },
  { key: 'due',    label: 'Due Date' },
];

export default function AdminWorkflows({ embedded = false, basePath = '/admin/workflows' }) {
  const { drillsFor } = useCategories();
  const navigate = useNavigate();
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const Wrap = embedded ? React.Fragment : AdminLayout;
  const { worker } = useAuth() || {};
  // PMs (and Admin) on mobile also see Zone 1 / office workflows.
  const includeOffice = embedded && ['ProjectManager', 'Admin', 'Accounting'].includes(worker?.role);
  // Field Supervisors see the boards their crews are sent out on — the scheduled ones, plus NVE and
  // USA Digs (Pat, Oct 8 2026). Shop and fiber still open from the job they belong to.
  const scheduledOnly = embedded && worker?.role === 'FieldSupervisor';
  const shownTypeKeys = includeOffice
    ? [...CORE_TYPES, ...OFFICE_TYPES]
    : scheduledOnly ? [...SCHEDULED_TYPES, 'nve', 'usa_digs'] : CORE_TYPES;

  const [workflows, setWorkflows] = useState([]);
  const [users, setUsers] = useState([]);
  const [smallJobs, setSmallJobs] = useState([]); // Small Jobs board cards (from /work-orders, not /workflows)
  const [hubQuery, setHubQuery] = useState('');   // search on the Work Flows landing page
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState('status');
  const [search, setSearch] = useState('');
  const [pmFilter, setPmFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // Mobile grouping for the drilling lists. Remembered on the device, because
  // whichever way you read the board is the way you read it every morning.
  const [mobileByDrill, setMobileByDrill] = useState(() => {
    try { return localStorage.getItem('titan_wf_mobile_group') !== 'status'; } catch { return true; }
  });
  const [drillFilter, setDrillFilter] = useState('');
  // The archive is fetched only when asked for. It grows for ever, and nobody opening
  // the board in the morning is waiting on last year's finished holes.
  const [showClosed, setShowClosed] = useState(false);
  const [closedRows, setClosedRows] = useState(null);
  // The tally arrives with the board so the button can say how much is behind it.
  const [closedCounts, setClosedCounts] = useState({});
  function setMobileGrouping(byDrill) {
    setMobileByDrill(byDrill);
    // The chips change meaning, so a filter set under the old grouping would keep
    // narrowing the list with nothing on screen to say so.
    setStatusFilter(''); setDrillFilter('');
    try { localStorage.setItem('titan_wf_mobile_group', byDrill ? 'drill' : 'status'); } catch { /* private mode */ }
  }

  const [dragId, setDragId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  // The desktop board: every column the same height — the room left under the
  // controls — scrolling inside itself, so a long column no longer towers over the
  // rest. On a phone each column is the screen width and you swipe (or tap a chip)
  // from one to the next, then scroll that column. Same as the Small Jobs board
  // (Pat, Sep 2026).
  const boardNarrow = useIsNarrow(640);
  const boardRef = useRef(null);
  const [boardH, setBoardH] = useState(null);
  const [boardColIdx, setBoardColIdx] = useState(0);
  const boardChipRefs = useRef({});
  useLayoutEffect(() => {
    const measure = () => {
      const el = boardRef.current; if (!el) return;
      const scroller = el.closest('.admin-main');
      const top = el.getBoundingClientRect().top + (scroller ? scroller.scrollTop : window.scrollY);
      setBoardH(Math.max(boardNarrow ? 360 : 420, Math.round(window.innerHeight - top - (boardNarrow ? 14 : 34))));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  });
  useEffect(() => { boardChipRefs.current[boardColIdx]?.scrollIntoView?.({ inline: 'center', block: 'nearest', behavior: 'smooth' }); }, [boardColIdx]);
  const boardScrollTo = i => { const el = boardRef.current; if (el) el.scrollTo({ left: i * (el.clientWidth + BOARD_GAP), behavior: 'smooth' }); };
  // { id, where } — which card you are over, and which side of it you are on.
  const [dragOverCard, setDragOverCard] = useState(null);
  const overCardId = dragOverCard && dragOverCard.id;
  const [sheetItem, setSheetItem] = useState(null); // mobile: card tapped to change status
  const [drillCard, setDrillCard] = useState(null); // drilling card opened as a Trello-style window
  const [nveCard, setNveCard] = useState(null); // NVE meter card opened as a full modal
  const [loopCard, setLoopCard] = useState(null); // Loops card opened as a full modal
  const [usaCard, setUsaCard] = useState(null); // USA (811) card opened as a full modal
  const dragRef = React.useRef(null); // synchronous source of truth for the dragged card

  const reloadWorkflows = () => apiFetch('/workflows').then(setWorkflows).catch(() => {});
  const loadClosed = () => apiFetch('/workflows?closed=1').then(setClosedRows).catch(() => setClosedRows([]));
  useEffect(() => {
    apiFetch('/workflows/closed-counts')
      .then(rows => setClosedCounts(Object.fromEntries((rows || []).map(r => [r.type, r.n]))))
      .catch(() => {});
  }, []);
  // Reopening a card moves it between the two lists, so both have to be re-read.
  const reloadBoth = () => { reloadWorkflows(); if (closedRows) loadClosed(); };
  useEffect(() => { if (showClosed && closedRows === null) loadClosed(); }, [showClosed]); // eslint-disable-line react-hooks/exhaustive-deps
  // Searching the hub is asking about a job, not about this week. Someone looking up
  // a number for a bid wants the drilling that was finished last year most of all, so
  // typing anything there pulls the archive in behind it.
  useEffect(() => { if (hubQuery.trim() && closedRows === null) loadClosed(); }, [hubQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link: /admin/workflows/<type>?card=<subJobId> opens that card straight
  // away, so a notification or activity entry lands on the exact card it was
  // written on instead of the job page. Runs once per card id, after load.
  const openedCardRef = useRef('');
  useEffect(() => {
    const wanted = searchParams.get('card');
    if (!wanted || !workflows.length || openedCardRef.current === wanted) return;
    // A card id, or (the job page's Take to Card, Nov 3 2026) the JOB id — its card of this board's type.
    const w = workflows.find(x => x.id === wanted || x.sub_job_id === wanted)
      || workflows.find(x => x.project_id === wanted && x.type === type);
    if (!w) return;
    openedCardRef.current = wanted;
    if (isDrillType(w.type)) setDrillCard(w);
    else if (w.type === 'nve') setNveCard(w);
    else if (w.type === 'loops') setLoopCard(w);
    else if (w.type === 'usa_digs') setUsaCard(w);
    else setSheetItem(w);
  }, [workflows, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // The same link, when the card has since been closed — followed from the hub search,
  // or from an activity entry written back when it was live. It opens the archive and
  // the card, rather than dropping you on a board the card is no longer on.
  useEffect(() => {
    const wanted = searchParams.get('card');
    if (!wanted || !workflows.length || openedCardRef.current === wanted) return;
    if (workflows.some(x => x.id === wanted || x.sub_job_id === wanted || (x.project_id === wanted && x.type === type))) return;
    if (closedRows === null) { loadClosed(); return; }
    const w = closedRows.find(x => x.id === wanted || x.sub_job_id === wanted)
      || closedRows.find(x => x.project_id === wanted && x.type === type);
    if (!w) return;
    openedCardRef.current = wanted;
    setShowClosed(true);
    if (isDrillType(w.type)) setDrillCard(w);
    else if (w.type === 'nve') setNveCard(w);
    else if (w.type === 'loops') setLoopCard(w);
    else if (w.type === 'usa_digs') setUsaCard(w);
    else setSheetItem(w);
  }, [workflows, closedRows, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    apiFetch('/workflows').then(setWorkflows).catch(e => console.error('workflows load failed', e)).finally(() => setLoading(false));
    apiFetch('/users').then(setUsers).catch(() => {});
    apiFetch('/work-orders').then(w => setSmallJobs((Array.isArray(w) ? w : []).filter(x => (x.schedule_workflow || 'small_jobs') === 'small_jobs'))).catch(() => {});
  }, []);

  // Drilling cards open a self-contained window (machine, holes, photos) so the
  // crew never leaves the board. Everything else keeps its existing behavior.
  const isDrillType = t => t === 'vertical_drilling' || t === 'horizontal_drilling';

  function pmName(id) {
    if (!id) return 'Unassigned';
    const u = users.find(u => String(u.id) === String(id));
    return u ? (`${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unassigned') : 'Unassigned';
  }

  // ── Landing: one card per workflow type ──────────────────────────────────
  if (!type) {
    const counts = {};
    // NVE is counted by meter (each meter is a card on its board); the rest by job.
    workflows.forEach(w => { counts[w.type] = (counts[w.type] || 0) + (w.type === 'nve' ? (w.nve_count || 0) : 1); });
    counts.small_jobs = smallJobs.length; // Small Jobs cards come from /work-orders, not /workflows
    const coreWorkflows = workflows.filter(w => shownTypeKeys.includes(w.type));
    const jobCount = new Set(coreWorkflows.map(w => w.project_id)).size;
    const visible = t => shownTypeKeys.includes(t) && (CORE_TYPES.includes(t) || counts[t] > 0);
    const sections = [
      { key: 'sched',   label: 'Scheduled Work Flows',     types: SCHEDULED_TYPES },
      { key: 'unsched', label: 'Not Scheduled',            types: UNSCHEDULED_TYPES },
      { key: 'office',  label: 'Office',                   types: OFFICE_TYPES },
    ];
    const secLabel = { fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.6, margin: '0 0 10px' };
    const renderTile = t => {
      const m = typeMeta(t);
      return embedded ? (
        <button key={t} onClick={() => navigate(`${basePath}/${t}`)}
          style={{ textAlign: 'left', background: '#fff', border: '1.5px solid #ececec', borderLeft: `5px solid ${m.color}`, borderRadius: 12, padding: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 26 }}>{m.icon}</span>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#111' }}>{m.label}</div>
              <div style={{ fontSize: 13, color: '#8a8a8a', marginTop: 2 }}>{counts[t] || 0} active</div>
            </div>
          </div>
          <span style={{ color: '#e87722', fontSize: 22 }}>›</span>
        </button>
      ) : (
        <button key={t} onClick={() => navigate(`${basePath}/${t}`)}
          style={{ textAlign: 'left', background: '#fff', border: '1px solid var(--border)', borderTop: `4px solid ${m.color}`, borderRadius: 12, padding: 20, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8, transition: 'box-shadow .15s, transform .1s' }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
          <div style={{ fontSize: 30 }}>{m.icon}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{m.label}</div>
          <div style={{ fontSize: 13, color: m.color, fontWeight: 600 }}>{counts[t] || 0} active</div>
        </button>
      );
    };
    // Search spans every board at once, so you can find a job without first knowing
    // which workflow it's filed under. Small Jobs work orders come from a different
    // endpoint, so they're folded in here rather than left out of their own search.
    const hq = hubQuery.trim().toLowerCase();
    const hubHits = !hq ? [] : [
      ...workflows.filter(w => shownTypeKeys.includes(w.type)).map(w => ({
        key: w.id, type: w.type, to: `${basePath}/${w.type}?card=${w.id}`,
        number: w.project_number, title: w.project_name, client: w.client_name, status: w.status,
      })),
      ...smallJobs.map(w => ({
        key: w.id, type: 'small_jobs', to: `/admin/new-projects/${w.id}`,
        number: w.project_number, title: w.name, client: w.client_name, status: w.wo_status,
      })),
      // Finished work, marked as such. It is the answer to "what did we do there",
      // which is most of what anyone types into this box.
      ...(closedRows || []).filter(w => shownTypeKeys.includes(w.type)).map(w => ({
        key: w.id, type: w.type, to: `${basePath}/${w.type}?card=${w.id}`, closed: true,
        number: w.project_number, title: w.project_name, client: w.client_name,
        status: w.closed_at ? `closed ${fmtDate(w.closed_at)}` : 'closed',
      })),
    ].filter(h => `${h.number || ''} ${h.title || ''} ${h.client || ''} ${typeMeta(h.type).label}`.toLowerCase().includes(hq));

    return (
      <Wrap>
        <div className="admin-page-header" style={embedded ? { paddingTop: 'max(env(safe-area-inset-top), 14px)' } : undefined}>
          <div>
            {embedded && <button onClick={() => navigate('/dashboard')} style={{ background: '#f3f4f6', border: 'none', color: '#e87722', fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: '10px 16px', borderRadius: 20, marginBottom: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}>‹ Home</button>}
            <h1 className="admin-page-title">Work Flows</h1>
            <p className="admin-page-sub">
              {hq
                ? `${hubHits.length} card${hubHits.length === 1 ? '' : 's'} matching “${hubQuery.trim()}” across every board`
                : `${coreWorkflows.length} active workflow${coreWorkflows.length !== 1 ? 's' : ''} across ${jobCount} job${jobCount !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginBottom: 18, maxWidth: embedded ? '100%' : 380 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ position: 'absolute', left: 12, color: 'var(--text-muted)', pointerEvents: 'none' }}><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/><path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          <input value={hubQuery} onChange={e => setHubQuery(e.target.value)} placeholder="Search every board — job, number, client…"
            style={{ width: '100%', padding: '10px 32px 10px 34px', fontSize: 14, border: '1px solid var(--border)', borderRadius: 999, background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box' }} />
          {hubQuery && <button onClick={() => setHubQuery('')} title="Clear search"
            style={{ position: 'absolute', right: 9, width: 20, height: 20, borderRadius: 999, border: 'none', background: '#e5e7eb', color: '#6b7280', fontSize: 13, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>×</button>}
        </div>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : hq ? (
          hubHits.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Nothing on any board matches “{hubQuery.trim()}”.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {hubHits.map(h => {
                const m = typeMeta(h.type);
                return (
                  <button key={`${h.type}-${h.key}`} onClick={() => navigate(h.to)}
                    style={{ textAlign: 'left', background: 'var(--surface)', border: '1px solid var(--border)', borderLeft: `5px solid ${m.color}`, borderRadius: 10, padding: '12px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 22 }}>{m.icon}</span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {h.number ? `${h.number} — ` : ''}{h.title || 'Untitled'}
                        {h.closed && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 800, color: '#57534e', background: '#f5f5f4', border: '1px solid #e7e5e4', borderRadius: 999, padding: '2px 7px', verticalAlign: 'middle' }}>ARCHIVED</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                        {m.label}{h.client ? ` · ${h.client}` : ''}{h.status ? ` · ${String(h.status).replace(/_/g, ' ')}` : ''}
                      </div>
                    </div>
                    <span style={{ color: '#e87722', fontSize: 20 }}>›</span>
                  </button>
                );
              })}
            </div>
          )
        ) : (
          sections.map(sec => {
            const secTypes = sec.types.filter(visible);
            if (!secTypes.length) return null;
            return (
              <div key={sec.key} style={{ marginBottom: 22 }}>
                <h2 style={secLabel}>{sec.label}</h2>
                <div style={{ display: 'grid', gridTemplateColumns: embedded ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))', gap: embedded ? 12 : 16 }}>
                  {secTypes.map(renderTile)}
                </div>
              </div>
            );
          })
        )}
      </Wrap>
    );
  }

  // Permits is its own board too (Pat, Oct 9 2026 — it was Fiber): Apply For → Submitted → Approved
  // → Completed, four tiles on each card. Office, PMs and Executives.
  if (type === 'permits') {
    return (
      <Wrap>
        <div className="admin-page-header" style={embedded ? { paddingTop: 'max(env(safe-area-inset-top), 14px)' } : undefined}>
          <div>
            <button onClick={() => navigate(embedded ? '/dashboard' : basePath)} style={{ background: '#f3f4f6', border: 'none', color: '#e87722', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '7px 14px', borderRadius: 20, marginBottom: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}>‹ Work Flows</button>
            <h1 className="admin-page-title">📜 Permits</h1>
            <p className="admin-page-sub">Building, encroachment and every other permit — Washoe County, Reno, Sparks, Carson, Fernley, NDOT, Storey County… One card per permit; a closed one goes to its job’s Permits tile.</p>
            <div style={{ marginTop: 10 }}><ActivityPills pageName="Permits" scope="permits" summaryLabel="Permits Update" linkJobs={!embedded} /></div>
          </div>
        </div>
        <PermitsBoard />
      </Wrap>
    );
  }

  // Small Jobs is its own board (the Work Orders Kanban), not a sub-job workflow.
  if (type === 'small_jobs') {
    return (
      <Wrap>
        <div className="admin-page-header" style={embedded ? { paddingTop: 'max(env(safe-area-inset-top), 14px)' } : undefined}>
          <div>
            <button onClick={() => navigate(embedded ? '/dashboard' : basePath)} style={{ background: '#f3f4f6', border: 'none', color: '#e87722', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '7px 14px', borderRadius: 20, marginBottom: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}>‹ Work Flows</button>
            <h1 className="admin-page-title">🗂️ Small Jobs</h1>
            <p className="admin-page-sub">Drag a card to schedule &amp; stack jobs · pick another board on a card to hand it off</p>
            {/* The same three pills the other boards carry (Pat, Sep 23 2026). */}
            <div style={{ marginTop: 10 }}><ActivityPills pageName="Small Jobs" scope="small_jobs" summaryLabel="Small Jobs Update" linkJobs={!embedded}
              leading={embedded ? [] : [{ label: 'Small Jobs Schedule', icon: '📅', onClick: () => navigate('/admin/workflows/small_jobs/schedule') }]} /></div>
          </div>
        </div>
        <WorkOrdersBoard cardModal={WorkOrderCardModal} pillows={WorkOrderPillows} />
      </Wrap>
    );
  }

  // ── Drilldown: Kanban board for one workflow type ────────────────────────
  const meta = typeMeta(type);
  const rawTypeItems = workflows.filter(w => w.type === type);

  // Submittals use their own status set (rolled up per job); everything else
  // uses the generic sub-job status.
  const isSubmittals = type === 'submittals';
  const isUsa = type === 'usa_digs';
  const isPayroll = type === 'payroll';
  const isNve = type === 'nve';

  // NVE: each meter is its own card. Flatten every NVE workflow into one card
  // per meter, carrying the parent sub-job + meter id so a drag can patch that
  // meter's status. A workflow with no meters yet shows a single placeholder.
  const typeItems = isNve
    ? rawTypeItems.flatMap(w => {
        const meters = Array.isArray(w.meters) ? w.meters : [];
        // No meters = underground only: the card's status is the sub-job's own (Oct 3 2026).
        if (meters.length === 0) return [{ ...w, id: `${w.id}::none`, sub_job_id: w.id, meter_id: null, meter_seq: 0, meter_address: null, nve_status: w.nve_status || 'application_in' }];
        return meters.map((m, i) => ({ ...w, id: `${w.id}::${m.id}`, sub_job_id: w.id, meter_id: m.id, meter_seq: i + 1, meter_address: m.address, meter: m, nve_status: m.status || 'application_in' }));
      })
    : rawTypeItems;

  const genericStatusColumns = SAYS_SCHEDULED.includes(type) ? SCHEDULED_STATUS_COLUMNS : STATUS_COLUMNS;
  const genericStatusMeta    = SAYS_SCHEDULED.includes(type) ? SCHEDULED_STATUS_META    : STATUS_META;
  const statusColumns = isSubmittals ? SUBMITTAL_STATUS_COLUMNS : isUsa ? USA_STATUS_COLUMNS : isPayroll ? PAY_STATUS_COLUMNS : isNve ? NVE_STATUS_COLUMNS : genericStatusColumns;
  const statusMetaMap = isSubmittals ? SUBMITTAL_META : isUsa ? USA_META : isPayroll ? PAY_META : isNve ? NVE_META : genericStatusMeta;
  const statusValue = (w) => isSubmittals ? (w.submittal_status || 'not_submitted') : isUsa ? (w.usa_status || 'not_called_in') : isPayroll ? (w.payroll_status || 'due') : isNve ? (w.nve_status || 'application_in') : (w.status || 'pending');

  // Cards someone has placed by hand come first, in that order; anything never
  // dragged keeps falling back to the order the server sent (by due date).
  const byBoardOrder = (a, b) => {
    const A = a.board_order, B = b.board_order;
    if (A == null && B == null) return 0;
    if (A == null) return 1;
    if (B == null) return -1;
    return A - B;
  };
  const filtered = typeItems.filter(w => {
    const q = search.trim().toLowerCase();
    // …and the dig ticket numbers on a USA Digs card (Oct 21 2026), so a ticket number finds its job.
    const matchSearch = !q || `${w.project_number || ''} ${w.project_name || ''} ${w.client_name || ''} ${w.meter_address || ''} ${(w.usa_tickets || []).map(t => t.usa_number || '').join(' ')} ${(w.usa_numbers || []).join(' ')}`.toLowerCase().includes(q);
    const matchPm = !pmFilter || String(w.pm_id || '') === String(pmFilter);
    const matchClient = !clientFilter || (w.client_name || '') === clientFilter;
    const matchStatus = !statusFilter || statusValue(w) === statusFilter;
    return matchSearch && matchPm && matchClient && matchStatus;
  });

  // The archive answers to the same search box and the same PM and Owner filters.
  // Status is not among them: everything here has the one status.
  // Once the archive is loaded it counts itself; until then the tally speaks for it.
  const closedTotal = closedRows ? closedRows.filter(w => w.type === type).length : (closedCounts[type] || 0);
  const closedFiltered = (closedRows || []).filter(w => {
    const q = search.trim().toLowerCase();
    if (w.type !== type) return false;
    const hay = `${w.project_number || ''} ${w.project_name || ''} ${w.client_name || ''} ${w.name || ''} ${w.drill_rig || ''}`.toLowerCase();
    return (!q || hay.includes(q))
      && (!pmFilter || String(w.pm_id || '') === String(pmFilter))
      && (!clientFilter || (w.client_name || '') === clientFilter);
  });

  // Build columns for the current group-by
  function colKeyFor(w) {
    if (groupBy === 'pm')     return w.pm_id || '__none';
    if (groupBy === 'client') return w.client_name || '__none';
    if (groupBy === 'due')    return dueBucket(isDrillType(w.type) ? w.drill_date : w.sched_finish);
    // Grouped by drill, this board is a schedule: a finished hole is no longer work
    // for a rig, so it moves out to its own column on the right rather than padding
    // the column of the rig that drilled it. The card still carries the rig badge, so
    // nothing about who did it is lost.
    if (groupBy === 'drill')  return statusValue(w) === 'complete' ? '__complete' : (w.drill_rig || '__none');
    return statusValue(w);
  }
  let columns;
  if (groupBy === 'drill') {
    // Settings order, plus any drill still named on a card but gone from the list.
    const listed = drillsFor(type);
    const rigOrder = [...listed, ...[...new Set(typeItems.map(w => w.drill_rig).filter(Boolean))].filter(d => !listed.includes(d))];
    columns = [
      { key: '__none', label: 'Unassigned', color: '#6b7280', bg: '#f9fafb' },
      ...rigOrder.map(d => ({ key: d, label: `🛠️ ${d}`, color: '#92400e', bg: '#fffbeb' })),
      { key: '__complete', label: '✓ Complete', color: '#15803d', bg: '#f0fdf4' },
    ];
  } else if (groupBy === 'status') {
    columns = [...statusColumns];
    [...new Set(typeItems.map(statusValue))].forEach(s => {
      if (s && !statusMetaMap[s]) columns.push({ key: s, label: s, color: '#374151', bg: '#f9fafb' });
    });
  } else if (groupBy === 'pm') {
    const ids = [...new Set(typeItems.map(w => w.pm_id || '__none'))];
    columns = ids.map(id => ({ key: id, label: id === '__none' ? 'Unassigned' : pmName(id), color: '#1e3a5f', bg: '#f9fafb' }))
      .sort((a, b) => a.key === '__none' ? 1 : b.key === '__none' ? -1 : a.label.localeCompare(b.label));
  } else if (groupBy === 'client') {
    const cs = [...new Set(typeItems.map(w => w.client_name || '__none'))];
    columns = cs.map(c => ({ key: c, label: c === '__none' ? '— No Client —' : c, color: '#1e3a5f', bg: '#f9fafb' }))
      .sort((a, b) => a.key === '__none' ? 1 : b.key === '__none' ? -1 : a.label.localeCompare(b.label));
  } else {
    columns = DUE_BUCKETS;
  }

  const pmOptions = [...new Set(typeItems.map(w => w.pm_id).filter(Boolean))].sort((a, b) => pmName(a).localeCompare(pmName(b)));
  const clientOptions = [...new Set(typeItems.map(w => w.client_name).filter(Boolean))].sort();
  // Submittals roll up multiple items, so a whole package can't be dragged to
  // one status — drag is only for single-status workflows.
  // Dragging is allowed where the column actually means something you can set:
  // status, and — on a drilling board — which rig is doing it. Client and due-date
  // columns are facts about the job, not switches, so a card cannot be dropped there.
  const canDrag = (groupBy === 'status' || (groupBy === 'drill' && isDrillType(type)))
    && !isSubmittals && !isUsa && !isPayroll;
  const backTo = (!embedded && OFFICE_TYPES.includes(type)) ? '/admin/administration' : basePath;

  async function moveToStatus(w, newStatus) {
    // NVE cards are meters — patch the meter's status on its parent sub-job.
    if (isNve) {
      if (!w || w.nve_status === newStatus) return;
      if (!w.meter_id) {
        // Underground only — no meter to carry the status, so the sub-job does.
        const before = w.nve_status;
        const patchSj = st => setWorkflows(ws => ws.map(x => x.id === w.sub_job_id ? { ...x, nve_status: st } : x));
        patchSj(newStatus);
        try { await apiFetch(`/sub-jobs/${w.sub_job_id}/card`, { method: 'PATCH', body: JSON.stringify({ nve_status: newStatus }) }); }
        catch (e) { patchSj(before); }
        return;
      }
      const prev = w.nve_status;
      const patchMeter = st => setWorkflows(ws => ws.map(x => x.id === w.sub_job_id ? { ...x, meters: (x.meters || []).map(m => m.id === w.meter_id ? { ...m, status: st } : m) } : x));
      patchMeter(newStatus);
      try {
        await apiFetch(`/sub-jobs/${w.sub_job_id}/nve-meters/${w.meter_id}`, { method: 'PATCH', body: JSON.stringify({ status: newStatus }) });
      } catch (e) { patchMeter(prev); }
      return;
    }
    if (!w || w.status === newStatus) return;
    const prevStatus = w.status;
    setWorkflows(prev => prev.map(x => x.id === w.id ? { ...x, status: newStatus } : x));
    try {
      await apiFetch(`/projects/${w.project_id}/sub-jobs/${w.id}`, { method: 'PATCH', body: JSON.stringify({ status: newStatus }) });
    } catch (e) {
      setWorkflows(prev => prev.map(x => x.id === w.id ? { ...x, status: prevStatus } : x));
    }
  }

  // Dropping a card into a rig's column assigns that rig. The Complete column is not
  // a rig at all — it is the status — so a card dropped there is marked complete, and
  // a completed card dragged back out to a rig starts again, because otherwise it
  // would refuse to move and look broken.
  async function moveToDrill(w, colKey) {
    if (!w) return;
    const prev = { rig: w.drill_rig || null, status: w.status };
    const rig = colKey === '__none' || colKey === '__complete' ? null : colKey;
    const next = colKey === '__complete'
      ? { drill_rig: prev.rig, status: 'complete' }
      : { drill_rig: rig, status: w.status === 'complete' ? 'active' : w.status };
    if (next.drill_rig === prev.rig && next.status === prev.status) return;
    setWorkflows(ws => ws.map(x => x.id === w.id ? { ...x, drill_rig: next.drill_rig, status: next.status } : x));
    try {
      if (next.drill_rig !== prev.rig) {
        await apiFetch(`/sub-jobs/${w.id}/drill`, { method: 'POST', body: JSON.stringify({ drill_rig: next.drill_rig }) });
      }
      if (next.status !== prev.status) {
        await apiFetch(`/projects/${w.project_id}/sub-jobs/${w.id}`, { method: 'PATCH', body: JSON.stringify({ status: next.status }) });
      }
    } catch {
      setWorkflows(ws => ws.map(x => x.id === w.id ? { ...x, drill_rig: prev.rig, status: prev.status } : x));
    }
  }

  // Dropping ON a card means "put it here", so the card also takes on that column —
  // moving between rigs and choosing where it sits are one gesture, not two.
  // target null means the end of the column.
  async function placeCard(target, colKey, where) {
    const id = dragRef.current;
    dragRef.current = null;
    setDragId(null); setDragOverCol(null); setDragOverCard(null);
    if (!id || (target && id === target.id)) return;
    const dragged = typeItems.find(x => x.id === id);
    if (!dragged) return;
    if (groupBy === 'drill') await moveToDrill(dragged, colKey);
    else await moveToStatus(dragged, colKey);
    // Renumber the whole column, so every card in it has an explicit place rather
    // than some being ordered and the rest falling back to a different rule.
    const column = filtered.filter(x => colKeyFor(x) === colKey && x.id !== id).sort(byBoardOrder);
    let at = target ? column.findIndex(x => x.id === target.id) : -1;
    if (at < 0) at = column.length;                 // no target, or it moved: the end
    else if (where === 'after') at += 1;
    const ordered = [...column.slice(0, at), dragged, ...column.slice(at)];
    const ids = ordered.map(x => x.id);
    setWorkflows(ws => ws.map(x => { const i = ids.indexOf(x.id); return i < 0 ? x : { ...x, board_order: i }; }));
    apiFetch('/workflows/board-order', { method: 'POST', body: JSON.stringify({ order: ids }) }).catch(() => {});
  }

  function onDrop(colKey) {
    if (!canDrag || !dragRef.current) { setDragOverCol(null); return; }
    placeCard(null, colKey, 'after');   // dropped in the column, not on a card: last
  }

  const selectSt = { border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: '#fff' };

  // ── Mobile (embedded): status chips + vertical list + tap-to-set-status ──────
  if (embedded) {
    const counts = {};
    typeItems.forEach(w => { const s = statusValue(w); counts[s] = (counts[s] || 0) + 1; });
    // One definition of "which column is this in", used by the chips, the counts and
    // the section headings, so the three can never disagree.
    const drillKeyOf = w => statusValue(w) === 'complete' ? '__complete' : (w.drill_rig || '__none');
    const mobileDrillSections = [
      { key: '__none', label: 'Unassigned' },
      ...[...drillsFor(type), ...[...new Set(typeItems.map(w => w.drill_rig).filter(Boolean))].filter(d => !drillsFor(type).includes(d))].map(d => ({ key: d, label: `🛠️ ${d}` })),
      { key: '__complete', label: '✓ Complete' },
    ].map(sec => ({ ...sec, count: typeItems.filter(w => drillKeyOf(w) === sec.key).length }));
    const canChangeStatus = !isSubmittals && !isUsa && !isPayroll; // NVE + generic sub-jobs
    const chip = on => ({ whiteSpace: 'nowrap', padding: '8px 14px', borderRadius: 20, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', background: on ? '#111' : '#f3f4f6', color: on ? '#e87722' : '#374151' });
    const badge = { fontSize: 11, color: '#6b7280', background: '#f3f4f6', padding: '3px 8px', borderRadius: 10, fontWeight: 600 };
    const cardTitle = w => isNve ? (w.meter_address || (w.meter_id ? `Meter ${w.meter_seq}` : (Number(w.nve_pole_hold) ? '🪝 Pole hold' : 'Underground only · no meters'))) : (jobNumber(wfJob(w)) || '—');
    const cardSub = w => isNve ? `${jobNumber(wfJob(w))}${w.project_name ? ' · ' + jobName(wfJob(w)) : ''}` : jobName(wfJob(w));
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
        <div style={{ background: '#111', color: '#fff', paddingTop: 'max(env(safe-area-inset-top), 14px)', paddingBottom: 14, paddingLeft: 16, paddingRight: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate(backTo)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', padding: 0, color: '#e87722', lineHeight: 1 }}>‹</button>
          <div style={{ fontWeight: 800, fontSize: 17 }}>{meta.icon} {meta.label}</div>
        </div>

        <div style={{ padding: '12px 12px 0', maxWidth: 540, margin: '0 auto' }}>
          <div style={{ marginBottom: 12 }}><ActivityPills pageName={meta.label} scope={type} summaryLabel={`${meta.label} Update`} linkJobs={false}
            variant="pillows" trailing={type === 'nve' ? <NveUpcomingPill pillow /> : null} /></div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search job, number, client…"
            style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 16, marginBottom: 12 }} />
          {/* Group by chooses what the chips underneath mean, so it sits above them.
              Pick Drill and the chips become the rigs; pick Status and they go back to
              being statuses. One row of chips, filtering whatever you're looking at. */}
          {isDrillType(type) && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 10 }}>
              {!showClosed && <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>Group by</span>}
              {!showClosed && [[true, 'Drill'], [false, 'Status']].map(([val, label]) => (
                <button key={label} onClick={() => setMobileGrouping(val)}
                  style={{ padding: '6px 14px', borderRadius: 20, border: 'none', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                    background: mobileByDrill === val ? '#111' : '#f3f4f6', color: mobileByDrill === val ? '#e87722' : '#374151' }}>
                  {label}
                </button>
              ))}
              <button onClick={() => setShowClosed(v => !v)}
                style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 20, border: 'none', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                  background: showClosed ? '#111' : '#f3f4f6', color: showClosed ? '#e87722' : '#374151' }}>
                🗄️ Closed{closedTotal ? ` (${closedTotal})` : ''}
              </button>
            </div>
          )}
          <div style={{ display: showClosed ? 'none' : 'flex', gap: 8, flexWrap: 'wrap', paddingBottom: 12 }}>
            {mobileByDrill && isDrillType(type) ? (<>
              <button onClick={() => setDrillFilter('')} style={chip(drillFilter === '')}>All ({typeItems.length})</button>
              {/* Every rig shows even at zero — on a scheduling list, a rig with
                  nothing on it is the thing you most want to notice. */}
              {mobileDrillSections.map(sec => (
                <button key={sec.key} onClick={() => setDrillFilter(sec.key)} style={chip(drillFilter === sec.key)}>{sec.label} ({sec.count})</button>
              ))}
            </>) : (<>
              <button onClick={() => setStatusFilter('')} style={chip(statusFilter === '')}>All ({typeItems.length})</button>
              {statusColumns.map(s => (counts[s.key] ? (
                <button key={s.key} onClick={() => setStatusFilter(s.key)} style={chip(statusFilter === s.key)}>{s.label} ({counts[s.key]})</button>
              ) : null))}
            </>)}
          </div>
        </div>

        <div style={{ padding: '0 12px 90px', maxWidth: 540, margin: '0 auto' }}>
          {showClosed ? (
            <ClosedArchive rows={closedFiltered} total={closedTotal} loading={closedRows === null} pmName={pmName}
              onOpen={w => setDrillCard(w)} compact />
          ) : loading ? (
            <p style={{ color: '#9ca3af', textAlign: 'center', paddingTop: 30 }}>Loading…</p>
          ) : filtered.length === 0 ? (
            <p style={{ color: '#9ca3af', textAlign: 'center', paddingTop: 30 }}>Nothing here.</p>
          ) : (() => {
          const renderCard = w => {
            const sm = statusMetaMap[statusValue(w)] || { label: statusValue(w), color: '#374151', bg: '#f3f4f6' };
            // Drill cards show their OWN date; everything else shows the job's due date.
            const due = fmtDate(isDrillType(w.type) ? w.drill_date : w.sched_finish);
            return (
              <div key={w.id} role="button" tabIndex={0}
                onClick={() => isDrillType(w.type) ? setDrillCard(w) : w.type === 'nve' ? setNveCard(w) : w.type === 'loops' ? setLoopCard(w) : w.type === 'usa_digs' ? setUsaCard(w) : setSheetItem(w)}
                style={{ width: '100%', boxSizing: 'border-box', textAlign: 'left', background: '#fff', border: '1.5px solid #ececec', borderLeft: `5px solid ${meta.color}`, borderRadius: 12, padding: '14px 16px', marginBottom: 10, cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#111' }}>{cardTitle(w)}</div>
                  <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '3px 10px', background: sm.bg, color: sm.color, whiteSpace: 'nowrap' }}>{sm.label}</span>
                </div>
                {cardSub(w) && <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>{cardSub(w)}</div>}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {w.client_name && <span style={badge}>🏢 {w.client_name}</span>}
                  <span style={badge}>👤 {pmName(w.pm_id)}</span>
                  {due && !isDrillType(w.type) && <span style={badge}>📅 {due}</span>}
                  {w.drill_hours_total > 0 && <span style={badge}>⏱️ {w.drill_hours_total} hrs</span>}
                  {isDrillType(w.type) && w.hole_count > 0 && <span style={badge}>🕳️ {w.holes_done}/{w.hole_count}</span>}
                  {isDrillType(w.type) && w.photo_count > 0 && <span style={badge}>📷 {w.photo_count}</span>}
                </div>
                {/* The same comment and pillows the desktop tile and the open card
                    show — this is the surface that gets used most, so it was the
                    wrong one to leave on the old badges. */}
                {isDrillType(w.type) && w.drill_comment && (
                  <div style={{ fontSize: 12.5, color: '#374151', marginTop: 8, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>{w.drill_comment}</div>
                )}
                {w.type === 'loops' && (
                  <LoopPillows w={w} onChanged={reloadWorkflows}
                    onOpenLoops={() => setLoopCard({ ...w, _openLoops: true })}
                    extras={[
                      { key: 'loops', label: '🔁 Loops' + (w.loop_locations_count ? ' · ' + w.loop_locations_count + ' location' + (w.loop_locations_count === 1 ? '' : 's') : ''),
                        tone: '#7c3aed', toneSoft: '#faf5ff', toneEdge: '#ddd6fe', open: false, onClick: () => setLoopCard({ ...w, _openLoops: true }) },
                    ]} />
                )}
                {isDrillType(w.type) && (
                  <DrillPillows w={w} onChanged={reloadWorkflows}
                    extras={[
                      { key: 'holes', label: holesPillowLabel(w.hole_count, w.holes_done, w.hole_depth_total),
                        tone: '#1e3a5f', toneSoft: '#eff6ff', toneEdge: '#bfdbfe', open: false, onClick: () => setDrillCard({ ...w, _openHoles: true }) },
                      w.is_tm ? { key: 'tm', label: '⏱️ Time & Material Billing', open: false, onClick: () => setDrillCard({ ...w, _openTm: true }) } : null,
                    ]} />
                )}
              </div>
            );
          };
          if (!mobileByDrill) return filtered.map(renderCard);
          // Same shape as the desktop board so the two read alike: unassigned first,
          // then the rigs in the yard's own order, and anything finished at the end
          // rather than sitting in the middle of a rig's work.
          const sections = mobileDrillSections
            .filter(sec => !drillFilter || sec.key === drillFilter)
            .map(sec => ({ ...sec, rows: filtered.filter(w => drillKeyOf(w) === sec.key) }))
            .filter(sec => sec.rows.length);   // a phone has no room for empty headings
          return sections.map(sec => (
            <div key={sec.key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '14px 2px 8px' }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: sec.key === '__complete' ? '#15803d' : '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6 }}>{sec.label}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', background: '#f3f4f6', borderRadius: 999, padding: '2px 8px' }}>{sec.rows.length}</span>
                <span style={{ flex: 1, height: 1, background: '#ececec' }} />
              </div>
              {sec.rows.map(renderCard)}
            </div>
          ));
          })()}
        </div>

        {sheetItem && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 4000, display: 'flex', alignItems: 'flex-end' }} onClick={() => setSheetItem(null)}>
            <div style={{ background: '#fff', width: '100%', borderRadius: '18px 18px 0 0', padding: '20px 20px 40px', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#111' }}>{cardTitle(sheetItem)}</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>{sheetItem.project_name}{sheetItem.client_name ? ' · ' + sheetItem.client_name : ''}</div>
              {canChangeStatus ? (
                <>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#999', textTransform: 'uppercase', marginBottom: 8 }}>Set status</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {statusColumns.map(s => {
                      const cur = statusValue(sheetItem) === s.key;
                      return (
                        <button key={s.key} onClick={() => { moveToStatus(sheetItem, s.key); setSheetItem(null); }}
                          style={{ padding: '14px 16px', borderRadius: 12, border: cur ? `2px solid ${s.color}` : '1.5px solid #e5e7eb', background: cur ? s.bg : '#fff', color: cur ? s.color : '#111', fontSize: 15, fontWeight: 700, cursor: 'pointer', textAlign: 'left' }}>
                          {s.label}{cur ? '  · current' : ''}
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 13, color: '#6b7280', background: '#f9fafb', border: '1px solid #eee', borderRadius: 10, padding: 12 }}>
                  Status for {meta.label} is managed in the office app.
                </div>
              )}
              <button onClick={() => setSheetItem(null)} style={{ width: '100%', marginTop: 14, padding: '12px', borderRadius: 12, border: '1.5px solid #e5e7eb', background: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        )}
        {drillCard && (
          <DrillCardModal card={drillCard} pmName={pmName} onClose={() => setDrillCard(null)} onChanged={reloadBoth} />
        )}
        {nveCard && (
          <NveCardModal card={nveCard} pmName={pmName} onChanged={reloadWorkflows} onClose={() => { setNveCard(null); reloadWorkflows(); }} />
        )}
        {loopCard && (
          <LoopsCardModal card={loopCard} pmName={pmName} onChanged={reloadWorkflows} onClose={() => { setLoopCard(null); reloadWorkflows(); }} />
        )}
        {usaCard && (
          <UsaCardModal card={usaCard} pmName={pmName} onClose={() => { setUsaCard(null); reloadWorkflows(); }} />
        )}
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header" style={{ alignItems: 'flex-start' }}>
        <div>
          <button onClick={() => navigate(backTo)}
            style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
            ‹ {backTo === '/admin/administration' ? 'Administration' : 'All Workflows'}
          </button>
          <h1 className="admin-page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>{meta.icon}</span> {meta.label}
          </h1>
          <p className="admin-page-sub">{showClosed
          ? `${closedFiltered.length} closed card${closedFiltered.length === 1 ? '' : 's'} — off the board, still on the job`
          : <>{filtered.length} of {typeItems.length} shown{canDrag ? (groupBy === 'drill' ? ' · drag a card onto a rig to assign it, or onto Complete to finish it' : ' · drag cards to change status') : ''}</>}</p>
          {/* Loops, Vertical, then Directional Drilling (Pat, Sep 24–25 2026); Small Jobs to follow. */}
          {/* NVE: Upcoming Inspections right after the Daily Update pill (Ryan, Oct 18 2026). */}
          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}><ActivityPills pageName={meta.label} scope={type} summaryLabel={`${meta.label} Update`}
            leading={['loops', 'vertical_drilling', 'horizontal_drilling'].includes(type) ? [{ label: `${meta.label} Schedule`, icon: '📅', onClick: () => navigate(`/admin/workflows/${type}/schedule`) }] : []} />
            {type === 'nve' && <NveUpcomingPill />}</div>
        </div>
      </div>

      {/* Controls: group-by + filters */}
      <div className="admin-card" style={{ padding: 14, marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Columns:</span>
          <div style={{ display: 'flex', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
            {(isDrillType(type) ? [...GROUP_OPTIONS, { key: 'drill', label: 'Drill' }] : GROUP_OPTIONS).map(g => (
              <button key={g.key} onClick={() => setGroupBy(g.key)}
                style={{ padding: '6px 12px', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  background: groupBy === g.key ? meta.color : '#fff', color: groupBy === g.key ? '#fff' : '#374151' }}>
                {g.label}
              </button>
            ))}
          </div>
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search job, number, client…"
          style={{ ...selectSt, flex: 1, minWidth: 180 }} />
        <select value={pmFilter} onChange={e => setPmFilter(e.target.value)} style={selectSt}>
          <option value="">All PMs</option>
          {pmOptions.map(id => <option key={id} value={id}>{pmName(id)}</option>)}
        </select>
        <select value={clientFilter} onChange={e => setClientFilter(e.target.value)} style={selectSt}>
          <option value="">All Clients</option>
          {clientOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectSt}>
          <option value="">All Statuses</option>
          {statusColumns.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        {/* Finished work. Kept out of the way of the board but never further than a click. */}
        {isDrillType(type) && (
          <button onClick={() => setShowClosed(v => !v)}
            style={{ border: 'none', borderRadius: 7, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: showClosed ? '#111' : '#f3f4f6', color: showClosed ? '#e87722' : '#374151' }}>
            🗄️ Closed{closedTotal ? ` (${closedTotal})` : ''}
          </button>
        )}
        {(search || pmFilter || clientFilter || statusFilter) && (
          <button onClick={() => { setSearch(''); setPmFilter(''); setClientFilter(''); setStatusFilter(''); }}
            style={{ border: 'none', background: '#f3f4f6', color: '#6b7280', borderRadius: 7, padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Clear
          </button>
        )}
      </div>

      {/* Board */}
      {showClosed ? (
        <ClosedArchive rows={closedFiltered} total={closedTotal} loading={closedRows === null} pmName={pmName}
          onOpen={w => setDrillCard(w)} />
      ) : loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
      ) : (
        <>
        {boardNarrow && (
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 8, paddingBottom: 2, scrollbarWidth: 'none' }}>
            {columns.map((col, i) => {
              const n = filtered.filter(w => colKeyFor(w) === col.key).length; const on = i === boardColIdx;
              return (
                <button key={col.key} ref={el => { boardChipRefs.current[i] = el; }} onClick={() => boardScrollTo(i)}
                  style={{ flexShrink: 0, fontSize: 12, fontWeight: 800, padding: '6px 11px', borderRadius: 999, border: `1.5px solid ${col.color}`, background: on ? col.color : '#fff', color: on ? '#fff' : col.color, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                  {col.label} · {n}
                </button>
              );
            })}
          </div>
        )}
        <div ref={boardRef}
          onScroll={boardNarrow ? e => { const el = e.currentTarget; const i = Math.max(0, Math.min(columns.length - 1, Math.round(el.scrollLeft / (el.clientWidth + BOARD_GAP)))); if (i !== boardColIdx) setBoardColIdx(i); } : undefined}
          style={boardNarrow
            ? { display: 'flex', gap: BOARD_GAP, overflowX: 'auto', overflowY: 'hidden', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }
            : { display: 'flex', gap: BOARD_GAP, overflowX: 'auto', paddingBottom: 12 }}>
          {columns.map((col, ci) => {
            const cards = filtered.filter(w => colKeyFor(w) === col.key).sort(byBoardOrder);
            return (
              <div key={col.key}
                onDragOver={e => { if (canDrag) { e.preventDefault(); setDragOverCol(col.key); if (dragOverCard) setDragOverCard(null); } }}
                onDragLeave={() => setDragOverCol(c => c === col.key ? null : c)}
                onDrop={() => onDrop(col.key)}
                style={{ ...(boardNarrow ? { flex: '0 0 100%', width: '100%', minWidth: 0, scrollSnapAlign: 'start' } : { flex: '0 0 280px', width: 280 }),
                  height: boardH || '76vh', boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
                  background: dragOverCol === col.key ? '#eef2ff' : '#f3f4f6', borderRadius: 12, padding: 10, border: dragOverCol === col.key ? '2px dashed #6366f1' : '2px solid transparent', transition: 'background .1s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px 10px', flexShrink: 0 }}>
                  {boardNarrow && <button onClick={() => boardScrollTo(ci - 1)} disabled={ci === 0} style={{ background: 'none', border: 'none', fontSize: 20, lineHeight: 1, color: ci === 0 ? '#e5e7eb' : col.color, cursor: 'pointer', padding: 0 }}>‹</button>}
                  <span style={{ fontSize: 12, fontWeight: 700, color: col.color, textTransform: 'uppercase', letterSpacing: 0.4 }}>{col.label}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#6b7280', background: '#fff', borderRadius: 10, padding: '2px 8px' }}>{cards.length}</span>
                  {boardNarrow && <button onClick={() => boardScrollTo(ci + 1)} disabled={ci === columns.length - 1} style={{ background: 'none', border: 'none', fontSize: 20, lineHeight: 1, color: ci === columns.length - 1 ? '#e5e7eb' : col.color, cursor: 'pointer', padding: 0 }}>›</button>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minHeight: 0, overflowY: 'auto', margin: '0 -4px', padding: '0 4px 4px' }}>
                  {cards.map(w => {
                    const sm = statusMetaMap[statusValue(w)] || { label: statusValue(w), color: '#374151', bg: '#f3f4f6' };
                    // Drill cards show their OWN date; everything else shows the job's due date.
                    const cardDate = isDrillType(w.type) ? w.drill_date : w.sched_finish;
                    const due = fmtDate(cardDate);
                    const overdue = cardDate && dueBucket(cardDate) === 'overdue' && w.status !== 'complete';
                    return (
                      <div key={w.id}
                        draggable={canDrag}
                        onDragStart={() => { if (canDrag) { dragRef.current = w.id; setDragId(w.id); } }}
                        onDragEnd={() => { dragRef.current = null; setDragId(null); setDragOverCol(null); setDragOverCard(null); }}
                        onDragOver={e => {
                          if (!canDrag || !dragRef.current || dragRef.current === w.id) return;
                          e.preventDefault(); e.stopPropagation();
                          // Top half puts it above, bottom half below — so the bottom of
                          // a column is reachable and the drop lands where you aimed.
                          const r = e.currentTarget.getBoundingClientRect();
                          const where = e.clientY > r.top + r.height / 2 ? 'after' : 'before';
                          if (!dragOverCard || dragOverCard.id !== w.id || dragOverCard.where !== where) {
                            setDragOverCard({ id: w.id, where });
                          }
                        }}
                        onDrop={e => { if (!canDrag) return; e.preventDefault(); e.stopPropagation(); placeCard(w, col.key, dragOverCard && dragOverCard.where); }}
                        onClick={() => isDrillType(w.type) ? setDrillCard(w) : w.type === 'nve' ? setNveCard(w) : w.type === 'loops' ? setLoopCard(w) : w.type === 'usa_digs' ? setUsaCard(w) : navigate(officePillowPath(w, embedded))}
                        style={{ background: '#fff', borderRadius: 9,
                          border: '1px solid #e5e7eb',
                          // The line showing where it will land is drawn INSIDE the card.
                          // As a border it grew from 1px to 3px and shunted the card down
                          // by two, which moved the target out from under the pointer and
                          // is most of why this felt like it was fighting back.
                          boxShadow: overCardId === w.id
                            ? (dragOverCard.where === 'after' ? 'inset 0 -3px 0 #e87722' : 'inset 0 3px 0 #e87722')
                            : '0 1px 2px rgba(0,0,0,0.04)',
                          padding: '10px 12px', cursor: canDrag ? 'grab' : 'pointer', opacity: dragId === w.id ? 0.4 : 1, flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 2 }}>
                          {isNve ? (w.meter_address || (w.meter_id ? `Meter ${w.meter_seq}` : (Number(w.nve_pole_hold) ? '🪝 Pole hold' : 'Underground only · no meters'))) : (jobNumber(wfJob(w)) || '—')}
                        </div>
                        <div style={{ fontSize: 12, color: '#374151', marginBottom: 6, lineHeight: 1.3 }}>
                          {isNve ? `${jobNumber(wfJob(w))}${w.project_name ? ' · ' + jobName(wfJob(w)) : ''}` : jobName(wfJob(w))}
                        </div>
                        {w.client_name && <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>🏢 {w.client_name}</div>}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          {groupBy !== 'status' && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: sm.color, background: sm.bg, padding: '2px 7px', borderRadius: 10 }}>{sm.label || w.status}</span>
                          )}
                          {groupBy !== 'pm' && (
                            <span style={{ fontSize: 10, color: '#6b7280', background: '#f3f4f6', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>👤 {pmName(w.pm_id)}</span>
                          )}
                          {due && !isDrillType(w.type) && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: overdue ? '#b91c1c' : '#6b7280', background: overdue ? '#fef2f2' : '#f3f4f6', padding: '2px 7px', borderRadius: 10 }}>
                              📅 {due}
                            </span>
                          )}
                          {(w.type === 'vertical_drilling' || w.type === 'horizontal_drilling' || w.type === 'shop') && w.drill_hours_total > 0 && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#4a1d50', background: '#faf5ff', padding: '2px 7px', borderRadius: 10 }}>
                              ⏱️ {w.drill_hours_total} hrs
                            </span>
                          )}
                          {isDrillType(w.type) && w.hole_count > 0 && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#1e3a5f', background: '#eff6ff', padding: '2px 7px', borderRadius: 10 }}>
                              🕳️ {w.holes_done}/{w.hole_count}
                            </span>
                          )}
                          {isDrillType(w.type) && w.photo_count > 0 && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#3730a3', background: '#eef2ff', padding: '2px 7px', borderRadius: 10 }}>
                              📷 {w.photo_count}
                            </span>
                          )}
                        </div>
                        {/* Comment, then the four pillows — the drilling card's own view. */}
                        {isDrillType(w.type) && w.drill_comment && (
                          <div style={{ fontSize: 12, color: '#374151', marginTop: 8, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>{w.drill_comment}</div>
                        )}
                        {/* Same size and the same five as the opened card — a tile that
                            reads differently from the card behind it is two things to
                            learn instead of one. */}
                        {w.type === 'loops' && (
                          <LoopPillows w={w} onChanged={reloadWorkflows} compact
                            onOpenLoops={() => setLoopCard({ ...w, _openLoops: true })}
                            extras={[
                              { key: 'loops', label: '🔁 Loops' + (w.loop_locations_count ? ' · ' + w.loop_locations_count + ' location' + (w.loop_locations_count === 1 ? '' : 's') : ''),
                                tone: '#7c3aed', toneSoft: '#faf5ff', toneEdge: '#ddd6fe', open: false, onClick: () => setLoopCard({ ...w, _openLoops: true }) },
                            ]} />
                        )}
                        {/* NVE: the opened card's four pillows on the board card too (Ryan, Oct 3
                            2026). A board card is one meter, so Request Inspection is that meter's;
                            a card with no meter is underground only and goes by the PID. Drawings and
                            Request Inspection open the card at that window. */}
                        {isNve && (
                          <NvePillows w={{ ...w, nve_file_count: (() => { try { const v = JSON.parse(w.nve_fw_sheets || '[]'); return Array.isArray(v) ? v.length : 0; } catch { return 0; } })() }}
                            onChanged={reloadWorkflows} compact underground={!w.meter_id}
                            onOpenDrawings={() => setNveCard({ ...w, _openDrawings: true })}
                            onOpenPermit={() => setNveCard({ ...w, _openPermit: true })} />
                        )}
                        {isDrillType(w.type) && (
                          <DrillPillows w={w} onChanged={reloadWorkflows}
                            extras={[
                              { key: 'holes', label: holesPillowLabel(w.hole_count, w.holes_done, w.hole_depth_total),
                                tone: '#1e3a5f', toneSoft: '#eff6ff', toneEdge: '#bfdbfe', open: false, onClick: () => setDrillCard({ ...w, _openHoles: true }) },
                              w.is_tm ? { key: 'tm', label: '⏱️ Time & Material Billing', open: false, onClick: () => setDrillCard({ ...w, _openTm: true }) } : null,
                            ]} />
                        )}
                        {/* Finished: bill it on its own or with the job it belongs to (Ryan, Oct 28 2026). */}
                        {CLOSE_CHOICE_TYPES.includes(w.type) && statusValue(w) === 'complete' && !isStandaloneWo(w) && canChooseBilling() && (
                          <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                            <button onClick={async () => { try { if (await closeWorkCard(w, w.id, 'billing')) reloadBoth(); } catch (e) { window.alert(e?.error || 'Could not close that card.'); } }}
                              title="Bill this work on its own — it comes off the board"
                              style={{ flex: 1, padding: '6px 8px', borderRadius: 7, border: '1px solid #15803d', background: '#f0fdf4', color: '#15803d', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              🧾 Send to billing
                            </button>
                            <button onClick={async () => { try { if (await closeWorkCard(w, w.id, 'job')) reloadBoth(); } catch (e) { window.alert(e?.error || 'Could not close that card.'); } }}
                              title={`Billed with ${jobWord(w)} — it comes off the board, nothing goes to billing`}
                              style={{ flex: 1, padding: '6px 8px', borderRadius: 7, border: '1px solid #1d4ed8', background: '#eff6ff', color: '#1d4ed8', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              📁 Close to {jobWord(w)}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {cards.length === 0 && (
                    <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: '16px 0', fontStyle: 'italic' }}>—</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}
      {drillCard && (
        <DrillCardModal card={drillCard} pmName={pmName} onClose={() => setDrillCard(null)} onChanged={reloadBoth} />
      )}
      {nveCard && (
        <NveCardModal card={nveCard} pmName={pmName} onChanged={reloadWorkflows} onClose={() => { setNveCard(null); reloadWorkflows(); }} />
      )}
      {loopCard && (
        <LoopsCardModal card={loopCard} pmName={pmName} onChanged={reloadWorkflows} onClose={() => { setLoopCard(null); reloadWorkflows(); }} />
      )}
      {usaCard && (
        <UsaCardModal card={usaCard} pmName={pmName} onClose={() => { setUsaCard(null); reloadWorkflows(); }} />
      )}
    </AdminLayout>
  );
}

// ── Trello-style drilling card ───────────────────────────────────────────────
// Opens in place on the drilling board: pick the machine (also moves it on the
// map), add/track holes with depth/diameter/status/photos, jot notes — all tied
// to the job's workflow card, so the crew never leaves the board.
const HOLE_STATUS_META = {
  pending:  { label: 'Pending',  color: '#6b7280', bg: '#f3f4f6', next: 'drilling' },
  drilling: { label: 'Drilling', color: '#1d4ed8', bg: '#eff6ff', next: 'done' },
  done:     { label: 'Done',     color: '#15803d', bg: '#f0fdf4', next: 'pending' },
};

// NVE card opened from the board — shows the job's full NVE panel (every meter,
// its status, permits, and photos) in a modal, reusing the job page's NVETab.
// The NVE card is a METER, not the job: one service, one permit, one status. The
// board has always listed them that way — address first, job under it — and opening
// one used to throw that away and show the whole job's meter table instead.
export function NveCardModal({ card, pmName, onClose, onChanged, inline = false }) {
  const sjId = card.sub_job_id || card.id;
  // The board opens a METER card (meter_id set). The job page opens the sub-job's card
  // with no meter on it — so the permit fields sat greyed out on a job that had a meter
  // (2602). Now the card finds the sub-job's meter itself, and if there truly is none
  // yet, the first permit entry creates one.
  const [meterId, setMeterId] = useState(card.meter_id || null);
  const [meter, setMeter] = useState(null);
  const [pid, setPid] = useState(card.nve_pid || '');
  // The Fieldwire sheets picked for this card (Ryan, Oct 2 2026) — ids, kept on the sub-job.
  const [fwSel, setFwSel] = useState(() => { try { const v = JSON.parse(card.nve_fw_sheets || '[]'); return Array.isArray(v) ? v : []; } catch { return []; } });
  const [files, setFiles] = useState([]);
  const [drawingsOpen, setDrawingsOpen] = useState(!!card._openDrawings);
  const [drawEdit, setDrawEdit] = useState(false);
  const [allMeters, setAllMeters] = useState([]);   // every meter on the job — one Request Inspection pillow each
  // Underground only is not a box to tick (Ryan, Oct 3 2026): a job with NO meters is
  // underground only — the meters pillow says so, and everything goes by the PID.
  const [metersLoaded, setMetersLoaded] = useState(false);
  // …or it was started as Underground only (Ryan, Oct 14 2026: New Service / Underground only / Pole Hold).
  const ug = (metersLoaded && allMeters.length === 0) || (!!Number(card.nve_underground) && !allMeters.length);
  // A pole hold (Ryan, Oct 14 2026): NV Energy holds a pole while someone digs — picked when the
  // workflow is started (New Service / Underground only / Pole Hold). No switch on the card
  // (Ryan: "can't think of a reason we'd need that box") — remove the workflow and add it again.
  // The request opens on Pole Hold with the card's address.
  const poleHold = !!Number(card.nve_pole_hold);
  // the Plans window: view the sheets, or edit (pick sheets, PID, uploads)
  const [permitOpen, setPermitOpen] = useState(!!card._openPermit);
  const [metersOpen, setMetersOpen] = useState(false);
  // A pole hold's own pillow holds its pictures (Ryan, Oct 14 2026) — the card's photos,
  // the same ones the drilling card keeps; they show on the job's Photos too.
  const [holePhotos, setHolePhotos] = useState([]);
  useEffect(() => { apiFetch(`/sub-jobs/${sjId}/photos`).then(d => setHolePhotos(Array.isArray(d) ? d : [])).catch(() => {}); }, [sjId]);
  async function uploadHolePhotos(files) {
    if (!files || !files.length) return;
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('photos', f));
    try {
      const res = await fetch(`${API_BASE}/sub-jobs/${sjId}/photos`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      if (res.ok) setHolePhotos(await res.json());
    } catch { /* ignore */ }
  }
  function removeHolePhoto(photoId) {
    apiFetch(`/sub-jobs/${sjId}/photos/${photoId}`, { method: 'DELETE' }).then(() => setHolePhotos(ps => ps.filter(p => p.id !== photoId))).catch(() => {});
  }
  const [viewing, setViewing] = useState(null);   // { url, title } — a drawing or the permit, in a window over the card (Ryan, Oct 2 2026)
  const [uploading, setUploading] = useState('');
  const [updates, setUpdates] = useState([]);
  const [updatesAll, setUpdatesAll] = useState(false);
  const [activity, setActivity] = useState([]);
  const [activityAll, setActivityAll] = useState(false);

  const loadMeter = React.useCallback(() => {
    apiFetch('/sub-jobs/' + sjId + '/nve-meters')
      .then(ms => {
        const list = Array.isArray(ms) ? ms : [];
        setAllMeters(list); setMetersLoaded(true);
        // The card's meter — or, when the id it came in with isn't on the job any more (a
        // meter deleted and re-added, a stale row), the job's first meter rather than none:
        // otherwise the header and the Request Inspection pillow fell back to the bare PID
        // on a job that plainly has an address (2616, Oct 3 2026).
        const mine = (meterId && list.find(m => m.id === meterId)) || list[0];
        if (mine && mine.id !== meterId) setMeterId(mine.id);
        setMeter(mine || null);
      })
      .catch(() => {});
  }, [sjId, meterId]);
  // The meter the permit lives on — the card's, the sub-job's first, or a new one.
  async function ensureMeter() {
    if (meterId) return meterId;
    const m = await apiFetch('/sub-jobs/' + sjId + '/nve-meters', { method: 'POST', body: JSON.stringify({}) });
    setMeterId(m.id); setMeter(m);
    onChanged && onChanged();
    return m.id;
  }
  const loadFiles = React.useCallback(() => {
    apiFetch('/sub-jobs/' + sjId + '/files').then(f => setFiles(Array.isArray(f) ? f : [])).catch(() => {});
  }, [sjId]);
  const loadActivity = React.useCallback(() => {
    if (!card.project_id) return;
    apiFetch('/activity?scope=job&scopeId=' + card.project_id + '&subJobId=' + encodeURIComponent(sjId) + '&limit=60')
      .then(a => setActivity(Array.isArray(a) ? a : [])).catch(() => {});
  }, [card.project_id]);

  useEffect(() => {
    loadMeter(); loadFiles(); loadActivity();
    // Only this card's own posts — a job-level update never comes down to a card (Ryan, Oct 1 2026).
    apiFetch('/projects/' + card.project_id + '/daily-updates?sub_job_id=' + encodeURIComponent(sjId)).then(u => setUpdates(Array.isArray(u) ? u : [])).catch(() => {});
  }, [loadMeter, loadFiles, loadActivity, card.project_id]);

  function changed() { loadMeter(); loadActivity(); onChanged && onChanged(); }
  async function patchMeter(body) {
    try {
      const id = await ensureMeter();
      const m = await apiFetch('/sub-jobs/' + sjId + '/nve-meters/' + id, { method: 'PATCH', body: JSON.stringify(body) });
      setMeter(m); onChanged && onChanged(); loadActivity();
    } catch { window.alert('Could not save that — check your connection and try again.'); }
  }
  function savePid(v) {
    apiFetch('/sub-jobs/' + sjId + '/card', { method: 'PATCH', body: JSON.stringify({ nve_pid: v || null }) })
      .then(() => onChanged && onChanged())
      .catch(() => window.alert('Could not save the PID.'));
  }
  async function uploadDrawing(fileList) {
    const f = fileList && fileList[0];
    if (!f || uploading) return;
    setUploading('drawing');
    const fd = new FormData(); fd.append('file', f); fd.append('label', 'NVE drawing');
    try {
      const r = await fetch(API_BASE + '/sub-jobs/' + sjId + '/files', { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      if (!r.ok) throw new Error('upload failed');
      loadFiles(); onChanged && onChanged();
    } catch { window.alert('Could not upload that drawing.'); }
    setUploading('');
  }
  async function uploadPermit(fileList) {
    const f = fileList && fileList[0];
    if (!f || uploading) return;
    setUploading('permit');
    const fd = new FormData(); fd.append('file', f);
    try {
      const id = await ensureMeter();
      const r = await fetch(API_BASE + '/sub-jobs/' + sjId + '/nve-meters/' + id + '/permit-pdf', { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      if (!r.ok) throw new Error('upload failed');
      setMeter(await r.json()); changed();
    } catch { window.alert('Could not upload that permit.'); }
    setUploading('');
  }
  function removeDrawing(id) {
    apiFetch('/sub-jobs/' + sjId + '/files/' + id, { method: 'DELETE' })
      .then(() => { setFiles(fs => fs.filter(x => x.id !== id)); onChanged && onChanged(); })
      .catch(() => window.alert('Could not remove that drawing.'));
  }

  const row = { ...card, meter, nve_pid: pid, nve_file_count: files.length + fwSel.length, nve_status: meter ? meter.status : card.nve_status };
  const ps = PERMIT_STYLE[permitState(meter)];
  const addr = (meter && meter.address) || card.meter_address;

  const viewer = viewing ? <PdfWindow url={viewing.url} title={viewing.title} fileName={viewing.title} onClose={() => setViewing(null)} /> : null;
  return (
    <><div style={inline ? { display: 'block' } : { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      paddingTop: 'max(env(safe-area-inset-top), 12px)', paddingBottom: 'max(env(safe-area-inset-bottom), 12px)',
      paddingLeft: 12, paddingRight: 12 }} onClick={inline ? undefined : onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#f9fafb', borderRadius: 14, width: inline ? '100%' : 560, maxWidth: '100%', maxHeight: inline ? 'none' : 'min(92vh, calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 24px))', display: 'flex', flexDirection: 'column', boxShadow: inline ? 'none' : '0 16px 60px rgba(0,0,0,0.28)', border: inline ? '1px solid #e5e7eb' : 'none' }}>
        {/* The address is what this card IS. The job is what it belongs to. */}
        {/* Header — shown inline too. A single card on a job's Work Flows board is
            meant to BE the workflow board's card, header and all; hiding it here made
            the two look like different things. */}
        <div style={{ background: '#d97706', borderRadius: '14px 14px 0 0', padding: '14px 18px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            {/* The job first and large, the PID under it (Ryan, Oct 2 2026) — NV Energy and
                the crew both talk in PIDs. The address, when there is one, comes third. */}
            <div style={{ fontSize: 20, fontWeight: 900, lineHeight: 1.2 }}>⚡ <JobNumberLink projectId={card.project_id}>{jobNumber(wfJob(card)) || '—'}</JobNumberLink>{jobName(wfJob(card)) ? <span style={{ fontWeight: 700, opacity: 0.95 }}> · {jobName(wfJob(card))}</span> : null}</div>
            <div style={{ fontSize: 15, fontWeight: 800, marginTop: 4, letterSpacing: 0.2 }}>{poleHold && <span style={{ marginRight: 8, fontSize: 11, fontWeight: 800, background: '#fff', color: '#b45309', borderRadius: 999, padding: '2px 9px' }}>🪝 POLE HOLD</span>}{pid ? 'PID ' + pid : 'No PID yet'}{ug && !poleHold ? <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 800, background: 'rgba(255,255,255,0.22)', borderRadius: 999, padding: '2px 8px', verticalAlign: 'middle' }}>UNDERGROUND ONLY</span> : null}</div>
            <div style={{ fontSize: 12, opacity: 0.88, marginTop: 3 }}>{(!ug || poleHold) && addr ? '📍 ' + addr + ' · ' : ''}{card.client_name ? '🏢 ' + card.client_name + ' · ' : ''}👤 {pmName(card.pm_id)}</div>
          </div>
          {/* Forward this card to someone in the app. It has its own address, so they
              land on the card rather than the board it sits on. */}
          <SendPointer tone="dark" push
            path={`/admin/workflows/${'nve'}?card=${sjId}`}
            label={`${jobNumber(wfJob(card)) || card.project_number || ''} ${typeMeta('nve').label}`.trim()} />
          {/* Inline the card is the container — nothing to close. */}
          {!inline && (
          <button onClick={onClose} aria-label="Close"
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1, width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
          )}
        </div>

        <div style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {ug && !poleHold && (
            <div style={{ fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 12px' }}>
              <b>Underground only — no meters on this job.</b> Inspections and drawings go by the PID. If there is a meter after all, add it under the pillow below.
            </div>
          )}
          {poleHold && (
            <div style={{ fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 12px' }}>
              <b>🪝 Pole hold.</b> The yellow pillow asks NV Energy for the pole hold, at the address on this card. Pictures go under the Pole hold pillow below.
            </div>
          )}

          <NvePillows w={row} onChanged={changed}
            onOpenDrawings={() => setDrawingsOpen(v => !v)}
            meters={allMeters} underground={ug}
            onOpenPermit={mid => { const m = mid ? allMeters.find(x => x.id === mid) : null; if (m) { setMeterId(m.id); setMeter(m); } setPermitOpen(v => (mid && mid !== meterId ? true : !v)); }}
            drawingsPanel={<>
          {/* Front and centre (Ryan, Oct 2 2026): one sheet at a time, ‹ › through the picked
              ones; ✎ Edit shows the pick, the PID and the uploaded drawings. */}
          {drawingsOpen && (
            <NveWindow title="📐 Plans" sub={pid ? `PID ${pid}` : 'No PID yet'} onClose={() => { setDrawingsOpen(false); setDrawEdit(false); }} edit={drawEdit || fwSel.length === 0} onEdit={fwSel.length ? () => setDrawEdit(e => !e) : null}>
            {!(drawEdit || fwSel.length === 0) ? <NveSheetPager projectId={card.project_id} selected={fwSel} pid={pid} isAdminZone /> : (
            <div style={{ border: '1.5px solid #c7d2fe', background: '#eef2ff', borderRadius: 10, padding: 14, margin: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#4338ca' }}>📐 NVE Drawings</div>
                {/* Drag a drawing (PDF/DWG/image) onto here, or click to browse. */}
                <AttachButton accept="any" icon="📐" variant="plain" disabled={!!uploading}
                  label={uploading === 'drawing' ? 'Uploading…' : 'Add drawing'}
                  style={{ padding: '6px 12px', fontSize: 12.5, borderRadius: 8, fontWeight: 700, color: '#4338ca' }}
                  onFiles={fs => uploadDrawing(fs)} />
              </div>
              <NveFieldwireSheets projectId={card.project_id} sjId={sjId} pid={pid} selected={fwSel} onSelectedChange={setFwSel} isAdminZone />
              <div style={{ marginBottom: 10 }}>
                <div style={dcLbl}>PID <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>· NV Energy's number for this job</span></div>
                <input defaultValue={pid} placeholder="e.g. 1234567"
                  onChange={e => setPid(e.target.value)}
                  onBlur={e => savePid(e.target.value.trim())}
                  style={{ ...dcInp, maxWidth: 220 }} />
              </div>
              {files.length === 0 ? (
                <div style={{ fontSize: 12.5, color: '#6b7280' }}>No drawings on this job yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {files.map(f => (
                    <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 11px' }}>
                      <span style={{ fontSize: 16 }}>{/^image\//.test(f.mime_type || '') ? '🖼️' : '📄'}</span>
                      <button onClick={() => setViewing({ url: photoUrl(f.filename), title: f.original_name || 'Drawing' })} title="Open it here"
                        style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: '#4338ca', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer' }}>
                        {f.original_name || 'Drawing'}
                      </button>
                      <button onClick={() => removeDrawing(f.id)} title="Remove"
                        style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>Delete</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
            </NveWindow>
          )}
            </>}
            permitPanel={<>
          {/* The yellow pillow is the inspection request now (Ryan, Oct 2 2026); the permit
              details it used to open stay right under it. */}
          {permitOpen && (
            <NveWindow title="📧 Request an NV Energy inspection" sub={pid ? `PID ${pid}` : 'No PID yet'} onClose={() => setPermitOpen(false)} width={820}>
            <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <NveInspectionRequest key={poleHold ? 'hold' : 'insp'} sjId={sjId} meterId={meter && meter.id} pid={pid} address={(meter && meter.address) || card.meter_address || null} defaultType={poleHold ? 'pole_hold' : ''} onSent={() => loadActivity()} />
            <div style={{ border: '1.5px solid ' + ps.edge, background: ps.bg === '#fff' ? '#fafafa' : ps.bg, borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: ps.tx, marginBottom: 10 }}>📄 Permit</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 150px' }}>
                  <div style={dcLbl}>City / jurisdiction</div>
                  <input key={'city-' + (meter && meter.id)} defaultValue={(meter && meter.permit_city) || ''} placeholder="City of Reno"
                    onBlur={e => { if (e.target.value.trim() !== ((meter && meter.permit_city) || '')) patchMeter({ permit_city: e.target.value.trim() || null }); }}
                    style={dcInp} />
                </div>
                <div style={{ flex: '1 1 130px' }}>
                  <div style={dcLbl}>Permit number</div>
                  <input key={'num-' + (meter && meter.id)} defaultValue={(meter && meter.permit_number) || ''} placeholder="—"
                    onBlur={e => { if (e.target.value.trim() !== ((meter && meter.permit_number) || '')) patchMeter({ permit_number: e.target.value.trim() || null }); }}
                    style={dcInp} />
                </div>
                <div style={{ flex: '1 1 130px' }}>
                  <div style={dcLbl}>Date pulled</div>
                  <input key={'date-' + (meter && meter.id)} type="date" defaultValue={(meter && meter.permit_date ? String(meter.permit_date).slice(0, 10) : '')}
                    onChange={e => patchMeter({ permit_date: e.target.value || null })}
                    style={dcInp} />
                </div>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13.5, fontWeight: 700, color: '#15803d', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!(meter && meter.city_ok)}
                  onChange={e => patchMeter({ city_ok: e.target.checked ? 1 : 0, city_ok_date: e.target.checked ? new Date().toISOString().slice(0, 10) : null })}
                  style={{ width: 18, height: 18 }} />
                City has signed off
                {meter && meter.city_ok && meter.city_ok_date && <span style={{ fontWeight: 500, color: '#6b7280' }}>· {fmtDate(meter.city_ok_date)}</span>}
              </label>

              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {meter && meter.permit_pdf ? (
                  <button onClick={() => setViewing({ url: photoUrl(meter.permit_pdf), title: meter.permit_pdf_name || 'Permit' })} title="Open it here"
                    style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>📄 {meter.permit_pdf_name || 'Open the permit'}</button>
                ) : <span style={{ fontSize: 12.5, color: '#9ca3af' }}>No permit document attached.</span>}
                {/* Drag the permit PDF (or a photo of it) straight onto here, or click to browse. */}
                <AttachButton accept="both" icon="📄" disabled={!!uploading}
                  variant={meter && meter.permit_pdf ? 'plain' : 'dashed'}
                  label={uploading === 'permit' ? 'Uploading…' : (meter && meter.permit_pdf ? 'Replace' : 'Drag & drop the permit here, or click to attach')}
                  style={meter && meter.permit_pdf ? { padding: '7px 12px', fontSize: 12.5 } : { flex: '1 1 100%', padding: '15px 12px', fontSize: 13, borderRadius: 8 }}
                  onFiles={fs => uploadPermit(fs)} />
              </div>
            </div>
            </div>
            </NveWindow>
          )}
            </>}
            extras={[
              // A pole hold has no meters — its pillow is the pole hold itself, with its pictures.
              poleHold ? { key: 'meters', label: '🪝 Pole hold' + (holePhotos.length ? ` · ${holePhotos.length} photo${holePhotos.length === 1 ? '' : 's'}` : ''), tone: '#d97706', toneSoft: '#fffbeb', toneEdge: '#fde68a', panel: (<>
          {metersOpen && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#92400e' }}>📷 Pole hold pictures <span style={{ color: '#9ca3af', fontWeight: 600 }}>· also on the job's Photos</span></div>
                <AttachButton multiple variant="plain" icon="📷" label="Add pictures" style={{ padding: '6px 12px', fontSize: 12 }} onFiles={fs => uploadHolePhotos(fs)} />
              </div>
              {holePhotos.length === 0 ? (
                <div style={{ fontSize: 13, color: '#9ca3af' }}>No pictures yet.</div>
              ) : (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {holePhotos.map(p => (
                    <AttachThumb key={p.id} size={80} src={photoUrl(p.filename)} name={p.original_name || p.filename} href={photoUrl(p.filename)} onRemove={() => removeHolePhoto(p.id)} />
                  ))}
                </div>
              )}
            </div>
          )}
              </>), open: metersOpen, onClick: () => setMetersOpen(v => !v) } :
              { key: 'meters', label: ug ? '⬇️ Underground only — no meters' : '⚡ All meters on this job', tone: '#d97706', toneSoft: '#fffbeb', toneEdge: '#fde68a', panel: (<>
          {metersOpen && (
            <div>
              <NVETab sjId={sjId} isAdminZone API_BASE={API_BASE} getToken={getToken}
                showToast={(msg, type) => { if (type === 'error' && msg) window.alert(msg); }} />
            </div>
          )}
              </>), open: metersOpen, onClick: () => setMetersOpen(v => !v) },
            ]} />

          {/* Permit — the number, who issued it, the document, and the sign-off that
              turns the pillow green. */}

          {/* Drawings — one set for the job, filed under its PID. */}

          {/* Every meter on the job — the panel this card used to open straight into. */}

          {/* Fieldwire pins whose category is mapped to this card — minus the ones a meter above
              already carries (Ryan, Oct 3 2026); quiet when there are none. */}
          <FieldwireTasks projectId={card.project_id} subJobId={sjId} canEdit excludeIds={allMeters.map(m => m.fw_task_id).filter(Boolean)} />

          <div>
            <div style={{ ...dcLbl, marginBottom: 6 }}>Daily Updates{updates.length ? ' (' + updates.length + ')' : ''}</div>
            {updates.length === 0 ? (
              <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Nothing posted on this job yet — use the Daily Update pillow above.</div>
            ) : (<>
              {(updatesAll ? updates : updates.slice(0, 5)).map(u => (
                <div key={u.id} style={{ borderLeft: '3px solid #e5e7eb', paddingLeft: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 11.5, color: '#6b7280', fontWeight: 700 }}>
                    {u.author_name || 'Someone'}
                    <span style={{ fontWeight: 500 }}> · {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>{u.from_job && <span title="Posted on the job — it names this card's work" style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 800, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, padding: '0 6px' }}>from the job</span>}
                  </div>
                  <div style={{ fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap', lineHeight: 1.45, marginTop: 2 }}>{u.notes}</div>
                  <Reactions type="update" id={u.id} reactions={u.reactions} />
                </div>
              ))}
              {updates.length > 5 && (
                <button onClick={() => setUpdatesAll(v => !v)}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                  {updatesAll ? 'Show fewer' : 'Show all ' + updates.length}
                </button>
              )}
            </>)}
          </div>

          <div>
            <div style={{ ...dcLbl, marginBottom: 6 }}>Activity{activity.length ? ' (' + activity.length + ')' : ''}</div>
            {activity.length === 0 ? (
              <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Nothing logged on this job yet.</div>
            ) : (<>
              {(activityAll ? activity : activity.slice(0, 6)).map(a => (
                <div key={a.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 7 }}>
                  <div style={{ flex: 1, fontSize: 12.5, color: '#374151', lineHeight: 1.4 }}>
                    <span style={{ fontWeight: 800, color: '#111827' }}>{a.actor_name || 'Someone'}</span> {a.action}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              ))}
              {activity.length > 6 && (
                <button onClick={() => setActivityAll(v => !v)}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                  {activityAll ? 'Show fewer' : 'Show all ' + activity.length}
                </button>
              )}
            </>)}
          </div>
        </div>

        <div style={{ borderTop: '1px solid #e5e7eb', background: '#fff', padding: '10px 14px', paddingBottom: 'max(env(safe-area-inset-bottom), 10px)', borderRadius: '0 0 14px 14px' }}>
          {/* Inline the card is the container — nothing to close. */}
          {!inline && (
          <button onClick={onClose}
            style={{ width: '100%', minHeight: 48, borderRadius: 10, border: '1.5px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
            Close
          </button>
          )}
        </div>
      </div>
    </div>{viewer}</>
  );
}

// Loops card opened from the board — shows the job's full Loops panel (every loop
// location, gate sizes, traffic directions, photos) in a modal, reusing LoopsTab.
// The small-jobs card, built to the same drilling card. A work order is the job
// itself rather than one of its cards, so its scheduled day, its comment and its
// plans hang off the project — but everything a crew does with it is in the same
// places as on every other board.
export function WorkOrderCardModal({ card, pmName, onClose, onChanged }) {
  const projectId = card.id;
  const [row, setRow] = useState(card);
  // One Scope box (Pat, Sep 28 2026): the old read-only bid scope + a separate
  // Comment box had people writing daily updates into the comment. The box starts
  // from the work order's own text, else the bid's scope, and saves on the work order.
  const [comment, setComment] = useState(card.wo_comment || card.scope || '');
  const savedComment = useRef(card.wo_comment || '');
  const [plansOpen, setPlansOpen] = useState(!!card._openPlans);
  const [tmOpen, setTmOpen] = useState(!!card._openTm);
  const [plans, setPlans] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [fromDocs, setFromDocs] = useState(false);   // the Documents-tile picker (Ryan, Oct 1 2026)
  const [bid, setBid] = useState(null);
  // Every work order gets the T&M section (Pat, Sep 23 2026: 'it looks like you removed the
  // ability to add T&M items and days'). It used to show only on a T&M bid, a bid-less work
  // order or one with rate lines already, so a Fixed Price call-out had nowhere to log a day
  // or add an item. Collapsed until opened, so a card that never uses it loses nothing.
  const [tmShow, setTmShow] = useState(true);
  // Photos from the field (Ryan, Sep 29 2026) — the phone files them as job notes,
  // which this card never showed.
  const [photosOpen, setPhotosOpen] = useState(false);
  const { photos: jobPhotos, reload: reloadPhotos } = useJobPhotos(projectId);
  const [updates, setUpdates] = useState([]);
  const [updatesAll, setUpdatesAll] = useState(false);
  const [activity, setActivity] = useState([]);
  const [activityAll, setActivityAll] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [jobLoc, setJobLoc] = useState(undefined);
  const { worker } = useAuth() || {};
  const canDeletePlan = ['admin', 'Admin', 'ProjectManager', 'OfficeStaff', 'Accounting', 'Executive'].includes(worker?.role);
  // T&M billing by the day: office sees the money, the field (supervisor) just logs.
  const tmOffice = canDeletePlan;
  const [permitsOpen, setPermitsOpen] = useState(false);
  const tmCanEdit = tmOffice || worker?.role === 'FieldSupervisor';

  const reloadRow = React.useCallback(() => {
    apiFetch('/work-orders')
      .then(all => { const r = (Array.isArray(all) ? all : []).find(x => x.id === projectId); if (r) setRow(r); })
      .catch(() => {});
  }, [projectId]);
  const loadPlans = React.useCallback(() => {
    apiFetch('/projects/' + projectId + '/plans').then(p => setPlans(Array.isArray(p) ? p : [])).catch(() => {});
  }, [projectId]);
  const loadActivity = React.useCallback(() => {
    apiFetch('/activity?scope=job&scopeId=' + projectId + '&limit=60')
      .then(a => setActivity(Array.isArray(a) ? a : [])).catch(() => {});
  }, [projectId]);

  useEffect(() => {
    loadPlans(); loadActivity(); reloadRow();
    apiFetch('/projects/' + projectId + '/daily-updates').then(u => setUpdates(Array.isArray(u) ? u : [])).catch(() => {});
    // Standard T&M shows on the card whenever the work order bills that way: a T&M bid,
    // a work order made WITHOUT a bid (most pole holds and call-outs — there was nowhere
    // for the field to log those), or a job that already has common items on its T&M
    // Rates. A hard-bid work order with nothing tracked stays as it was: no T&M section.
    apiFetch('/projects/' + projectId + '/bid')
      .then(b => { if (b && b.pricing_type === 'tm') { setBid(b); setTmShow(true); } })
      .catch(e => { if (e && e.error === 'No bid linked') setTmShow(true); });
    apiFetch('/projects/' + projectId + '/tm-items').then(r => { if (Array.isArray(r) && r.length) setTmShow(true); }).catch(() => {});
  }, [projectId, loadPlans, loadActivity, reloadRow]);

  function changed() { reloadRow(); loadActivity(); onChanged && onChanged(); }
  function saveComment(text) {
    if (text === savedComment.current) return;   // nothing changed (e.g. the bid scope just shown)
    savedComment.current = text;
    apiFetch('/work-orders/' + projectId, { method: 'PATCH', body: JSON.stringify({ wo_comment: text }) })
      .then(() => changed())
      .catch(() => window.alert('Could not save that — check your connection and try again.'));
  }
  async function uploadPlans(files) {
    if (!files || !files.length || uploading) return;
    setUploading(true);
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('plans', f));
    try {
      const r = await fetch(API_BASE + '/projects/' + projectId + '/plans', { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      if (!r.ok) throw new Error('upload failed');
      setPlans(await r.json());
      changed();
    } catch { window.alert('Could not upload that. Please try again.'); }
    setUploading(false);
  }
  function removePlan(id) {
    if (!window.confirm('Remove this plan from the job?')) return;
    apiFetch('/projects/' + projectId + '/plans/' + id, { method: 'DELETE' })
      .then(() => { setPlans(p => p.filter(x => x.id !== id)); changed(); })
      .catch(() => window.alert('Could not remove that.'));
  }
  async function openMap() {
    setShowMap(true);
    if (jobLoc !== undefined) return;
    try {
      const projects = await apiFetch('/projects-map');
      const p = (Array.isArray(projects) ? projects : []).find(x => String(x.id) === String(projectId));
      setJobLoc(p && p.lat != null && p.lng != null ? { lat: p.lat, lng: p.lng, label: p.name || jobName(card) } : null);
    } catch { setJobLoc(null); }
  }

  const planCount = plans.length;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      paddingTop: 'max(env(safe-area-inset-top), 12px)', paddingBottom: 'max(env(safe-area-inset-bottom), 12px)',
      paddingLeft: 12, paddingRight: 12 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#f9fafb', borderRadius: 14, width: 560, maxWidth: '100%', maxHeight: 'min(92vh, calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 24px))', display: 'flex', flexDirection: 'column', boxShadow: '0 16px 60px rgba(0,0,0,0.28)' }}>
        {/* Header */}
        <div style={{ background: '#0f766e', borderRadius: '14px 14px 0 0', padding: '14px 18px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800 }}>🔧 <JobNumberLink projectId={card.id}>{jobNumber(card) || '—'}</JobNumberLink></div>
            <div style={{ fontSize: 13, opacity: 0.9, marginTop: 2 }}>{jobName(card)}</div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 3 }}>{card.client_name ? '🏢 ' + card.client_name + ' · ' : ''}👤 {card.pm_name || (pmName ? pmName(card.pm_id) : '')}</div>
          </div>
          {/* Forward this card to someone in the app. It has its own address, so they
              land on the card rather than the board it sits on. */}
          <SendPointer tone="dark" push
            path={`/admin/workflows/${'small_jobs'}?card=${projectId}`}
            label={`${jobNumber(wfJob(card)) || card.project_number || ''} ${typeMeta('small_jobs').label}`.trim()} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <button onClick={onClose} aria-label="Close"
              style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', fontSize: 20, cursor: 'pointer',
                lineHeight: 1, width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
            <button onClick={openMap} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '6px 12px', borderRadius: 8, whiteSpace: 'nowrap' }}>📍 Location</button>
          </div>
        </div>

        <div style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* No status row: on a work order the status IS the category, and it is one
              tap away on the pillow below. Two controls for one field is how they end
              up disagreeing. */}
          <div>
            <div style={{ ...dcLbl, marginBottom: 6 }}>Scope</div>
            <textarea value={comment} placeholder="What this work order covers… (day-to-day news goes in a Daily Update below)"
              onChange={e => setComment(e.target.value)}
              onBlur={e => saveComment(e.target.value)}
              rows={3} style={{ ...dcInp, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
          {/* Executives: put people on this job in QuickBooks Time (Ryan, Oct 19 2026). */}
          <AddToSchedule projectId={projectId} workflow="small_jobs" startDate={row.wo_date || card.wo_date} days={row.wo_days || card.wo_days} />

          <WorkOrderPillows w={row} onChanged={changed}
            extras={[
              { key: 'plans', label: '📐 Plans' + (planCount ? ' · ' + planCount : ''),
                tone: '#0f766e', toneSoft: '#f0fdfa', toneEdge: '#99f6e4', panel: (<>
          {plansOpen && (
            <div style={{ border: '1.5px solid #ccfbf1', background: '#f0fdfa', borderRadius: 10, padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#0f766e' }}>📐 Plans &amp; Documents</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button onClick={() => setFromDocs(true)} title="Pick files already on the job's Documents tile"
                  style={{ background: 'none', border: 'none', padding: 0, fontSize: 12.5, fontWeight: 700, color: '#0f766e', cursor: 'pointer' }}>📂 From Documents</button>
                <label style={{ fontSize: 12.5, fontWeight: 700, color: '#0f766e', cursor: uploading ? 'default' : 'pointer' }}>
                  {uploading ? 'Uploading…' : '＋ Add'}
                  <input type="file" multiple style={{ display: 'none' }} disabled={uploading}
                    onChange={e => { uploadPlans(e.target.files); e.target.value = ''; }} />
                </label>
                </div>
              </div>
              {fromDocs && <PlansFromDocuments projectId={projectId} plans={plans} onClose={() => setFromDocs(false)} onAdded={r => { if (r && Array.isArray(r.plans)) setPlans(r.plans); else loadPlans(); changed(); }} />}
              {plans.length === 0 ? (
                <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Nothing on this job yet — add the plan sheet, the permit, a sketch.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {plans.map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 11px' }}>
                      <span style={{ fontSize: 16 }}>{/^image\//.test(p.mime_type || '') ? '🖼️' : '📄'}</span>
                      <a href={photoUrl(p.filename)} target="_blank" rel="noreferrer"
                        style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: '#0f766e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.original_name || 'File'}
                      </a>
                      {canDeletePlan && (
                        <button onClick={() => removePlan(p.id)} title="Remove"
                          style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>Delete</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
              </>), open: plansOpen, onClick: () => setPlansOpen(v => !v) },
              // The pillow says which rates lead on the card (set on the job's T&M Rates tile).
              tmShow ? { key: 'tm', label: `⏱️ ${TM_SOURCE_LABEL[row.tm_rate_source] || 'Standard T&M'}`, panel: (<>
          {tmShow && tmOpen && (
            // A small-job work order bills T&M by the day — the same tracker as Extra Work,
            // and the same data the office sees on the job's T&M Billing tile.
            <TmProjectTracker projectId={projectId} projectNumber={card.project_number}
              canEdit={tmCanEdit} canSeeMoney={tmOffice} workflow="small_jobs" rateSource={row.tm_rate_source || null} />
          )}
              </>), open: tmOpen, onClick: () => setTmOpen(v => !v) } : null,
              { key: 'photos', label: '📷 Photos' + (jobPhotos && jobPhotos.length ? ' · ' + jobPhotos.length : ''),
                tone: '#0369a1', toneSoft: '#f0f9ff', toneEdge: '#bae6fd', panel: (<>
          {photosOpen && <JobPhotosSection photos={jobPhotos} projectId={projectId} onAdded={reloadPhotos} />}
              </>), open: photosOpen, onClick: () => setPhotosOpen(v => !v) },
              // Its permits (Pat, Oct 9 2026) — the office, PMs and Executives.
              tmOffice ? { key: 'permits', label: '📜 Permits', tone: '#0e7490', toneSoft: '#ecfeff', toneEdge: '#a5f3fc', panel: (<>
          {permitsOpen && <JobPermitsPillow projectId={projectId} />}
              </>), open: permitsOpen, onClick: () => setPermitsOpen(v => !v) } : null,
            ]} />


          {/* Plans — the drawing the crew is standing on, on the job rather than
              buried in the bid. Any file: a PDF most days, a photo of a marked-up
              sheet the rest. */}

          {/* T&M — the same ledger the drilling card writes, because it is the same
              job. Only on jobs actually bid that way. */}

          {/* A work order is the whole job — every Fieldwire pin on it (quiet when unlinked). */}
          <FieldwireTasks projectId={projectId} canEdit />

          <div>
            <div style={{ ...dcLbl, marginBottom: 6 }}>Daily Updates{updates.length ? ' (' + updates.length + ')' : ''}</div>
            {updates.length === 0 ? (
              <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Nothing posted on this job yet — use the Daily Update pillow above.</div>
            ) : (<>
              {(updatesAll ? updates : updates.slice(0, 5)).map(u => (
                <div key={u.id} style={{ borderLeft: '3px solid #e5e7eb', paddingLeft: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 11.5, color: '#6b7280', fontWeight: 700 }}>
                    {u.author_name || 'Someone'}
                    <span style={{ fontWeight: 500 }}> · {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>{u.from_job && <span title="Posted on the job — it names this card's work" style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 800, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, padding: '0 6px' }}>from the job</span>}
                  </div>
                  <div style={{ fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap', lineHeight: 1.45, marginTop: 2 }}>{u.notes}</div>
                  <Reactions type="update" id={u.id} reactions={u.reactions} />
                </div>
              ))}
              {updates.length > 5 && (
                <button onClick={() => setUpdatesAll(v => !v)}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                  {updatesAll ? 'Show fewer' : 'Show all ' + updates.length}
                </button>
              )}
            </>)}
          </div>

          <div>
            <div style={{ ...dcLbl, marginBottom: 6 }}>Activity{activity.length ? ' (' + activity.length + ')' : ''}</div>
            {activity.length === 0 ? (
              <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Nothing logged on this job yet.</div>
            ) : (<>
              {(activityAll ? activity : activity.slice(0, 6)).map(a => (
                <div key={a.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 7 }}>
                  <div style={{ flex: 1, fontSize: 12.5, color: '#374151', lineHeight: 1.4 }}>
                    <span style={{ fontWeight: 800, color: '#111827' }}>{a.actor_name || 'Someone'}</span> {a.action}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              ))}
              {activity.length > 6 && (
                <button onClick={() => setActivityAll(v => !v)}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                  {activityAll ? 'Show fewer' : 'Show all ' + activity.length}
                </button>
              )}
            </>)}
          </div>
        </div>

        <div style={{ borderTop: '1px solid #e5e7eb', background: '#fff', padding: '10px 14px', paddingBottom: 'max(env(safe-area-inset-bottom), 10px)', borderRadius: '0 0 14px 14px' }}>
          <button onClick={onClose}
            style={{ width: '100%', minHeight: 48, borderRadius: 10, border: '1.5px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>

      {showMap && (
        <div onClick={e => { e.stopPropagation(); setShowMap(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 620, maxWidth: '100%', overflow: 'hidden', boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ background: '#0f766e', padding: '12px 16px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 700 }}>📍 {jobNumber(card)} — {jobName(card)}</span>
              <button onClick={() => setShowMap(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            {jobLoc === undefined ? (
              <div style={{ padding: 50, textAlign: 'center', color: '#9ca3af' }}>Loading map…</div>
            ) : jobLoc === null ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>No saved location for this job yet.</div>
            ) : (
              <MapContainer center={[jobLoc.lat, jobLoc.lng]} zoom={14} style={{ height: 380, width: '100%' }}>
                <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[jobLoc.lat, jobLoc.lng]}><Popup>{jobLoc.label}</Popup></Marker>
              </MapContainer>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// The loops card, built to the drilling card it was copied from: the same header,
// the same status row, the same pillow grid and the same record at the bottom. What
// differs is what a loops job is made of — a quantity instead of a drill, and the
// kinds of loop instead of a locate ticket.
// Close after Complete (Pat, Oct 5 2026 — "Job 2616 on the loops board: close it after it's
// complete so the card is only seen on the job; if the status is changed it goes back to the
// workflow"). Closing is not another status: the card leaves the board and stays on its job
// with everything on it. Offered once the card is Complete; a closed card shows Reopen, and
// any status button reopens it too (the server clears the close).
function CloseCardStrip({ card, sjId, status, setStatus, board, onChanged, onClose }) {
  const [busy, setBusy] = useState(false);
  const [closedKind, setClosedKind] = useState(card.close_kind || null);
  const boardName = board === 'loops' ? 'loops board' : 'drilling board';
  // Not a stand-alone work order → the PM / office / Executive chooses how it is billed.
  const choose = !isStandaloneWo(card);
  async function closeAs(kind) {
    setBusy(true);
    try {
      const r = await closeWorkCard(card, sjId, kind);
      if (r) { setClosedKind(kind); setStatus('closed'); onChanged && onChanged(); if (onClose) onClose(); }
    } catch (e) { window.alert(e?.error || 'Could not close that card.'); }
    setBusy(false);
  }
  async function closeCard() {
    if (!window.confirm(`Close ${card.name || 'this card'}?\n\nIt comes off the ${boardName} and stays on the job. Nothing on it is lost, and changing its status (or Reopen) puts it back.`)) return;
    setBusy(true);
    try {
      await apiFetch(`/projects/${card.project_id}/sub-jobs/${sjId}/close`, { method: 'POST' });
      setStatus('closed');
      onChanged && onChanged();
      if (onClose) onClose();
    } catch (e) { window.alert(e?.error || 'Could not close that card.'); }
    setBusy(false);
  }
  async function reopenCard() {
    setBusy(true);
    try {
      // The server puts it back to whatever it was when it closed.
      const back = await apiFetch(`/projects/${card.project_id}/sub-jobs/${sjId}/reopen`, { method: 'POST' });
      setStatus(back?.status || 'complete');
      onChanged && onChanged();
    } catch (e) { window.alert(e?.error || 'Could not reopen that card.'); }
    setBusy(false);
  }
  if (status === 'closed') {
    const tag = closeTag({ ...card, close_kind: closedKind });
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#f5f5f4', border: '1px solid #e7e5e4', borderRadius: 8, padding: '9px 12px' }}>
        <span style={{ fontSize: 12.5, color: '#57534e' }}><b>Closed.</b> Off the {boardName} and kept on the job.</span>
        {tag && <span style={{ fontSize: 11.5, fontWeight: 800, color: tag.color, background: tag.bg, borderRadius: 999, padding: '3px 9px' }}>{tag.text}</span>}
        <button onClick={reopenCard} disabled={busy} style={{ marginLeft: 'auto', padding: '6px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Reopen</button>
      </div>
    );
  }
  if (status !== 'complete') return null;
  if (choose) {
    if (!canChooseBilling()) return (
      <div style={{ fontSize: 12.5, color: '#6b7280' }}>Complete — the PM or the office sends it to billing or closes it to the job.</div>
    );
    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={() => closeAs('billing')} disabled={busy} title="Bill this work on its own — it comes off the board"
          style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #15803d', background: '#f0fdf4', color: '#15803d', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>
          🧾 Send to billing
        </button>
        <button onClick={() => closeAs('job')} disabled={busy} title={`Billed with ${jobWord(card)} — it comes off the board, nothing goes to billing`}
          style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #1d4ed8', background: '#eff6ff', color: '#1d4ed8', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>
          📁 Close to {jobWord(card)}
        </button>
      </div>
    );
  }
  return (
    <button onClick={closeCard} disabled={busy} title={`Done with it — take it off the ${boardName}; it stays on the job`}
      style={{ alignSelf: 'flex-start', padding: '7px 14px', borderRadius: 8, border: '1px solid #d6d3d1', background: '#fff', color: '#57534e', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
      ✓ Close this card
    </button>
  );
}

export function LoopsCardModal({ card, pmName, onClose, onChanged, inline = false }) {
  const sjId = card.sub_job_id || card.id;
  // The card's own workflow row. Quantity and kinds are counted on the server from
  // the loop locations, so they are re-read rather than tracked by hand here.
  const [row, setRow] = useState(card);
  const [status, setStatus] = useState(card.status || 'pending');
  const [comment, setComment] = useState(card.drill_comment || '');
  const [loopsOpen, setLoopsOpen] = useState(!!card._openLoops);
  // Standard T&M on the Loops card (Pat, Sep 2026 — it had no T&M logging at all). Same
  // rule as the small-job card: a T&M bid, a job made without a bid, or a job that
  // already has common items on its T&M Rates. It is the job's one daily log — the same
  // days the office sees on the job's Standard T&M tile — offering the loop items first.
  const [tmOpen, setTmOpen] = useState(!!card._openTm);
  const [commonOpen, setCommonOpen] = useState(false);
  const [tmShow, setTmShow] = useState(false);
  // Photos from the field (Ryan, Sep 29 2026) — job-note photos, loop-location photos
  // and T&M-day photos in one strip; the card only ever showed the loop ones, inside Loops.
  const [photosOpen, setPhotosOpen] = useState(false);
  const { photos: jobPhotos, reload: reloadPhotos } = useJobPhotos(card.project_id);
  const { worker: loopWorker } = useAuth() || {};
  const loopTmOffice = ['admin', 'Admin', 'ProjectManager', 'OfficeStaff', 'Accounting', 'Executive'].includes(loopWorker?.role);
  const loopTmCanEdit = loopTmOffice || loopWorker?.role === 'FieldSupervisor';
  React.useEffect(() => {
    if (!card.project_id) return;
    apiFetch('/projects/' + card.project_id + '/bid')
      .then(b => { if (b && b.pricing_type === 'tm') setTmShow(true); })
      .catch(e => { if (e && e.error === 'No bid linked') setTmShow(true); });
    apiFetch('/projects/' + card.project_id + '/tm-items').then(r => { if (Array.isArray(r) && r.length) setTmShow(true); }).catch(() => {});
  }, [card.project_id]);
  const [updates, setUpdates] = useState([]);
  const [updatesAll, setUpdatesAll] = useState(false);
  const [activity, setActivity] = useState([]);
  const [activityAll, setActivityAll] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [jobLoc, setJobLoc] = useState(undefined); // undefined=not loaded, null=no location

  const reloadRow = React.useCallback(() => {
    apiFetch('/workflows')
      .then(all => { const r = (Array.isArray(all) ? all : []).find(x => x.id === sjId); if (r) setRow(r); })
      .catch(() => {});
  }, [sjId]);
  const loadActivity = React.useCallback(() => {
    if (!card.project_id) return;
    apiFetch('/activity?scope=job&scopeId=' + card.project_id + '&subJobId=' + encodeURIComponent(sjId) + '&limit=60')
      .then(a => setActivity(Array.isArray(a) ? a : [])).catch(() => {});
  }, [card.project_id]);

  useEffect(() => {
    if (!card.project_id) return;
    loadActivity();
    reloadRow();
    apiFetch('/projects/' + card.project_id + '/daily-updates?sub_job_id=' + encodeURIComponent(sjId))
      .then(u => setUpdates(Array.isArray(u) ? u : [])).catch(() => {});
  }, [card.project_id, loadActivity, reloadRow]);

  // Everything the pillows write also changes what the board behind shows.
  function changed() { reloadRow(); loadActivity(); onChanged && onChanged(); }

  function setCardStatus(next) {
    const prev = status;
    setStatus(next);
    apiFetch('/projects/' + card.project_id + '/sub-jobs/' + sjId, { method: 'PATCH', body: JSON.stringify({ status: next }) })
      .then(() => changed())
      .catch(() => { setStatus(prev); window.alert('Could not update the status — check your connection and try again.'); });
  }
  function saveCard(body) {
    apiFetch('/sub-jobs/' + sjId + '/card', { method: 'PATCH', body: JSON.stringify(body) })
      .then(() => changed())
      .catch(() => window.alert('Could not save that — check your connection and try again.'));
  }
  async function openMap() {
    setShowMap(true);
    if (jobLoc !== undefined) return;
    try {
      const projects = await apiFetch('/projects-map');
      const p = (Array.isArray(projects) ? projects : []).find(x => String(x.id) === String(card.project_id));
      setJobLoc(p && p.lat != null && p.lng != null ? { lat: p.lat, lng: p.lng, label: p.name || jobName(wfJob(card)) } : null);
    } catch { setJobLoc(null); }
  }
  // Closing the list is the moment to re-count: a location added or re-typed in
  // there changes the quantity and the kinds sitting on the pillows above it.
  function toggleLoops() {
    setLoopsOpen(v => { if (v) { reloadRow(); onChanged && onChanged(); } return !v; });
  }

  const locCount = row.loop_locations_count || 0;

  return (
    <div style={inline ? { display: 'block' } : { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      paddingTop: 'max(env(safe-area-inset-top), 12px)', paddingBottom: 'max(env(safe-area-inset-bottom), 12px)',
      paddingLeft: 12, paddingRight: 12 }} onClick={inline ? undefined : onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#f9fafb', borderRadius: 14, width: inline ? '100%' : 560, maxWidth: '100%', maxHeight: inline ? 'none' : 'min(92vh, calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 24px))', display: 'flex', flexDirection: 'column', boxShadow: inline ? 'none' : '0 16px 60px rgba(0,0,0,0.28)', border: inline ? '1px solid #e5e7eb' : 'none' }}>
        {/* Header */}
        {/* Header — shown inline too. A single card on a job's Work Flows board is
            meant to BE the workflow board's card, header and all; hiding it here made
            the two look like different things. */}
        <div style={{ background: '#7c3aed', borderRadius: '14px 14px 0 0', padding: '14px 18px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800 }}>🔁 <JobNumberLink projectId={card.project_id}>{jobNumber(wfJob(card)) || '—'}</JobNumberLink></div>
            <div style={{ fontSize: 13, opacity: 0.9, marginTop: 2 }}>{jobName(wfJob(card))}</div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 3 }}>{card.client_name ? '🏢 ' + card.client_name + ' · ' : ''}👤 {pmName(card.pm_id)}</div>
          </div>
          {/* Forward this card to someone in the app. It has its own address, so they
              land on the card rather than the board it sits on. */}
          <SendPointer tone="dark" push
            path={`/admin/workflows/${'loops'}?card=${sjId}`}
            label={`${jobNumber(wfJob(card)) || card.project_number || ''} ${typeMeta('loops').label}`.trim()} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            {/* Inline the card is the container — nothing to close. */}
            {!inline && (
            <button onClick={onClose} aria-label="Close"
              style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', fontSize: 20, cursor: 'pointer',
                lineHeight: 1, width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
            )}
            <button onClick={openMap} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '6px 12px', borderRadius: 8, whiteSpace: 'nowrap' }}>📍 Location</button>
          </div>
        </div>

        <div style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Status — at the very top, and saying Scheduled, because the loops board
              behind this card says Scheduled too. */}
          <div>
            <div style={{ ...dcLbl, marginBottom: 6 }}>Status</div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'nowrap' }}>
              {SCHEDULED_STATUS_COLUMNS.map(s => {
                const on = status === s.key;
                return (
                  <button key={s.key} onClick={() => setCardStatus(s.key)}
                    style={{ flex: 1, minWidth: 0, padding: '8px 2px', borderRadius: 20, border: on ? '2px solid ' + s.color : '1.5px solid #e5e7eb', background: on ? s.bg : '#fff', color: on ? s.color : '#374151',
                      fontSize: 'clamp(10px, 3vw, 12px)', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <CloseCardStrip card={card} sjId={sjId} status={status} setStatus={setStatus} board="loops" onChanged={changed} onClose={onClose} />

          {/* What was sold — the bid's Job Description, read here rather than retyped
              into the comment (Ryan, Sep 30 2026). Edited on the job's Job Description tile. */}
          {String(row.bid_scope || card.bid_scope || '').trim() && (
            <div>
              <div style={{ ...dcLbl, marginBottom: 6 }}>Scope of work <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>· from the bid</span></div>
              <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.45, whiteSpace: 'pre-wrap', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px' }}>
                {row.bid_scope || card.bid_scope}
              </div>
            </div>
          )}

          {/* Comment */}
          <div>
            <div style={{ ...dcLbl, marginBottom: 6 }}>Comment</div>
            <textarea value={comment} placeholder="Add a note about this loops card…"
              onChange={e => setComment(e.target.value)}
              onBlur={e => saveCard({ drill_comment: e.target.value })}
              rows={3} style={{ ...dcInp, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
          {/* Executives: put people on this job in QuickBooks Time (Ryan, Oct 19 2026). */}
          <AddToSchedule projectId={card.project_id} workflow="loops" startDate={row.drill_date || card.drill_date} days={row.sched_days || card.sched_days} />

          {/* The same four pillows as the board tile, so the card reads the same
              wherever you meet it. The loop list itself folds away underneath. */}
          <LoopPillows w={row} onChanged={changed} onOpenLoops={() => setLoopsOpen(true)}
            extras={[
              { key: 'loops', label: '🔁 Loops' + (locCount ? ' · ' + locCount + ' location' + (locCount === 1 ? '' : 's') : ''),
                tone: '#7c3aed', toneSoft: '#faf5ff', toneEdge: '#ddd6fe', panel: (<>
          {loopsOpen && (
            <div>
              <LoopsTab sjId={sjId} isAdminZone API_BASE={API_BASE} getToken={getToken}
                showToast={(msg, type) => { if (type === 'error' && msg) window.alert(msg); }} />
            </div>
          )}
              </>), open: loopsOpen, onClick: toggleLoops },
              { key: 'photos', label: '📷 Photos' + (jobPhotos && jobPhotos.length ? ' · ' + jobPhotos.length : ''),
                tone: '#0369a1', toneSoft: '#f0f9ff', toneEdge: '#bae6fd', panel: (<>
          {photosOpen && <JobPhotosSection photos={jobPhotos} projectId={card.project_id} onAdded={reloadPhotos} />}
              </>), open: photosOpen, onClick: () => setPhotosOpen(v => !v) },
            ]} />


          {/* The loop locations — every kind, quantity, photo and note, behind the
              pillow. The same panel the office uses on the bid, not a second copy. */}

          {/* The job's quoted rates — loops by the foot or each — are their OWN purple pill,
              under the loop info and above Standard T&M (Pat, Sep 2026): they come from the
              job's T&M Rates tile, not from labor / material / equipment, so they don't belong
              inside that one. Same days and the same invoice underneath; two doors into them. */}
          {tmShow && (
            <button onClick={() => setCommonOpen(v => !v)}
              style={{ minHeight: DRILL_PILL_H, boxSizing: 'border-box', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                borderRadius: 10, border: '1.5px solid #ddd6fe', background: commonOpen ? '#7c3aed' : '#faf5ff', color: commonOpen ? '#fff' : '#7c3aed',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '0 10px', textAlign: 'center' }}>
              📋 Common Items · quantities <span style={{ fontSize: 11, opacity: 0.8 }}>{commonOpen ? '▲' : '▼'}</span>
            </button>
          )}
          {tmShow && commonOpen && (
            <TmProjectTracker projectId={card.project_id} projectNumber={card.project_number}
              canEdit={loopTmCanEdit} canSeeMoney={loopTmOffice} workflow="loops" show="common" />
          )}

          {/* Standard T&M sits BELOW the loop info (Pat, Sep 2026): the loops are the job,
              the T&M log is how it bills. Its bar is its own here rather than one of the
              pillows' extras, so the loop list opens directly under the Loops bar. */}
          {tmShow && (
            <button onClick={() => setTmOpen(v => !v)}
              style={{ minHeight: DRILL_PILL_H, boxSizing: 'border-box', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                borderRadius: 10, border: '1.5px solid #fde68a', background: tmOpen ? '#b45309' : '#fffbeb', color: tmOpen ? '#fff' : '#b45309',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '0 10px', textAlign: 'center' }}>
              ⏱️ Standard T&amp;M <span style={{ fontSize: 11, opacity: 0.8 }}>{tmOpen ? '▲' : '▼'}</span>
            </button>
          )}
          {tmShow && tmOpen && (
            <TmProjectTracker projectId={card.project_id} projectNumber={card.project_number}
              canEdit={loopTmCanEdit} canSeeMoney={loopTmOffice} workflow="loops" show="standard" />
          )}

          {/* Fieldwire pins whose category is mapped to this card (quiet when there are none). */}
          <FieldwireTasks projectId={card.project_id} subJobId={sjId} canEdit />

          {/* Daily updates — newest first. What happened on this job, in the words of
              whoever was standing on it. */}
          <div>
            <div style={{ ...dcLbl, marginBottom: 6 }}>Daily Updates{updates.length ? ' (' + updates.length + ')' : ''}</div>
            {updates.length === 0 ? (
              <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Nothing posted on this job yet — use the Daily Update pillow above.</div>
            ) : (<>
              {(updatesAll ? updates : updates.slice(0, 5)).map(u => (
                <div key={u.id} style={{ borderLeft: '3px solid #e5e7eb', paddingLeft: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 11.5, color: '#6b7280', fontWeight: 700 }}>
                    {u.author_name || 'Someone'}
                    <span style={{ fontWeight: 500 }}> · {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>{u.from_job && <span title="Posted on the job — it names this card's work" style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 800, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, padding: '0 6px' }}>from the job</span>}
                  </div>
                  <div style={{ fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap', lineHeight: 1.45, marginTop: 2 }}>{u.notes}</div>
                  <Reactions type="update" id={u.id} reactions={u.reactions} />
                </div>
              ))}
              {updates.length > 5 && (
                <button onClick={() => setUpdatesAll(v => !v)}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                  {updatesAll ? 'Show fewer' : 'Show all ' + updates.length}
                </button>
              )}
            </>)}
          </div>

          {/* Activity — what changed on this job and who changed it, including a
              moved date, exactly as the drilling card records it. */}
          <div>
            <div style={{ ...dcLbl, marginBottom: 6 }}>Activity{activity.length ? ' (' + activity.length + ')' : ''}</div>
            {activity.length === 0 ? (
              <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Nothing logged on this job yet.</div>
            ) : (<>
              {(activityAll ? activity : activity.slice(0, 6)).map(a => (
                <div key={a.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 7 }}>
                  <div style={{ flex: 1, fontSize: 12.5, color: '#374151', lineHeight: 1.4 }}>
                    <span style={{ fontWeight: 800, color: '#111827' }}>{a.actor_name || 'Someone'}</span> {a.action}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              ))}
              {activity.length > 6 && (
                <button onClick={() => setActivityAll(v => !v)}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                  {activityAll ? 'Show fewer' : 'Show all ' + activity.length}
                </button>
              )}
            </>)}
          </div>
        </div>

        {/* Close, where a thumb actually is — outside the scrolling body, so it never
            scrolls away. */}
        <div style={{ borderTop: '1px solid #e5e7eb', background: '#fff', padding: '10px 14px', paddingBottom: 'max(env(safe-area-inset-bottom), 10px)', borderRadius: '0 0 14px 14px' }}>
          {/* Inline the card is the container — nothing to close. */}
          {!inline && (
          <button onClick={onClose}
            style={{ width: '100%', minHeight: 48, borderRadius: 10, border: '1.5px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
            Close
          </button>
          )}
        </div>
      </div>

      {showMap && (
        <div onClick={e => { e.stopPropagation(); setShowMap(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 620, maxWidth: '100%', overflow: 'hidden', boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ background: '#7c3aed', padding: '12px 16px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 700 }}>📍 {jobNumber(wfJob(card))} — {jobName(wfJob(card))}</span>
              <button onClick={() => setShowMap(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            {jobLoc === undefined ? (
              <div style={{ padding: 50, textAlign: 'center', color: '#9ca3af' }}>Loading map…</div>
            ) : jobLoc === null ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>No saved location for this job yet.<div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>Set the job's address on the project to place it on the map.</div></div>
            ) : (
              <MapContainer center={[jobLoc.lat, jobLoc.lng]} zoom={14} style={{ height: 380, width: '100%' }}>
                <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[jobLoc.lat, jobLoc.lng]}><Popup>{jobLoc.label}</Popup></Marker>
              </MapContainer>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// USA (811) card opened from the board — shows the job's full USA dig panel
// (every ticket, status, photos, PDF) in a modal, reusing UsaDigsTab.
export function UsaCardModal({ card, pmName, onClose, inline = false }) {
  return (
    <div style={inline ? { display: 'block' } : { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      // A phone's clock and notch sit over a full-height overlay, and these cards are
      // tall enough to reach them — which put the job number and the × behind the
      // status bar where neither could be read or tapped.
      paddingTop: 'max(env(safe-area-inset-top), 12px)', paddingBottom: 'max(env(safe-area-inset-bottom), 12px)',
      paddingLeft: 12, paddingRight: 12 }} onClick={inline ? undefined : onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#f9fafb', borderRadius: 14, width: inline ? '100%' : 640, maxWidth: '100%', maxHeight: inline ? 'none' : 'min(92vh, calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 24px))', display: 'flex', flexDirection: 'column', boxShadow: inline ? 'none' : '0 16px 60px rgba(0,0,0,0.28)', border: inline ? '1px solid #e5e7eb' : 'none' }}>
        {/* Header — shown inline too. A single card on a job's Work Flows board is
            meant to BE the workflow board's card, header and all; hiding it here made
            the two look like different things. */}
        <div style={{ background: '#b45309', borderRadius: '14px 14px 0 0', padding: '14px 18px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800 }}>🚧 <JobNumberLink projectId={card.project_id}>{jobNumber(wfJob(card)) || '—'}</JobNumberLink></div>
            <div style={{ fontSize: 13, opacity: 0.9, marginTop: 2 }}>{jobName(wfJob(card))}</div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 3 }}>{card.client_name ? `🏢 ${card.client_name} · ` : ''}👤 {pmName(card.pm_id)}</div>
          </div>
          {/* Forward this card to someone in the app. It has its own address, so they
              land on the card rather than the board it sits on. */}
          <SendPointer tone="dark" push
            path={`/admin/workflows/usa_digs?card=${card.sub_job_id || card.id}`}
            label={`${jobNumber(wfJob(card)) || card.project_number || ''} ${typeMeta('usa_digs').label}`.trim()} />
          {/* Inline the card is the container — nothing to close. */}
          {!inline && (
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
          )}
        </div>
        <div style={{ padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <DailyUpdateComposer projectId={card.project_id} subJobId={card.sub_job_id || card.id} workflowLabel="this USA board" />
          <UsaDigsTab sjId={card.sub_job_id || card.id} isAdminZone API_BASE={API_BASE} getToken={getToken} showToast={(msg, type) => { if (type === 'error' && msg) window.alert(msg); }} />
          {/* Fieldwire pins whose category is mapped to this card (quiet when there are none). */}
          <FieldwireTasks projectId={card.project_id} subJobId={card.sub_job_id || card.id} canEdit />
        </div>
      </div>
    </div>
  );
}
const dcLbl = { fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 };
const dcInp = { border: '1px solid #d1d5db', borderRadius: 7, padding: '9px 10px', fontSize: 14, width: '100%', boxSizing: 'border-box', background: '#fff' };

// Shared "Add Daily Update" pill + composer with a 🎤 mic. Posts to the job's
// Daily Updates list AND — because it passes the originating card's sub_job_id —
// that workflow's activity feed (drilling, NVE, …). Used on drill & NVE cards.
// meterId (NVE): the update's words and pictures also go on that meter's Fieldwire task,
// when it has one (Ryan, Oct 3 2026).
// meters (NVE, 2 or more): the composer asks which meter — or Underground, the PID itself —
// before it posts, so the right Fieldwire task gets the words and pictures.
function DailyUpdateComposer({ projectId, subJobId, workflowLabel = 'this board', autoOpen = false, hideLabel = false, meterId = null, meters = [] }) {
  const [showUpdate, setShowUpdate] = useState(autoOpen);
  const askWhich = Array.isArray(meters) && meters.length > 1;
  const [where, setWhere] = useState(undefined);   // undefined = not picked yet; null = Underground; else a meter id
  const targetMeter = askWhich ? (where || null) : meterId;
  const needsPick = askWhich && where === undefined;
  const [updateText, setUpdateText] = useState('');
  const [posting, setPosting] = useState(false);
  const [postedOk, setPostedOk] = useState(false);
  const [photos, setPhotos] = useState([]);          // picked, not yet uploaded
  const [listening, setListening] = useState(false);
  const photoInputRef = useRef(null);
  const recognitionRef = useRef(null);
  const voiceSupported = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  function startVoice() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Voice input is not supported on this browser. You can still type.'); return; }
    const r = new SR();
    r.lang = 'en-US'; r.continuous = true; r.interimResults = false;
    r.onresult = e => { const t = Array.from(e.results).map(x => x[0].transcript).join(' '); setUpdateText(n => n ? n + ' ' + t : t); };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    r.start(); recognitionRef.current = r; setListening(true);
  }
  function stopVoice() { recognitionRef.current?.stop(); setListening(false); }
  async function post() {
    if (posting || !updateText.trim() || needsPick) return;
    stopVoice(); setPosting(true);
    try {
      await apiFetch(`/projects/${projectId}/daily-updates`, { method: 'POST', body: JSON.stringify({ notes: updateText.trim(), sub_job_id: subJobId, nve_meter_id: targetMeter || undefined }) });
      // Pictures go up after the words are safely posted. If an upload fails out in
      // the field on a bad signal, the update itself is already saved — losing the
      // typing because a photo didn't send would be the worse trade.
      if (photos.length && subJobId) {
        const fd = new FormData();
        if (targetMeter) fd.append('meter_id', targetMeter);
        photos.forEach(p => fd.append('photos', p));
        try {
          await fetch(`${API_BASE}/sub-jobs/${subJobId}/photos`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
        } catch { alert('The update posted, but the photos did not upload. Try adding them again from the card.'); }
      }
      setUpdateText(''); setPhotos([]); setShowUpdate(false); setPostedOk(true); setWhere(undefined);
    } catch { alert('Could not post the update. Please try again.'); }
    setPosting(false);
  }
  return (
    <div>
      {!hideLabel && <div style={{ ...dcLbl, marginBottom: 6 }}>Daily Update</div>}
      {!showUpdate ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={() => { setShowUpdate(true); setPostedOk(false); }}
            style={{ padding: '11px 16px', borderRadius: 10, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
            ➕ Add Daily Update
          </button>
          {postedOk && <span style={{ fontSize: 12.5, fontWeight: 700, color: '#15803d' }}>✓ Posted to the job's daily updates</span>}
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 12 }}>
          <div style={{ position: 'relative' }}>
            <textarea value={updateText} autoFocus placeholder="What happened on the job today?"
              onChange={e => setUpdateText(e.target.value)}
              rows={3} style={{ ...dcInp, resize: 'vertical', fontFamily: 'inherit', paddingRight: 52 }} />
            {voiceSupported && (
              <button onClick={() => listening ? stopVoice() : startVoice()} title={listening ? 'Stop' : 'Tap to talk'}
                style={{ position: 'absolute', top: 8, right: 8, width: 38, height: 38, borderRadius: '50%', border: 'none', cursor: 'pointer',
                  background: listening ? '#dc2626' : '#eef2ff', color: listening ? '#fff' : '#1e3a5f', fontSize: 18, lineHeight: 1,
                  boxShadow: listening ? '0 0 0 4px rgba(220,38,38,0.18)' : 'none' }}>
                🎤
              </button>
            )}
          </div>
          {listening && <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginTop: 6 }}>● Listening… tap the mic to stop.</div>}

          {/* Pictures & files — the phone offers its camera, its library, or Files
              (a PDF). Most are taken on the spot, but a photo from an hour ago, or a
              scanned page, is just as much the job. */}
          <AttachButton multiple variant="plain" icon="📷" label={`Add Photo or File${photos.length ? ` (${photos.length})` : ''}`}
            style={{ marginTop: 10 }} onFiles={fs => setPhotos(p => [...p, ...fs])} />
          {photos.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {photos.map((p, i) => (
                <AttachThumb key={i} size={64} src={isPdf(p) ? null : URL.createObjectURL(p)} name={p.name}
                  onRemove={() => setPhotos(ps => ps.filter((_, j) => j !== i))} />
              ))}
            </div>
          )}

          {askWhich && (
            <div style={{ marginTop: 12, border: `1.5px solid ${needsPick ? '#fcd34d' : '#e5e7eb'}`, background: needsPick ? '#fffbeb' : '#fafafa', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: needsPick ? '#92400e' : '#374151', marginBottom: 8 }}>Which is this about?{needsPick ? ' — pick one before posting' : ''}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {[{ id: null, label: '⬇️ Underground · the PID' }, ...meters.map((m, i) => ({ id: m.id, label: `⚡ Meter ${i + 1}${m.address ? ' — ' + m.address : ''}` }))].map(o => {
                  const on = where !== undefined && where === o.id;
                  return (
                    <button key={o.id || 'ug'} type="button" onClick={() => setWhere(o.id)}
                      style={{ padding: '7px 11px', borderRadius: 999, border: `1.5px solid ${on ? '#b45309' : '#e5e7eb'}`, background: on ? '#b45309' : '#fff', color: on ? '#fff' : '#374151', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={post} disabled={posting || !updateText.trim() || needsPick}
              title={needsPick ? 'Pick Underground or a meter first' : undefined}
              style={{ flex: 1, padding: '11px', borderRadius: 8, border: 'none', background: updateText.trim() && !needsPick ? '#15803d' : '#cbd5e1', color: '#fff', fontSize: 14, fontWeight: 800, cursor: updateText.trim() && !needsPick ? 'pointer' : 'default' }}>
              {posting ? 'Posting…' : 'Post Update'}
            </button>
            <button onClick={() => { stopVoice(); setShowUpdate(false); setUpdateText(''); setPhotos([]); setWhere(undefined); }}
              style={{ padding: '11px 16px', borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>Posts to both the job's Daily Updates and {workflowLabel}'s activity{voiceSupported ? ' · tap 🎤 to dictate' : ''}.</div>
    </div>
  );
}

// A card on the office boards (Submittals, Contract, Subcontracts, Payroll) opens THAT
// pillow on the job, not the job's tile hub (Pat, Sep 23 2026: 'it goes to the job
// instead of the submittal'). Anything else still opens the job page.
const OFFICE_PILLOW = { submittals: 'submittals', contract: 'contract', subcontracts: 'subcontracts', payroll: 'payroll' };
function officePillowPath(w, embedded) {
  const pillow = OFFICE_PILLOW[w.type];
  if (pillow) return (embedded ? `/sup/jobs/${w.project_id}` : `/admin/jobs/${w.project_id}`) + `?pillow=${pillow}`;
  return embedded ? `/project/${w.project_id}` : `/admin/new-projects/${w.project_id}`;
}
const BOARD_GAP = 14;
// True on a phone-width screen. Used where a header row only earns its place while the
// row it labels is still on one line.
function useIsNarrow(px = 560) {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(`(max-width:${px}px)`).matches);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${px}px)`);
    const on = e => setNarrow(e.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, [px]);
  return narrow;
}

// The Time & Material ledger, lifted out of the drilling card so a work order can
// bill the same way. It is addressed by JOB rather than by card: a job has one T&M
// ledger and one invoice, whichever card you happen to open it from — two
// half-ledgers is how a job ends up billed twice or not at all.
//
// The panel collects what happened and does not price it. The only money here is the
// printable Billing Summary, which is office-only and needs a card to hang the
// invoice number on; a job with no card at all prints without one.
export function TmBillingPanel({ job, bid, projectId, invoiceSjId, invoiceNo, pmName, onChanged }) {
  const narrow = useIsNarrow();
  const [tmLog, setTmLog] = useState([]);     // field-logged days/hours + mobilizations
  const [tmItems, setTmItems] = useState([]); // the job's quote items — offered first when logging
  const [tmSaving, setTmSaving] = useState(false);
  const [tmInvoiceNo, setTmInvoiceNo] = useState(invoiceNo || null);
  const [tmInv, setTmInv] = useState(null);       // the job's drilling invoice, open in the editor
  const [tmAddCommon, setTmAddCommon] = useState(false);   // picking a Price Book item onto this job
  const [tmInvBusy, setTmInvBusy] = useState(false);
  // Rates and totals are office-only — the field logs days and mobs, no money.
  const { worker: tmWorker } = useAuth() || {};
  const tmCanSeeMoney = ['admin', 'Admin', 'ProjectManager', 'OfficeStaff', 'Accounting', 'Executive'].includes(tmWorker?.role)
    && !!invoiceSjId;
  useEffect(() => {
    if (!projectId) return;
    apiFetch(`/projects/${projectId}/tm-log`).then(l => setTmLog(Array.isArray(l) ? l : [])).catch(() => {});
    const loadItems = () => apiFetch(`/projects/${projectId}/tm-items`).then(r => setTmItems(Array.isArray(r) ? r : [])).catch(() => {});
    loadItems();
    window.addEventListener('titan:tm-items-changed', loadItems);
    return () => window.removeEventListener('titan:tm-items-changed', loadItems);
  }, [projectId]);
  // What a quote item is logged in: a day, an hour, one trip, or its own unit.
  const itemUnit = it => it.type === 'unit' ? (it.unit_label || 'ea') : it.type === 'hour' ? 'hr' : it.type === 'mobilization' ? 'trip' : 'day';
  const itemOf = e => (e.tm_item_id ? (tmItems.find(i => i.id === e.tm_item_id) || (e.item_name ? { id: e.tm_item_id, name: e.item_name, rate: e.item_rate, type: e.item_type, unit_label: e.item_unit_label } : null)) : null);

  // The unit a job was quoted in, which is what a new line starts as. Both units are
  // always offered: the field records what actually happened, and the office prices it.
  const tmBasis = bid?.tm_rate_basis === 'hourly' ? 'hourly' : 'daily';
  // The next day that isn't already on the sheet — so adding three days running gives
  // three dates instead of three lines all stamped today.
  function nextWorkDate() {
    const today = new Date(); today.setHours(12, 0, 0, 0);
    const latest = tmLog.filter(e => e.kind === 'work').map(e => e.work_date).filter(Boolean).sort().pop();
    if (!latest) return today.toISOString().slice(0, 10);
    const after = new Date(`${latest}T12:00:00`); after.setDate(after.getDate() + 1);
    return (after > today ? after : today).toISOString().slice(0, 10);
  }
  // Adds a line rather than posting a total. A full day in the job's own unit is the
  // usual answer and a visible starting point — the date, the number and the unit are
  // all sitting right there to be corrected.
  // With a quote item: the line is that item — a day of it, 8 hours of it, one trip,
  // one of its unit — priced at the quoted rate. Without: the legacy day/hour line.
  async function addTmEntry(kind, item) {
    if (tmSaving) return;
    const work_date = nextWorkDate();
    const body = item
      ? (item.type === 'mobilization'
        ? { kind: 'mobilization', work_date, tm_item_id: item.id }
        : { kind: 'work', work_date, tm_item_id: item.id, quantity: item.type === 'hour' ? 8 : 1, basis: item.type === 'hour' ? 'hourly' : item.type === 'day' ? 'daily' : null })
      : kind === 'mobilization'
      ? { kind: 'mobilization', work_date }
      : { kind: 'work', work_date, quantity: tmBasis === 'hourly' ? 8 : 1, basis: tmBasis };
    setTmSaving(true);
    try {
      const row = await apiFetch(`/projects/${projectId}/tm-log`, { method: 'POST', body: JSON.stringify(body) });
      setTmLog(l => [row, ...l]); onChanged && onChanged();
    } catch { alert('Could not save. Please try again.'); }
    setTmSaving(false);
  }
  // Edits save as they're made. The row is shown changed straight away and rolled back
  // if the server refuses, so the sheet never claims something that wasn't stored.
  async function patchTmEntry(id, patch) {
    const before = tmLog.find(e => e.id === id);
    if (!before) return;
    setTmLog(l => l.map(e => e.id === id ? { ...e, ...patch } : e));
    try {
      const row = await apiFetch(`/projects/${projectId}/tm-log/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
      setTmLog(l => l.map(e => e.id === id ? row : e)); onChanged && onChanged();
    } catch {
      setTmLog(l => l.map(e => e.id === id ? before : e));
      alert('Could not save that change.');
    }
  }
  function deleteTmEntry(id) {
    apiFetch(`/projects/${projectId}/tm-log/${id}`, { method: 'DELETE' }).then(() => { setTmLog(l => l.filter(e => e.id !== id)); onChanged && onChanged(); }).catch(() => {});
  }
  // Kept apart, because 2 days and 6 hours are not 8 of anything. An older entry saved
  // before a job carried two rates has no basis of its own; it belongs to the unit the
  // job was quoted in, which is what it was logged as.
  // A line logged against a quote item whose type isn't day/hour (a unit price) has no
  // basis; it is counted in the item's own unit, below.
  const entryBasis = e => (e.basis === 'hourly' ? 'hourly' : e.basis === 'daily' ? 'daily' : (itemOf(e)?.type === 'unit' ? null : tmBasis));
  // Billed in parts (Pat, Oct 9 2026 — WO26117): a closed part's lines stay on the card, folded and
  // read-only; the counts and the next invoice are the open part's.
  const tmLogOpen = tmLog.filter(e => !e.part_closed);
  const tmClosedParts = [];
  for (const e of tmLog.filter(x => x.part_closed)) {
    let g = tmClosedParts.find(x => x.id === e.part_id);
    if (!g) { g = { id: e.part_id, name: e.part_name, rows: [] }; tmClosedParts.push(g); }
    g.rows.push(e);
  }
  const [tmPartOpen, setTmPartOpen] = useState({});
  const tmWork = tmLogOpen.filter(e => e.kind === 'work');
  const tmDaysTotal = tmWork.filter(e => entryBasis(e) === 'daily').reduce((s, e) => s + (e.quantity || 0), 0);
  const tmHoursTotal = tmWork.filter(e => entryBasis(e) === 'hourly').reduce((s, e) => s + (e.quantity || 0), 0);
  const tmMobCount = tmLogOpen.filter(e => e.kind === 'mobilization').length;
  // (The pricing — each line × its quote item's rate, or the bid's day / hour /
  // mobilization rate — lives on the server now, in drillLogInvoiceLines, which is what
  // the invoice is built from. This card counts; it does not price.)
  // Per-line unit word: the quote item's, or day/hr for a legacy line.
  const tmUnitOfEntry = e => { const it = itemOf(e); if (it) return itemUnit(it); return e.kind === 'mobilization' ? 'trip' : (entryBasis(e) === 'hourly' ? 'hr' : 'day'); };
  // Unit-priced quote lines (loops, feet…) summed per item — they aren't days or hours.
  const tmUnitTotals = [];
  for (const e of tmWork.filter(e => entryBasis(e) === null)) {
    const it = itemOf(e); const key = it ? it.id : 'other';
    let row = tmUnitTotals.find(r => r.key === key);
    if (!row) { row = { key, name: it ? it.name : 'Other', unit: it ? itemUnit(it) : 'ea', qty: 0 }; tmUnitTotals.push(row); }
    row.qty += e.quantity || 0;
  }
  // "2 days · 6 hrs · 12 loops", dropping whichever is zero so a single-unit job reads
  // as it always did.
  const tmLoggedWords = [
    tmDaysTotal ? `${qtyNiceWf(tmDaysTotal)} day${tmDaysTotal === 1 ? '' : 's'}` : null,
    tmHoursTotal ? `${qtyNiceWf(tmHoursTotal)} hrs` : null,
    ...tmUnitTotals.map(r => `${qtyNiceWf(r.qty)} ${r.unit}`),
  ].filter(Boolean).join(' · ') || '0 ' + (tmBasis === 'hourly' ? 'hrs' : 'days');

  // The invoice. This used to open a print page in the browser — a good-looking sheet
  // that was saved nowhere, and printing it quietly "prepared the bill" on the billing
  // board. Now it opens the job's REAL invoice (the same record the Billing tile on the
  // project shows), drawn by the server in this card's layout: save, confirm, email,
  // checked & sent, paid — all tracked on the job. Opening it moves nothing; Confirm does.
  async function openTmInvoice() {
    if (tmInvBusy) return;
    setTmInvBusy(true);
    try { setTmInv(await apiFetch(`/projects/${projectId}/invoices/drill-log`, { method: 'POST' })); }
    catch (e) { alert(e?.error || 'Could not open the invoice.'); }
    setTmInvBusy(false);
  }
  return (
            <div style={{ border: '1.5px solid #ede9fe', background: '#faf5ff', borderRadius: 10, padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#7c3aed' }}>⏱️ Time &amp; Material Billing</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>
                  {bid.tm_drill ? `🛠️ ${bid.tm_drill} · ` : ''}
                  {tmBasis === 'hourly' ? 'Hourly' : 'Daily'}
                  {/* Rates and totals are office-only — the field logs days/mobs, no money. */}

                </div>
              </div>

              {/* One line per day worked, each one editable where it sits. A day that
                  turned out to be six hours gets corrected on its own row, so the
                  breakdown behind a total is always visible instead of being added up
                  into a number nobody can take apart later. */}
              {/* The quoted items come first — one button each, so a day of the quoted
                  crew rate or a trip of the quoted mobilization is one tap and bills at
                  the quoted price. The plain day/hour and mobilization buttons stay for
                  a job with no quote items (or something quoted nowhere). */}
              {tmItems.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 800, color: '#6d28d9', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>From the quote</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {tmItems.map(it => (
                      <button key={it.id} onClick={() => addTmEntry(it.type === 'mobilization' ? 'mobilization' : 'work', it)} disabled={tmSaving}
                        title={`Log ${it.type === 'mobilization' ? 'one trip' : it.type === 'hour' ? '8 hours' : 'one ' + itemUnit(it)} of ${it.name}${tmCanSeeMoney && it.rate != null ? ` at ${fmtMoneyWf(it.rate)}/${itemUnit(it)}` : ''}`}
                        style={{ padding: '10px 14px', borderRadius: 8, border: 'none', background: it.type === 'mobilization' ? '#b45309' : '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                        {it.type === 'mobilization' ? '🚚 ' : '+ '}{it.name}
                        <span style={{ fontWeight: 600, opacity: 0.8 }}> · {itemUnit(it)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                <button onClick={() => addTmEntry('work')} disabled={tmSaving}
                  style={tmItems.length
                    ? { padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd6fe', background: '#fff', color: '#6d28d9', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }
                    : { padding: '10px 16px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                  + Add Time{tmItems.length ? ' (not quoted)' : ''}
                </button>
                {!tmItems.some(i => i.type === 'mobilization') && (
                  <button onClick={() => addTmEntry('mobilization')} disabled={tmSaving} title="Log a drill mobilization (round trip)"
                    style={{ padding: tmItems.length ? '8px 12px' : '10px 16px', borderRadius: 8, border: '1.5px solid #b45309', background: '#fffbeb', color: '#b45309', fontSize: tmItems.length ? 12.5 : 13, fontWeight: 700, cursor: 'pointer' }}>🚚 + Mobilization</button>
                )}
              </div>

              {/* The drilling rates (and the any-job items) from the Price Book, one tap onto
                  this job — then they are buttons above like anything else that was quoted. */}
              {tmAddCommon
                ? <AddCommonItem projectId={projectId} workflow="drilling" have={tmItems.map(i => String(i.name || '').toLowerCase())} onClose={() => setTmAddCommon(false)} />
                : <button onClick={() => setTmAddCommon(true)}
                    style={{ marginBottom: 10, padding: '7px 12px', borderRadius: 8, border: '1px dashed #c4b5fd', background: '#fff', color: '#6d28d9', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>
                    ＋ Add a drill rate or common item to this job
                  </button>}

              {tmLogOpen.length === 0 ? <div style={{ fontSize: 12.5, color: '#9ca3af' }}>{tmClosedParts.length ? 'Nothing logged since the last part was billed — press ' : 'No time logged yet — press '}<strong>+ Add Time</strong> and edit the line.</div>
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {/* Column names, because a bare row of boxes doesn't say what it wants.
                        Dropped on a phone, where the row wraps onto two lines and headers
                        sitting above the wrong boxes would be worse than none — a date
                        picker, a number and a Days/Hours menu already read as themselves. */}
                    {!narrow && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '0 10px' }}>
                        <div style={{ ...dcLbl, flex: '1 1 130px', minWidth: 118 }}>Date</div>
                        <div style={{ ...dcLbl, width: 72 }}>Qty</div>
                        <div style={{ ...dcLbl, width: 92 }}>Unit</div>
                        <div style={{ width: 44 }} />
                      </div>
                    )}
                    {tmLogOpen.map(e => (
                      <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: '8px 10px' }}>
                        <input type="date" value={(e.work_date || '').slice(0, 10)}
                          onChange={ev => patchTmEntry(e.id, { work_date: ev.target.value })}
                          style={{ ...dcInp, flex: '1 1 130px', minWidth: 118, padding: '8px 8px', fontSize: 13 }} />
                        {e.kind === 'mobilization' ? (
                          // A mobilization is one round trip, not a quantity of anything —
                          // so it keeps its date and says what it is.
                          <div style={{ flex: '1 1 168px', fontSize: 13, fontWeight: 800, color: '#b45309' }}>🚚 {itemOf(e)?.name || 'Mobilization'}</div>
                        ) : (
                          <>
                            {/* A line off the quote says which item it is. */}
                            {itemOf(e) && <div style={{ flex: '1 1 140px', minWidth: 0, fontSize: 13, fontWeight: 800, color: '#6d28d9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={itemOf(e).name}>{itemOf(e).name}</div>}
                            {/* step="any" because half a day and 6.5 hours are both real. */}
                            <input type="number" step="any" inputMode="decimal" value={e.quantity ?? ''}
                              onChange={ev => patchTmEntry(e.id, { quantity: ev.target.value === '' ? 0 : Number(ev.target.value) })}
                              style={{ ...dcInp, width: 72, padding: '8px 8px', fontSize: 13 }} />
                            {/* Both units always offered on a day/hour line. The field records
                                what happened; the office prices it. A unit-priced quote line
                                (loops, feet) is counted in its own unit. */}
                            {entryBasis(e) === null
                              ? <div style={{ width: 92, fontSize: 13, color: '#6b7280', fontWeight: 700 }}>{tmUnitOfEntry(e)}</div>
                              : <select value={entryBasis(e)} onChange={ev => patchTmEntry(e.id, { basis: ev.target.value })}
                                  style={{ ...dcInp, width: 92, padding: '8px 6px', fontSize: 13 }}>
                                  <option value="daily">Days</option>
                                  <option value="hourly">Hours</option>
                                </select>}
                          </>
                        )}
                        <button onClick={() => deleteTmEntry(e.id)} title="Remove this line"
                          style={{ width: 44, background: 'none', border: 'none', color: '#b91c1c', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>Delete</button>
                      </div>
                    ))}
                  </div>
                )}

              {/* Parts already billed — folded, read only. */}
              {tmClosedParts.map(g => (
                <div key={g.id} style={{ marginTop: 10, border: '1px solid #d1d5db', borderRadius: 8, background: '#f3f4f6' }}>
                  <button onClick={() => setTmPartOpen(o => ({ ...o, [g.id]: !o[g.id] }))}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none', padding: '8px 10px', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                    <span>🔒</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: '#111' }}>{partLabel(g.name)} · billed</span>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>{g.rows.length} line{g.rows.length === 1 ? '' : 's'} · read only</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: '#6b7280' }}>{tmPartOpen[g.id] ? 'Hide ▴' : 'Show ▾'}</span>
                  </button>
                  {tmPartOpen[g.id] && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 10px 10px' }}>
                      {g.rows.map(e => (
                        <div key={e.id} style={{ display: 'flex', gap: 10, fontSize: 12.5, color: '#374151', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px' }}>
                          <span style={{ width: 92 }}>{e.work_date ? new Date(String(e.work_date).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</span>
                          <span style={{ fontWeight: 700 }}>{e.kind === 'mobilization' ? `🚚 ${itemOf(e)?.name || 'Mobilization'}` : `${qtyNiceWf(e.quantity || 0)} ${tmUnitOfEntry(e)}${itemOf(e) ? ' — ' + itemOf(e).name : ''}`}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Running totals */}
              <div style={{ display: 'flex', gap: 16, margin: '12px 0 0', fontSize: 13, fontWeight: 700 }}>
                <span style={{ color: '#7c3aed' }}>{tmLoggedWords} {tmClosedParts.length ? 'since the last part billed' : 'total'}</span>
                <span style={{ color: '#b45309' }}>{tmMobCount} mobilization{tmMobCount !== 1 ? 's' : ''}</span>
              </div>

              {/* This card collects what happened; it does not price it. Nobody sees
                  dollars here, whatever their role — the priced rollup lives on the
                  job's own T&M billing section, and the Billing Summary below still
                  prints it for the office. Two places showing the same money is how
                  two numbers end up disagreeing. */}
              <div style={{ marginTop: 12, borderTop: '1px dashed #ddd6fe', paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, color: '#4b5563' }}>
                <span>Logged so far</span>
                <span>{tmLoggedWords}{tmMobCount > 0 ? ` · ${tmMobCount} mob${tmMobCount > 1 ? 's' : ''}` : ''}</span>
              </div>
              {/* The priced summary still prints for the office — a document is not
                  the same as putting the money on the screen the field is filling in. */}
              {tmCanSeeMoney && (
                <button onClick={openTmInvoice} disabled={tmInvBusy}
                  style={{ marginTop: 10, width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #7c3aed', background: '#fff', color: '#7c3aed', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: tmInvBusy ? 0.6 : 1 }}>
                  {tmInvBusy ? 'Opening…' : `🧾 Invoice${tmInvoiceNo ? ' · INV-' + tmInvoiceNo : ''}`}
                </button>
              )}
              {tmInv && (
                <InvoiceModal key={tmInv.id} inv={tmInv} zIndex={7000}
                  onClose={() => setTmInv(null)}
                  onChanged={async upd => {
                    if (upd) { setTmInv(upd); if (upd.invoice_no) setTmInvoiceNo(upd.invoice_no); }
                    onChanged && onChanged();
                  }} />
              )}
            </div>
  );
}
// Build the card object a board card expects from a job's sub-job + project. It
// lives next to the cards so every page that opens one builds it the same way — the
// job page and the workflow board were assembling this shape separately.
//
// The sub-job is spread in whole rather than field-by-field: the drilling card wants
// drill_rig and drill_date, NVE wants meter_id, meter_address, nve_pid and nve_status,
// and hand-picking fields meant NVE would have opened with all of those blank.
export function workflowCardFromSubJob(sj, project, extra = {}) {
  if (!sj) return null;
  return {
    ...sj,
    id: sj.id,
    sub_job_id: sj.id,
    project_id: project?.id || sj.project_id,
    project_number: project?.project_number,
    project_name: project?.name,
    client_name: project?.client_name,
    pm_id: project?.pm_id,
    ...extra,
  };
}
// The old name, kept so nothing importing it breaks.
export const drillCardFromSubJob = workflowCardFromSubJob;

// One hole on the drilling card (Ryan, Oct 3 2026): a closed row — name, depth, Ø, its
// Fieldwire task, its status — that opens to the task's pictures (from Fieldwire) and the
// app's own, an Edit for the name and sizes, Add photo (which also goes on the task), Delete.
function DrillHoleRow({ h, projectId, fwLinked, busy, onPatch, onDelete, onUpload, onRemovePhoto, onCycleStatus, onOpenTask, onUnlink, onPick }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);     // { label, depth, diameter, note } while editing
  const [task, setTask] = useState(null);     // the Fieldwire task detail (photos) once opened
  const sm = HOLE_STATUS_META[h.status] || HOLE_STATUS_META.pending;
  useEffect(() => {
    if (!open || !h.fw_task_id || !projectId) { setTask(null); return; }
    apiFetch(`/projects/${projectId}/fieldwire/tasks/${h.fw_task_id}`).then(setTask).catch(() => setTask(null));
  }, [open, h.fw_task_id, projectId, (h.photos || []).length]); // eslint-disable-line react-hooks/exhaustive-deps
  const dims = [h.depth ? `Depth ${h.depth}` : null, h.diameter ? `Ø ${h.diameter}` : null].filter(Boolean).join(' · ');
  const fwPhotos = (task && task.photos) || [];
  async function save() {
    const body = {}; ['label', 'depth', 'diameter', 'note'].forEach(k => { if ((edit[k] || '') !== (h[k] || '')) body[k] = edit[k] || null; });
    if (Object.keys(body).length) await onPatch(h.id, body);
    setEdit(null);
  }
  return (
    <div style={{ background: '#fff', border: `1px solid ${open ? '#bfdbfe' : '#eef0f2'}`, borderRadius: 10 }}>
      {/* The closed row — tap anywhere on it to open. */}
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>{h.label || 'Hole'}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
            {h.fw_task && h.fw_task.sheet ? <span style={{ fontWeight: 800, color: '#374151' }}>📄 {h.fw_task.sheet} · </span> : null}
            {dims || 'No dimensions yet'}{(h.photos || []).length || fwPhotos.length ? ` · 📷 ${(h.photos || []).length + (open ? fwPhotos.length : 0)}` : ''}
            {' · '}<a href={`/admin/holes/${h.id}`} onClick={e => { e.preventDefault(); e.stopPropagation(); navigate(`/admin/holes/${h.id}`); }} style={{ color: '#1d4ed8', fontWeight: 700, textDecoration: 'none' }}>Detail ›</a>
          </div>
        </div>
        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {h.fw_task_id ? (<>
            <button onClick={() => onOpenTask(h.fw_task_id)} title="Open the Fieldwire task"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fff', border: `1px solid ${(h.fw_task && h.fw_task.color) || '#bfdbfe'}`, borderRadius: 999, padding: '3px 9px', fontSize: 11, fontWeight: 700, color: '#1e40af', cursor: 'pointer' }}>
              📍 {h.fw_task && h.fw_task.number ? '#' + h.fw_task.number : 'Fieldwire'}{h.fw_task && h.fw_task.status ? <span style={{ color: h.fw_task.color || '#6b7280' }}>· {h.fw_task.status}</span> : null}
            </button>
            <button onClick={() => onUnlink(h)} title="Take this hole off that task (the task stays in Fieldwire)" style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 14, lineHeight: 1, padding: 0, cursor: 'pointer' }}>⨯</button>
          </>) : (fwLinked && (
            <button onClick={() => onPick(h)} disabled={busy} title="Put this hole on a Fieldwire task — one the crew already made, or a new one"
              style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, color: '#1e40af', fontSize: 11, padding: '3px 9px', cursor: 'pointer', fontWeight: 700 }}>
              {busy ? 'Sending…' : '📍 Fieldwire task'}
            </button>
          ))}
          <button onClick={() => onCycleStatus(h)} title="Tap to change status"
            style={{ fontSize: 11, fontWeight: 800, color: sm.color, background: sm.bg, padding: '4px 10px', borderRadius: 12, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {sm.label}
          </button>
          <span onClick={() => setOpen(o => !o)} style={{ fontSize: 12, color: '#9ca3af', cursor: 'pointer', padding: '0 2px' }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {open && (
        <div style={{ borderTop: '1px solid #eef2ff', padding: '10px 12px 12px' }}>
          {edit ? (
            <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: '1.2 1 120px' }}><div style={dcLbl}>Hole #</div><input value={edit.label} onChange={e => setEdit(v => ({ ...v, label: e.target.value }))} placeholder="ts4-A" style={dcInp} /></div>
                <div style={{ flex: '1 1 90px' }}><div style={dcLbl}>Depth</div><input value={edit.depth} onChange={e => setEdit(v => ({ ...v, depth: e.target.value }))} placeholder="30 ft" style={dcInp} /></div>
                <div style={{ flex: '1 1 90px' }}><div style={dcLbl}>Diameter</div><input value={edit.diameter} onChange={e => setEdit(v => ({ ...v, diameter: e.target.value }))} placeholder='24"' style={dcInp} /></div>
              </div>
              <div style={{ marginTop: 8 }}><div style={dcLbl}>Note</div><input value={edit.note} onChange={e => setEdit(v => ({ ...v, note: e.target.value }))} placeholder="Anything about this hole" style={dcInp} /></div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                <button onClick={() => setEdit(null)} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                <button onClick={save} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>Save</button>
              </div>
            </div>
          ) : (h.note ? <div style={{ fontSize: 12.5, color: '#374151', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{h.note}</div> : null)}

          {/* Pictures: the task's (from Fieldwire) first, then the app's own. */}
          {h.fw_task_id && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ ...dcLbl, marginBottom: 4 }}>📍 On the Fieldwire task{task && task.sequence_number ? ` #${task.sequence_number}` : ''}{fwPhotos.length ? ` · ${fwPhotos.length}` : ''}</div>
              {!task ? <div style={{ fontSize: 12, color: '#9ca3af' }}>Reading Fieldwire…</div>
                : fwPhotos.length === 0 ? <div style={{ fontSize: 12, color: '#9ca3af' }}>No pictures on the task yet.</div>
                : <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{fwPhotos.map(p => <AttachThumb key={p.id} size={64} src={p.thumb_url || p.file_url} name={p.file_name || 'photo'} href={p.file_url} />)}</div>}
            </div>
          )}
          {(h.photos || []).length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ ...dcLbl, marginBottom: 4 }}>In the app · {h.photos.length}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {h.photos.map(p => <AttachThumb key={p.id} size={64} src={photoUrl(p.filename)} name={p.original_name || p.filename} href={photoUrl(p.filename)} onRemove={() => onRemovePhoto(h.id, p.id)} />)}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <AttachButton multiple variant="plain" icon="📷" label={h.fw_task_id ? 'Add photo — goes on the task too' : 'Add photo or file'} style={{ padding: '6px 12px', fontSize: 12 }}
              onFiles={fs => onUpload(h.id, fs)} />
            {!edit && <button onClick={() => setEdit({ label: h.label || '', depth: h.depth || '', diameter: h.diameter || '', note: h.note || '' })} style={{ background: 'none', border: 'none', color: '#1d4ed8', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>✎ Edit</button>}
            <span style={{ flex: 1 }} />
            <button onClick={() => onDelete(h.id)} style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>Delete</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function DrillCardModal({ card, pmName, onClose, onChanged, inline = false }) {
  const sjId = card.id;
  const [holes, setHoles] = useState([]);
  const [cardPhotos, setCardPhotos] = useState([]);
  const [bidHoles, setBidHoles] = useState([]);
  const [importingBid, setImportingBid] = useState(false);
  const [drillRig, setDrillRig] = useState(card.drill_rig || '');
  const [savingDrill, setSavingDrill] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newHole, setNewHole] = useState({ label: '', depth: '', diameter: '', fw: 'none' });   // fw: none | create | attach
  const [addOpen, setAddOpen] = useState(false);   // the ＋ Add Hole pillow (Ryan, Oct 3 2026)
  // Photos from the field (Ryan, Sep 29 2026): job-note photos + this card's own + hole photos.
  const [savingHole, setSavingHole] = useState(false);
  const [status, setStatus] = useState(card.status || 'pending');
  const [drillDate, setDrillDate] = useState(card.drill_date || '');
  const [comment, setComment] = useState(card.drill_comment || '');
  const [lightbox, setLightbox] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [jobLoc, setJobLoc] = useState(undefined); // undefined=not loaded, null=no location
  const [tmBid, setTmBid] = useState(null);   // the job's bid (for T&M rate reference), null if not T&M
  // Handed to the billing panel, which owns the number from there on.
  const [tmInvoiceNo] = useState(card.tm_invoice_no || null);
  // Opened from the tile's T&M pillow? Then that is what they came for — start it
  // unfolded rather than making them tap the same pillow twice.
  const [tmOpen, setTmOpen] = useState(!!card._openTm);
  const [holesOpen, setHolesOpen] = useState(!!card._openHoles);
  // Holes ↔ Fieldwire tasks (Ryan, Oct 3 2026): the job's Fieldwire picture, which hole's
  // chooser is open, which task is open in the drawer, the match-by-name proposals.
  const [fwOv, setFwOv] = useState(null);
  const [fwPick, setFwPick] = useState(null);
  const [fwOpen, setFwOpen] = useState(null);
  const [fwBusy, setFwBusy] = useState('');
  const [fwMatch, setFwMatch] = useState(null);   // { proposals, chosen: {hole_id: task_id} }
  useEffect(() => { if (card.project_id) apiFetch(`/projects/${card.project_id}/fieldwire`).then(setFwOv).catch(() => setFwOv(null)); }, [card.project_id]);
  const fwLinked = !!(fwOv && fwOv.configured && fwOv.linked);
  const reloadHoles = () => apiFetch(`/sub-jobs/${sjId}/holes`).then(h => setHoles(Array.isArray(h) ? h : [])).catch(() => {});
  async function holeFw(hole, how, taskId, pin = {}) {
    setFwBusy(hole.id);
    try {
      if (how === 'create') await apiFetch(`/sub-jobs/${sjId}/holes/${hole.id}/fieldwire`, { method: 'POST', body: JSON.stringify(pin || {}) });
      else if (how === 'attach') await apiFetch(`/sub-jobs/${sjId}/holes/${hole.id}/fieldwire/attach`, { method: 'POST', body: JSON.stringify({ task_id: taskId }) });
      else if (how === 'unlink') { if (!window.confirm('Take this hole off its Fieldwire task? The task stays in Fieldwire.')) { setFwBusy(''); return; } await apiFetch(`/sub-jobs/${sjId}/holes/${hole.id}/fieldwire`, { method: 'DELETE' }); }
      await reloadHoles(); setFwPick(null); loadActivity();
    } catch (e) { window.alert(e?.error || e?.message || 'Fieldwire did not take that.'); }
    setFwBusy('');
  }
  async function matchHoles(apply) {
    setFwBusy('match');
    try {
      if (!apply) { const r = await apiFetch(`/sub-jobs/${sjId}/holes/fieldwire/match`, { method: 'POST', body: '{}' }); setFwMatch({ proposals: r.proposals, chosen: Object.fromEntries(r.proposals.filter(p => p.task).map(p => [p.hole_id, p.task.id])) }); }
      else { const pairs = Object.entries(fwMatch.chosen).map(([hole_id, task_id]) => ({ hole_id, task_id })); const r = await apiFetch(`/sub-jobs/${sjId}/holes/fieldwire/match`, { method: 'POST', body: JSON.stringify({ pairs }) }); setHoles(r.holes); setFwMatch(null); loadActivity(); }
    } catch (e) { window.alert(e?.error || e?.message || 'Could not match the holes.'); }
    setFwBusy('');
  }
  const [updates, setUpdates] = useState([]);        // the job's daily updates, newest first
  const [updatesAll, setUpdatesAll] = useState(false);
  const [activity, setActivity] = useState([]);      // this job's who-did-what feed
  const [activityAll, setActivityAll] = useState(false);

  useEffect(() => {
    Promise.all([
      apiFetch(`/sub-jobs/${sjId}/holes`).catch(() => []),
      apiFetch(`/sub-jobs/${sjId}/photos`).catch(() => []),
      apiFetch(`/sub-jobs/${sjId}/bid-holes`).catch(() => []),
    ]).then(([h, ph, bh]) => {
      setHoles(Array.isArray(h) ? h : []);
      setCardPhotos(Array.isArray(ph) ? ph : []);
      setBidHoles(Array.isArray(bh) ? bh : []);
    }).finally(() => setLoading(false));
  }, [sjId]);

  // The job's activity feed. Its own function because a date change made from the
  // card has to show up on the card, not on the next page load.
  const loadActivity = React.useCallback(() => {
    if (!card.project_id) return;
    apiFetch(`/activity?scope=job&scopeId=${card.project_id}&subJobId=${encodeURIComponent(sjId)}&limit=60`)
      .then(a => setActivity(Array.isArray(a) ? a : [])).catch(() => {});
  }, [card.project_id]);

  // Is this drilling job a Time & Material bid? Load its rate + the field log.
  useEffect(() => {
    if (!card.project_id) return;
    loadActivity();
    apiFetch(`/projects/${card.project_id}/bid`).then(b => { if (b && b.pricing_type === 'tm') setTmBid(b); }).catch(() => {});
    // The server already hands these back newest-first.
    apiFetch(`/projects/${card.project_id}/daily-updates?sub_job_id=${encodeURIComponent(sjId)}`).then(u => setUpdates(Array.isArray(u) ? u : [])).catch(() => {});
  }, [sjId, card.project_id]);
  const plannedCount = bidHoles.reduce((n, r) => n + (Math.max(1, parseInt(r.qty, 10) || 1)), 0);
  async function importFromBid() {
    if (importingBid) return;
    setImportingBid(true);
    try {
      const updated = await apiFetch(`/sub-jobs/${sjId}/holes/import-from-bid`, { method: 'POST' });
      setHoles(Array.isArray(updated) ? updated : []);
      onChanged && onChanged();
    } catch { /* ignore */ }
    setImportingBid(false);
  }

  async function openMap() {
    setShowMap(true);
    if (jobLoc !== undefined) return;
    try {
      const projects = await apiFetch('/projects-map');
      const p = (Array.isArray(projects) ? projects : []).find(x => String(x.id) === String(card.project_id));
      setJobLoc(p && p.lat != null && p.lng != null ? { lat: p.lat, lng: p.lng, label: p.name || jobName(wfJob(card)) } : null);
    } catch { setJobLoc(null); }
  }

  function patchHole(id, body) {
    apiFetch(`/sub-jobs/${sjId}/holes/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
      .then(upd => { setHoles(hs => hs.map(h => h.id === id ? upd : h)); onChanged && onChanged(); })
      .catch(() => {});
  }
  function cycleHoleStatus(h) {
    patchHole(h.id, { status: (HOLE_STATUS_META[h.status] || HOLE_STATUS_META.pending).next });
  }
  async function addHole() {
    if (savingHole) return;
    setSavingHole(true);
    try {
      const { fw, ...body } = newHole;
      const created = await apiFetch(`/sub-jobs/${sjId}/holes`, { method: 'POST', body: JSON.stringify(body) });
      setHoles(hs => [...hs, created]);
      setNewHole({ label: '', depth: '', diameter: '', fw: 'none' });
      onChanged && onChanged();
      // The Fieldwire choice made in the form: a new task now, or the pick list for an existing one.
      if (fw === 'create' && fwLinked) await holeFw(created, 'create');
      else if (fw === 'attach' && fwLinked) setFwPick(created);
    } catch { /* ignore */ }
    setSavingHole(false);
  }
  function deleteHole(id) {
    if (!window.confirm('Delete this hole?')) return;
    apiFetch(`/sub-jobs/${sjId}/holes/${id}`, { method: 'DELETE' })
      .then(() => { setHoles(hs => hs.filter(h => h.id !== id)); onChanged && onChanged(); }).catch(() => {});
  }
  async function uploadHolePhotos(holeId, files) {
    if (!files || !files.length) return;
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('photos', f));
    try {
      const res = await fetch(`${API_BASE}/sub-jobs/${sjId}/holes/${holeId}/photos`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      if (res.ok) { const upd = await res.json(); setHoles(hs => hs.map(h => h.id === holeId ? upd : h)); }
    } catch { /* ignore */ }
  }
  function removePhoto(holeId, photoId) {
    apiFetch(`/sub-jobs/${sjId}/holes/${holeId}/photos/${photoId}`, { method: 'DELETE' })
      .then(() => setHoles(hs => hs.map(h => h.id === holeId ? { ...h, photos: (h.photos || []).filter(p => p.id !== photoId) } : h))).catch(() => {});
  }
  async function chooseDrill(rig) {
    if (savingDrill) return;
    const next = drillRig === rig ? '' : rig; // tap the active one to clear
    setSavingDrill(true);
    setDrillRig(next);
    try {
      await apiFetch(`/sub-jobs/${sjId}/drill`, { method: 'POST', body: JSON.stringify({ drill_rig: next || null }) });
      onChanged && onChanged();
    } catch { setDrillRig(drillRig); }
    setSavingDrill(false);
  }
  async function uploadCardPhotos(files) {
    if (!files || !files.length) return;
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('photos', f));
    try {
      const res = await fetch(`${API_BASE}/sub-jobs/${sjId}/photos`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      if (res.ok) { setCardPhotos(await res.json()); onChanged && onChanged(); }
    } catch { /* ignore */ }
  }
  function removeCardPhoto(photoId) {
    apiFetch(`/sub-jobs/${sjId}/photos/${photoId}`, { method: 'DELETE' })
      .then(() => { setCardPhotos(ps => ps.filter(p => p.id !== photoId)); onChanged && onChanged(); }).catch(() => {});
  }
  function setCardStatus(next) {
    const prev = status;
    setStatus(next);
    // Send status only — the sub-job's notes field carries other data we must not clobber.
    apiFetch(`/projects/${card.project_id}/sub-jobs/${sjId}`, { method: 'PATCH', body: JSON.stringify({ status: next }) })
      .then(() => onChanged && onChanged())
      .catch(() => { setStatus(prev); window.alert('Could not update the status — check your connection and try again.'); });
  }
  // Save the card's own date / comment (dedicated columns).
  function saveCard(body) {
    apiFetch(`/sub-jobs/${sjId}/card`, { method: 'PATCH', body: JSON.stringify(body) })
      .then(() => onChanged && onChanged())
      .catch(() => window.alert('Could not save that — check your connection and try again.'));
  }

  const done = holes.filter(h => h.status === 'done').length;

  return (
    <div style={inline ? { display: 'block' } : { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      // A phone's clock and notch sit over a full-height overlay, and these cards are
      // tall enough to reach them — which put the job number and the × behind the
      // status bar where neither could be read or tapped.
      paddingTop: 'max(env(safe-area-inset-top), 12px)', paddingBottom: 'max(env(safe-area-inset-bottom), 12px)',
      paddingLeft: 12, paddingRight: 12 }} onClick={inline ? undefined : onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#f9fafb', borderRadius: 14, width: inline ? '100%' : 560, maxWidth: '100%', maxHeight: inline ? 'none' : 'min(92vh, calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 24px))', display: 'flex', flexDirection: 'column', boxShadow: inline ? 'none' : '0 16px 60px rgba(0,0,0,0.28)', border: inline ? '1px solid #e5e7eb' : 'none' }}>
        {/* Header */}
        {/* Header — shown inline too. A single card on a job's Work Flows board is
            meant to BE the workflow board's card, header and all; hiding it here made
            the two look like different things. */}
        <div style={{ background: '#1e3a5f', borderRadius: '14px 14px 0 0', padding: '14px 18px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800 }}>🕳️ <JobNumberLink projectId={card.project_id}>{jobNumber(wfJob(card)) || '—'}</JobNumberLink></div>
            <div style={{ fontSize: 13, opacity: 0.9, marginTop: 2 }}>{jobName(wfJob(card))}</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 3 }}>{card.client_name ? `🏢 ${card.client_name} · ` : ''}👤 {pmName(card.pm_id)}</div>
          </div>
          {/* Forward this card to someone in the app. It has its own address, so they
              land on the card rather than the board it sits on. */}
          <SendPointer tone="dark" push
            path={`/admin/workflows/${card.type}?card=${sjId}`}
            label={`${jobNumber(wfJob(card)) || card.project_number || ''} ${typeMeta(card.type).label}`.trim()} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            {/* Inline there is nothing to close, and a dead × is worse than none. */}
            {!inline && (
            <button onClick={onClose} aria-label="Close"
              style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', fontSize: 20, cursor: 'pointer',
                lineHeight: 1, width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
            )}
            <button onClick={openMap} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '6px 12px', borderRadius: 8, whiteSpace: 'nowrap' }}>📍 Location</button>
          </div>
        </div>

        <div style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Status — at the very top */}
          <div>
            <div style={{ ...dcLbl, marginBottom: 6 }}>Status</div>
            {/* One row, never two: the four share the width rather than wrapping, so
                the card does not grow a second line of status on a phone. */}
            <div style={{ display: 'flex', gap: 5, flexWrap: 'nowrap' }}>
              {/* Scheduled, not In Progress — this is a drilling card, and the board
                  behind it says Scheduled. The two must not disagree. */}
              {SCHEDULED_STATUS_COLUMNS.map(s => {
                const on = status === s.key;
                return (
                  <button key={s.key} onClick={() => setCardStatus(s.key)}
                    style={{ flex: 1, minWidth: 0, padding: '8px 2px', borderRadius: 20, border: on ? `2px solid ${s.color}` : '1.5px solid #e5e7eb', background: on ? s.bg : '#fff', color: on ? s.color : '#374151',
                      // Shrinks a shade on a 320px phone so "Not Started" is not cut in
                      // half, and sits back at a comfortable size on anything larger.
                      fontSize: 'clamp(10px, 3vw, 12px)', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <CloseCardStrip card={card} sjId={sjId} status={status} setStatus={setStatus} board="drilling" onChanged={onChanged} onClose={onClose} />

          {/* The bid's Job Description, same as the loops card (Ryan, Sep 30 2026). */}
          {String(card.bid_scope || '').trim() && (
            <div>
              <div style={{ ...dcLbl, marginBottom: 6 }}>Scope of work <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>· from the bid</span></div>
              <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.45, whiteSpace: 'pre-wrap', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px' }}>
                {card.bid_scope}
              </div>
            </div>
          )}

          {/* Comment */}
          <div>
            <div style={{ ...dcLbl, marginBottom: 6 }}>Comment</div>
            <textarea value={comment} placeholder="Add a note about this drilling card…"
              onChange={e => setComment(e.target.value)}
              onBlur={e => saveCard({ drill_comment: e.target.value })}
              rows={3} style={{ ...dcInp, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
          {/* Executives: put people on this job in QuickBooks Time (Ryan, Oct 19 2026). */}
          <AddToSchedule projectId={card.project_id} workflow={card.type === 'horizontal_drilling' ? 'horizontal_drilling' : 'vertical_drilling'} startDate={drillDate} days={card.sched_days} />

          {/* The same four pillows as the board tile, so the card reads the same
              wherever you meet it. The detail for each still lives further down. */}
          <DrillPillows w={{ ...card, drill_rig: drillRig, drill_date: drillDate }} onChanged={() => { loadActivity(); onChanged && onChanged(); }}
            extras={[
              { key: 'holes', label: holesPillowLabel(holes.length, done, holes.reduce((t, h) => t + (parseFloat(h.depth) || 0), 0)),
                tone: '#1e3a5f', toneSoft: '#eff6ff', toneEdge: '#bfdbfe', panel: (<>
          {holesOpen && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <div style={dcLbl}>Holes {holes.length > 0 && <span style={{ color: '#15803d' }}>· {done}/{holes.length} done</span>}</div>
            </div>
            {!loading && holes.length === 0 && plannedCount > 0 && (
              <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 10, padding: 12, marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#6d28d9' }}>📋 {plannedCount} planned hole{plannedCount !== 1 ? 's' : ''} on the bid</div>
                <div style={{ fontSize: 12, color: '#7c3aed', margin: '3px 0 10px' }}>Pull them onto the card with their size &amp; depth, then track each in the field.</div>
                <button onClick={importFromBid} disabled={importingBid}
                  style={{ padding: '9px 14px', borderRadius: 8, border: 'none', background: '#6d28d9', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  {importingBid ? 'Adding…' : `+ Add ${plannedCount} hole${plannedCount !== 1 ? 's' : ''} from bid`}
                </button>
              </div>
            )}
            {/* Holes ↔ Fieldwire (Ryan, Oct 3 2026): match the unlinked holes to the crew's pole
                tasks by name — sheet + letter — and show what will be linked before it is. */}
            {fwLinked && !loading && holes.some(h => !h.fw_task_id) && !fwMatch && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: '9px 12px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff' }}>
                <span style={{ fontSize: 12.5, color: '#1e40af', fontWeight: 700, flex: 1 }}>📍 {holes.filter(h => !h.fw_task_id).length} of {holes.length} holes are not on a Fieldwire task yet.</span>
                <button onClick={() => matchHoles(false)} disabled={!!fwBusy} style={{ padding: '6px 12px', borderRadius: 7, border: 'none', background: '#1e40af', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{fwBusy === 'match' ? 'Looking…' : 'Match by name'}</button>
              </div>
            )}
            {fwMatch && (
              <div style={{ marginBottom: 8, padding: 12, borderRadius: 10, border: '1.5px solid #bfdbfe', background: '#f8fbff' }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1e3a8a', marginBottom: 8 }}>Holes → Fieldwire tasks, by sheet and pole letter</div>
                {fwMatch.proposals.map(p => (
                  <label key={p.hole_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid #eef2ff', fontSize: 13 }}>
                    <input type="checkbox" disabled={!p.task} checked={!!fwMatch.chosen[p.hole_id]} onChange={e => setFwMatch(m => { const c = { ...m.chosen }; if (e.target.checked && p.task) c[p.hole_id] = p.task.id; else delete c[p.hole_id]; return { ...m, chosen: c }; })} />
                    <span style={{ fontWeight: 800, minWidth: 64 }}>{p.label || 'Hole'}</span>
                    <span style={{ color: '#9ca3af' }}>→</span>
                    {p.task ? <span style={{ flex: 1 }}>#{p.task.number} {p.task.name}<span style={{ color: '#6b7280' }}> · {[p.task.sheet, p.task.team].filter(Boolean).join(' · ')} · {p.why}</span></span>
                      : <span style={{ flex: 1, color: '#b45309' }}>no match — {p.why}; use the 📍 button on the hole to pick one</span>}
                  </label>
                ))}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                  <button onClick={() => setFwMatch(null)} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={() => matchHoles(true)} disabled={!!fwBusy || !Object.keys(fwMatch.chosen).length} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#1e40af', color: '#fff', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>{fwBusy === 'match' ? 'Linking…' : `Link ${Object.keys(fwMatch.chosen).length}`}</button>
                </div>
              </div>
            )}
            {loading ? (
              <div style={{ fontSize: 13, color: '#9ca3af', padding: '10px 0' }}>Loading…</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {holes.map(h => (
                  <DrillHoleRow key={h.id} h={h} projectId={card.project_id} fwLinked={fwLinked} busy={fwBusy === h.id}
                    onPatch={patchHole} onDelete={deleteHole} onUpload={uploadHolePhotos} onRemovePhoto={removePhoto} onCycleStatus={cycleHoleStatus}
                    onOpenTask={setFwOpen} onUnlink={x => holeFw(x, 'unlink')} onPick={setFwPick} />
                ))}
                {holes.length === 0 && <div style={{ fontSize: 13, color: '#9ca3af', padding: '4px 0' }}>No holes yet — use ＋ Add Hole below.</div>}
              </div>
            )}

          </div>
          )}
          {fwPick && card.project_id && (
            <FwTaskChooser projectId={card.project_id} heading={`Fieldwire task for hole ${fwPick.label || ''}`}
              createHint="Named with the hole, its depth and diameter, under the Poles / Drilling category; details in the first comment."
              taken={Object.fromEntries(holes.filter(x => x.fw_task_id && x.id !== fwPick.id).map(x => [x.fw_task_id, 'hole ' + (x.label || '')]))}
              busy={fwBusy === fwPick.id} onCreate={pin => holeFw(fwPick, 'create', undefined, pin)} onAttach={tid => holeFw(fwPick, 'attach', tid)} onClose={() => setFwPick(null)} />
          )}
          {fwOpen && card.project_id && (
            <FieldwireTaskDrawer projectId={card.project_id} taskId={fwOpen} statuses={(fwOv && fwOv.statuses) || []} canEdit onClose={() => setFwOpen(null)} onChanged={reloadHoles} />
          )}
              </>), open: holesOpen, onClick: () => setHolesOpen(v => !v) },
              tmBid ? { key: 'tm', label: '⏱️ Time & Material Billing', panel: (<>
          {tmBid && tmOpen && (
            <TmBillingPanel job={card} bid={tmBid} projectId={card.project_id} invoiceSjId={sjId}
              invoiceNo={tmInvoiceNo} pmName={pmName} onChanged={onChanged} />
          )}
              </>), open: tmOpen, onClick: () => setTmOpen(v => !v) } : null,
              // ＋ Add Hole as its own pillow at the bottom (Ryan, Oct 3 2026): the form opens under
              // it — name, depth, diameter, and whether it gets a Fieldwire task now.
              { key: 'addhole', label: '＋ Add Hole', tone: '#1e3a5f', toneSoft: '#eff6ff', toneEdge: '#bfdbfe', open: addOpen, onClick: () => setAddOpen(v => !v), panel: (<>
          {addOpen && (
            <div style={{ background: '#fff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 12 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: '1.2 1 120px' }}><div style={dcLbl}>Hole #</div><input value={newHole.label} onChange={e => setNewHole(n => ({ ...n, label: e.target.value }))} placeholder="ts4-A (sheet + pole)" style={dcInp} /></div>
                <div style={{ flex: '1 1 90px' }}><div style={dcLbl}>Depth</div><input value={newHole.depth} onChange={e => setNewHole(n => ({ ...n, depth: e.target.value }))} placeholder="30 ft" style={dcInp} /></div>
                <div style={{ flex: '1 1 90px' }}><div style={dcLbl}>Diameter</div><input value={newHole.diameter} onChange={e => setNewHole(n => ({ ...n, diameter: e.target.value }))} placeholder='24"' style={dcInp} /></div>
              </div>
              {fwLinked && (
                <div style={{ marginTop: 10 }}>
                  <div style={dcLbl}>Fieldwire task</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {[['none', 'Not now'], ['create', '＋ Create a new task'], ['attach', '🔗 Pick an existing task']].map(([k, l]) => (
                      <button key={k} type="button" onClick={() => setNewHole(n => ({ ...n, fw: k }))}
                        style={{ padding: '6px 11px', borderRadius: 999, border: `1.5px solid ${newHole.fw === k ? '#1e40af' : '#e5e7eb'}`, background: newHole.fw === k ? '#1e40af' : '#fff', color: newHole.fw === k ? '#fff' : '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{l}</button>
                    ))}
                  </div>
                  {newHole.fw === 'attach' && <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 4 }}>The job's Fieldwire tasks come up to pick from as soon as the hole is added.</div>}
                </div>
              )}
              <button onClick={async () => { await addHole(); if (newHole.fw !== 'attach') setAddOpen(false); }} disabled={savingHole} style={{ marginTop: 10, width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {savingHole ? 'Adding…' : '+ Add Hole'}
              </button>
            </div>
          )}
              </>) },
            ]} />


          {/* T&M Billing — one ledger for the whole job, and only once its pillow is
              tapped. It is the longest thing on the card by some way, and most of the
              time you open this card for something else. */}

          {/* Job photos (also shown on the project page) */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={dcLbl}>Job Photos &amp; files <span style={{ color: '#9ca3af', fontWeight: 600 }}>· show on project page</span></div>
              <AttachButton multiple variant="plain" icon="📷" label="Add photos or files" style={{ padding: '6px 12px', fontSize: 12 }}
                onFiles={fs => uploadCardPhotos(fs)} />
            </div>
            {cardPhotos.length === 0 ? (
              <div style={{ fontSize: 13, color: '#9ca3af' }}>No job photos yet.</div>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {cardPhotos.map(p => (
                  <AttachThumb key={p.id} size={80} src={photoUrl(p.filename)} name={p.original_name || p.filename}
                    href={photoUrl(p.filename)} onRemove={() => removeCardPhoto(p.id)} />
                ))}
              </div>
            )}
          </div>

          {/* The Field Tasks block came off this card (Ryan, Oct 3 2026): each hole carries its own
              Fieldwire task now. The job's pins are still on the Fieldwire pillow of the job page. */}

          {/* Holes — behind its own pillow, like the billing above it. */}

          {/* Daily updates — newest first, straight after the holes. What happened on
              this job, in the words of whoever was standing on it. Outside the holes
              conditional, so it shows whether or not the holes are unfolded. */}
          <div>
            <div style={{ ...dcLbl, marginBottom: 6 }}>Daily Updates{updates.length ? ` (${updates.length})` : ''}</div>
            {updates.length === 0 ? (
              <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Nothing posted on this job yet — use the Daily Update pillow above.</div>
            ) : (<>
              {(updatesAll ? updates : updates.slice(0, inline ? 0 : 5)).map(u => (
                <div key={u.id} style={{ borderLeft: '3px solid #e5e7eb', paddingLeft: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 11.5, color: '#6b7280', fontWeight: 700 }}>
                    {u.author_name || 'Someone'}
                    <span style={{ fontWeight: 500 }}> · {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>{u.from_job && <span title="Posted on the job — it names this card's work" style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 800, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, padding: '0 6px' }}>from the job</span>}
                  </div>
                  <div style={{ fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap', lineHeight: 1.45, marginTop: 2 }}>{u.notes}</div>
                  <Reactions type="update" id={u.id} reactions={u.reactions} />
                </div>
              ))}
              {/* A long-running job would otherwise turn the card into a scroll. */}
              {updates.length > 5 && (
                <button onClick={() => setUpdatesAll(v => !v)}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                  {updatesAll ? 'Show fewer' : `Show all ${updates.length}`}
                </button>
              )}
            </>)}
          </div>

          {/* Activity — what changed on this job and who changed it. A moved drill
              date lands here ("moved the drill date from 8/25 to 9/9"), which is the
              one people come back looking for. */}
          <div>
            <div style={{ ...dcLbl, marginBottom: 6 }}>Activity{activity.length ? ` (${activity.length})` : ''}</div>
            {activity.length === 0 ? (
              <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Nothing logged on this job yet.</div>
            ) : (<>
              {(activityAll ? activity : activity.slice(0, inline ? 0 : 6)).map(a => (
                <div key={a.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 7 }}>
                  <div style={{ flex: 1, fontSize: 12.5, color: '#374151', lineHeight: 1.4 }}>
                    <span style={{ fontWeight: 800, color: '#111827' }}>{a.actor_name || 'Someone'}</span> {a.action}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              ))}
              {activity.length > 6 && (
                <button onClick={() => setActivityAll(v => !v)}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                  {activityAll ? 'Show fewer' : `Show all ${activity.length}`}
                </button>
              )}
            </>)}
          </div>
        </div>
        {/* Close, where a thumb actually is — and hidden inline, where the pillow is the
            container and closing this would close nothing. The × up in the corner is the furthest
            point on the screen from where you hold a phone, and this card is mostly
            read on a phone. Outside the scrolling body, so it never scrolls away. */}
        {!inline && (
        <div style={{ borderTop: '1px solid #e5e7eb', background: '#fff', padding: '10px 14px', paddingBottom: 'max(env(safe-area-inset-bottom), 10px)', borderRadius: '0 0 14px 14px' }}>
          <button onClick={onClose}
            style={{ width: '100%', minHeight: 48, borderRadius: 10, border: '1.5px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
            Close
          </button>
        </div>
        )}
      </div>

      {lightbox && (
        <div onClick={e => { e.stopPropagation(); setLightbox(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <img src={lightbox} alt="hole" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
        </div>
      )}

      {showMap && (
        <div onClick={e => { e.stopPropagation(); setShowMap(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 620, maxWidth: '100%', overflow: 'hidden', boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ background: '#1e3a5f', padding: '12px 16px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 700 }}>📍 {jobNumber(wfJob(card))} — {jobName(wfJob(card))}</span>
              <button onClick={() => setShowMap(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            {jobLoc === undefined ? (
              <div style={{ padding: 50, textAlign: 'center', color: '#9ca3af' }}>Loading map…</div>
            ) : jobLoc === null ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>No saved location for this job yet.<div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>Set the job's address on the project to place it on the map.</div></div>
            ) : (
              <MapContainer center={[jobLoc.lat, jobLoc.lng]} zoom={14} style={{ height: 380, width: '100%' }}>
                <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[jobLoc.lat, jobLoc.lng]}><Popup>{jobLoc.label}</Popup></Marker>
              </MapContainer>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
